import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import ReactPlayer from 'react-player';
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ImportDataTab from "../components/ImportDataTab";
import AdminLegalCompliance from "../components/AdminLegalCompliance";
import PaymentGatewaySettings from "../components/PaymentGatewaySettings";
import SubscriptionPlansManagement from "../components/SubscriptionPlansManagement";
import PaymentTransactionsViewer from "../components/PaymentTransactionsViewer";
import UserSubscriptionsViewer from "../components/UserSubscriptionsViewer";
import UserActivityMonitor from "../components/UserActivityMonitor";
import ContentModerationDashboard from "../components/ContentModerationDashboard";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Users, BookOpen, Calendar, GraduationCap, Trash2, Edit, Plus, Lock, Unlock,
  Database, TrendingUp, FileText, Video, Award, Building2, UserCheck, BarChart3, Target,
  DollarSign, CreditCard, Eye, EyeOff, Trophy, Gift, Star, Coins, MessageCircle, Send, Upload,
  Bell, Mail, Filter, CheckCircle, XCircle, AlertCircle, Loader2, Sparkles, Save, ArrowRight
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const AdminPanel = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState("all");

  // Helper function to convert 24hr to 12hr format
  const convertTo12Hour = (time) => {
    if (!time) return 'N/A';
    
    // If already in 12hr format (contains AM/PM), return as is
    if (time.includes('AM') || time.includes('PM')) return time;
    
    // Convert 24hr to 12hr
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Data states for each entity
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [userPage, setUserPage] = useState(0);
  const [userLimit] = useState(50);
  const [userSearch, setUserSearch] = useState("");
  const [userFilters, setUserFilters] = useState({
    role: "all",
    is_verified: null,
    locked: null,
    subscription_plan: "all"
  });
  const [scholarships, setScholarships] = useState([]);
  const [exams, setExams] = useState([]);
  const [resources, setResources] = useState([]);
  const [events, setEvents] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [careers, setCareers] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [courses, setCourses] = useState([]);
  const [courseFormData, setCourseFormData] = useState({
    title: "",
    category: "",
    description: "",
    duration: "",
    level: "",
    price: 0,
    image: "",
    prerequisites: [],
    syllabus_overview: [],
    career_paths: [],
    average_salary_india: "",
    average_salary_global: "",
    top_recruiters: [],
    skills_gained: [],
    is_popular: false,
    is_active: true
  });
  const [editingCourse, setEditingCourse] = useState(null);
  const [isCourseDialogOpen, setIsCourseDialogOpen] = useState(false);
  const [courseImageFile, setCourseImageFile] = useState(null);
  const [courseImagePreview, setCourseImagePreview] = useState(null);
  const [courseFormErrors, setCourseFormErrors] = useState({});
  const [showCoursePreview, setShowCoursePreview] = useState(false);
  
  // Lesson Management States
  const [selectedCourseForLessons, setSelectedCourseForLessons] = useState(null);
  const [isLessonDialogOpen, setIsLessonDialogOpen] = useState(false);
  const [lessonFormData, setLessonFormData] = useState({
    title: "",
    video_url: "",
    duration_minutes: 0,
    description: "",
    order: 1,
    materials: [],
    is_preview: false
  });
  const [editingLesson, setEditingLesson] = useState(null);
  const [viewingLesson, setViewingLesson] = useState(null);
  const [isViewLessonDialogOpen, setIsViewLessonDialogOpen] = useState(false);
  
  const [supportTickets, setSupportTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [adminMessage, setAdminMessage] = useState("");
  
  // FAQ Management States
  const [faqs, setFaqs] = useState([]);
  const [faqFormData, setFaqFormData] = useState({
    question: "",
    answer: "",
    category: "Account",
    status: true
  });
  const [editingFaq, setEditingFaq] = useState(null);
  const [isFaqDialogOpen, setIsFaqDialogOpen] = useState(false);
  const [faqSearch, setFaqSearch] = useState("");
  const [faqCategoryFilter, setFaqCategoryFilter] = useState("all");
  const [faqStatusFilter, setFaqStatusFilter] = useState("all");
  
  // Website Settings States
  const [websiteSettings, setWebsiteSettings] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [isWebsiteSettingsLoading, setIsWebsiteSettingsLoading] = useState(false);
  
  // SEO Settings States
  const [seoSettings, setSeoSettings] = useState([]);
  const [editingSeoPage, setEditingSeoPage] = useState(null);
  const [seoFormData, setSeoFormData] = useState({
    page_name: '',
    page_title: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    robots: 'index, follow'
  });
  const [isSeoDialogOpen, setIsSeoDialogOpen] = useState(false);
  
  // Landing Page CMS States
  const [landingPageContent, setLandingPageContent] = useState(null);
  const [isLandingPageLoading, setIsLandingPageLoading] = useState(false);
  const [editingLandingSection, setEditingLandingSection] = useState(null);
  
  const [bookings, setBookings] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [countries, setCountries] = useState([]);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [currencySettings, setCurrencySettings] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [badges, setBadges] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [emailSettings, setEmailSettings] = useState({
    enabled: false,
    host: '',
    port: 587,
    username: '',
    password: '',
    from_email: ''
  });
  const [userProgress, setUserProgress] = useState([]);
  const [skillTestResults, setSkillTestResults] = useState([]);
  const [readinessSettings, setReadinessSettings] = useState({
    profile_completion_weight: 20,
    assessment_weight: 30,
    skills_weight: 20,
    consultation_weight: 15,
    active_engagement_weight: 15
  });
  const [selectedUserResults, setSelectedUserResults] = useState(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  
  // Mock Interview states
  const [mockInterviewSessions, setMockInterviewSessions] = useState([]);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [showInterviewModal, setShowInterviewModal] = useState(false);

  // Broadcast Notification states
  const [broadcastForm, setBroadcastForm] = useState({
    title: '',
    message: '',
    type: 'system_update',
    priority: 'normal',
    link: '',
    send_email: false,
    target_roles: null,
    target_subscription_tiers: null
  });
  const [broadcastLoading, setBroadcastLoading] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState(null);
  const [broadcastHistory, setBroadcastHistory] = useState([]);

  // Modal states
  const [modals, setModals] = useState({
    scholarship: false,
    exam: false,
    resource: false,
    event: false,
    mentor: false,
    quiz: false,
    college: false,
    career: false,
    opportunity: false,
    currency: false,
    country: false,
    exchangeRate: false,
    language: false
  });
  
  // User management modals
  const [selectedUser, setSelectedUser] = useState(null);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [walletAmount, setWalletAmount] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('basic');
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [paymentDetails, setPaymentDetails] = useState(null);

  // Editing states
  const [editing, setEditing] = useState({
    scholarship: null,
    exam: null,
    resource: null,
    event: null,
    mentor: null,
    quiz: null,
    college: null,
    career: null,
    currency: null,
    country: null,
    exchangeRate: null,
    language: null
  });

  // Form states
  const [forms, setForms] = useState({
    scholarship: {
      name: "", organization: "", amount: "", eligibility: "", deadline: "",
      min_grade: 1, max_grade: 12, category: "", application_link: "", description: ""
    },
    exam: {
      name: "", full_name: "", exam_date: "", registration_deadline: "",
      eligibility: "", subjects: "", career_paths: "", description: ""
    },
    resource: {
      title: "", type: "article", category: "", url: "", description: "", thumbnail: "", duration: ""
    },
    event: {
      title: "", type: "webinar", date: "", time: "", speaker: "", description: "", registration_link: "", max_attendees: ""
    },
    mentor: {
      name: "", profession: "", company: "", designation: "", expertise: "", bio: "", 
      image: "", education: "", qualifications: "", skills: "", experience_years: 0, 
      work_experience: "", email: "", contact_number: "", rate_per_minute: 2.0, 
      availability: true
    },
    quiz: {
      title: "", category: "", difficulty: "medium", points: 10, time_limit: 30, questions: []
    },
    college: {
      name: "", description: "", location: "", state: "", type: "", established: "", 
      image: "", website: "", ranking_nirf: "", ranking_india_today: "", ranking_qs_world: "",
      courses_offered: "", admission_ug: "", admission_pg: "", cutoff_marks: "",
      fees_ug: "", fees_pg: "", placement_avg: "", placement_highest: "", placement_rate: "",
      top_recruiters: "", scholarships: "", infra_library: "", infra_hostels: "", infra_labs: "",
      infra_sports: "", infra_other: "", contact_phone: "", contact_email: "", accreditation: "",
      is_featured: false, is_active: true
    },
    career: {
      title: "", description: "", category: "", required_education: "", skills: "", 
      salary_range_india: "", salary_range_global: "", job_outlook: "", timeline_to_enter: "", 
      work_life_balance: "", growth_potential: "", work_environment: "",
      certifications: "", top_companies: "", day_in_life: "", roadmap: ""
    },
    opportunity: {
      title: "", company_name: "", type: "internship", location: "", work_mode: "remote",
      field: "", experience_level: "entry", description: "", requirements: "",
      skills_required: "", salary: "", stipend: "", duration: "", start_date: "",
      application_deadline: "", application_link: "", status: "active", spots_available: null
    },
    currency: {
      code: "", symbol: "", name: "", decimal_places: 2, is_active: true
    },
    country: {
      name: "", code: "", currency_code: "", is_active: true
    },
    exchangeRate: {
      from_currency: "USD", to_currency: "", rate: "", is_manual: true
    },
    language: {
      code: "", name: "", native_name: "", is_active: true, is_default: false
    }
  });

  useEffect(() => {
    if (user?.role !== "admin" && user?.role !== "school_admin") {
      toast.error("Access denied: Admin only");
      navigate("/");
      return;
    }
    if (token) {
      fetchAnalytics();
      fetchAllData();
      fetchPlansAndPaymentDetails();
      fetchFaqs();
      fetchWebsiteSettings();
      fetchSeoSettings();
      fetchLandingPageContent();
      fetchCourses();
      fetchBroadcastHistory();
    }
  }, []);

  // Refetch analytics when time filter changes
  useEffect(() => {
    if (token && user && user.role === "admin") {
      fetchAnalytics();
    }
  }, [timeFilter]);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics?time_filter=${timeFilter}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAnalytics(response.data);
    } catch (error) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllData = async () => {
    try {
      // Build user query params
      const userParams = new URLSearchParams({
        skip: userPage * userLimit,
        limit: userLimit
      });
      
      if (userSearch) userParams.append('search', userSearch);
      if (userFilters.role !== 'all') userParams.append('role', userFilters.role);
      if (userFilters.is_verified !== null) userParams.append('is_verified', userFilters.is_verified);
      if (userFilters.locked !== null) userParams.append('locked', userFilters.locked);
      if (userFilters.subscription_plan !== 'all') userParams.append('subscription_plan', userFilters.subscription_plan);
      
      const [
        usersRes,
        scholarshipsRes,
        examsRes,
        resourcesRes,
        eventsRes,
        mentorsRes,
        quizzesRes,
        collegesRes,
        careersRes,
        bookingsRes,
        currenciesRes,
        countriesRes,
        exchangeRatesRes,
        currencySettingsRes,
        languagesRes,
        emailSettingsRes,
        badgesRes,
        rewardsRes,
        readinessSettingsRes,
        mockInterviewsRes,
        opportunitiesRes,
        supportTicketsRes
      ] = await Promise.all([
        axios.get(`${API}/admin/users?${userParams.toString()}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/scholarships/all`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/exams/all`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/resources/all`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/events/all`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/mentors/all`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/quizzes/all`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/colleges`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/careers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/bookings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/currencies/all`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({data: {currencies: []}})),
        axios.get(`${API}/admin/countries/all`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({data: {countries: []}})),
        axios.get(`${API}/admin/exchange-rates/all`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({data: {rates: []}})),
        axios.get(`${API}/admin/currency-settings`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({data: null})),
        axios.get(`${API}/admin/languages/all`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({data: {languages: []}})),
        axios.get(`${API}/admin/email-settings`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({data: {enabled: false, host: '', port: 587, username: '', password: '', from_email: ''}})),
        axios.get(`${API}/badges`).catch(() => ({data: []})),
        axios.get(`${API}/rewards`).catch(() => ({data: []})),
        axios.get(`${API}/admin/career-readiness-settings`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({data: {profile_completion_weight: 20, assessment_weight: 30, skills_weight: 20, consultation_weight: 15, active_engagement_weight: 15}})),
        axios.get(`${API}/admin/mock-interview/sessions`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({data: {sessions: []}})),
        axios.get(`${API}/admin/opportunities/all`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({data: []})),
        axios.get(`${API}/support/admin/tickets`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({data: {tickets: []}})),
      ]);

      setUsers(usersRes.data.users || usersRes.data);
      setTotalUsers(usersRes.data.total || usersRes.data.length);
      setScholarships(scholarshipsRes.data);
      setExams(examsRes.data);
      setResources(resourcesRes.data);
      setEvents(eventsRes.data);
      setMentors(mentorsRes.data);
      setQuizzes(quizzesRes.data);
      setColleges(collegesRes.data.colleges || []);
      setCareers(careersRes.data);
      setOpportunities(opportunitiesRes.data.opportunities || []);
      setSupportTickets(supportTicketsRes.data.tickets || []);
      setBookings(bookingsRes.data);
      setCurrencies(currenciesRes.data.currencies || []);
      setCountries(countriesRes.data.countries || []);
      setExchangeRates(exchangeRatesRes.data.rates || []);
      setCurrencySettings(currencySettingsRes.data);
      setLanguages(languagesRes.data.languages || []);
      setEmailSettings(emailSettingsRes.data);
      setBadges(badgesRes.data || []);
      setRewards(rewardsRes.data || []);
      setReadinessSettings(readinessSettingsRes.data || {
        profile_completion_weight: 20,
        assessment_weight: 30,
        skills_weight: 20,
        consultation_weight: 15,
        active_engagement_weight: 15
      });
      setMockInterviewSessions(mockInterviewsRes.data.sessions || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("User deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) return;
    
    try {
      const entityTypes = {
        scholarship: 'scholarships',
        exam: 'exams',
        resource: 'resources',
        event: 'events',
        mentor: 'mentors',
        quiz: 'quizzes',
        college: 'colleges',
        career: 'careers',
        course: 'courses',  // ADD THIS - courses were missing!
        opportunity: 'opportunities',
        currency: 'currencies',
        country: 'countries',
        exchangeRate: 'exchange-rates',
        language: 'languages'
      };
      
      await axios.delete(
        `${API}/admin/${entityTypes[type]}/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${type} deleted successfully`);
      fetchAllData();
    } catch (error) {
      toast.error(`Failed to delete ${type}`);
      console.error(error);
    }
  };

  // ============= FAQ Management Functions =============
  
  const fetchFaqs = async () => {
    try {
      const response = await axios.get(`${API}/support/admin/faqs/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFaqs(response.data.faqs || []);
    } catch (error) {
      console.error("Error fetching FAQs:", error);
      toast.error("Failed to load FAQs");
    }
  };
  
  const handleInitializeFaqs = async () => {
    try {
      const response = await axios.post(
        `${API}/support/admin/faqs/initialize`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchFaqs();
    } catch (error) {
      toast.error("Failed to initialize FAQs");
      console.error(error);
    }
  };
  
  const handleSaveFaq = async () => {
    if (!faqFormData.question || !faqFormData.answer) {
      toast.error("Question and Answer are required");
      return;
    }
    
    try {
      if (editingFaq) {
        // Update existing FAQ
        await axios.patch(
          `${API}/support/admin/faqs/${editingFaq.id}`,
          faqFormData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("FAQ updated successfully");
      } else {
        // Create new FAQ
        await axios.post(
          `${API}/support/admin/faqs`,
          faqFormData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("FAQ created successfully");
      }
      
      setIsFaqDialogOpen(false);
      setEditingFaq(null);
      setFaqFormData({
        question: "",
        answer: "",
        category: "Account",
        status: true
      });
      fetchFaqs();
    } catch (error) {
      toast.error("Failed to save FAQ");
      console.error(error);
    }
  };
  
  const handleEditFaq = (faq) => {
    setEditingFaq(faq);
    setFaqFormData({
      question: faq.question,
      answer: faq.answer,
      category: faq.category,
      status: faq.status
    });
    setIsFaqDialogOpen(true);
  };
  
  const handleDeleteFaq = async (faqId) => {
    if (!window.confirm("Are you sure you want to delete this FAQ?")) return;
    
    try {
      await axios.delete(`${API}/support/admin/faqs/${faqId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("FAQ deleted successfully");
      fetchFaqs();
    } catch (error) {
      toast.error("Failed to delete FAQ");
      console.error(error);
    }
  };
  
  const handleToggleFaqStatus = async (faq) => {
    try {
      await axios.patch(
        `${API}/support/admin/faqs/${faq.id}`,
        { status: !faq.status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`FAQ ${!faq.status ? "activated" : "deactivated"}`);
      fetchFaqs();
    } catch (error) {
      toast.error("Failed to update FAQ status");
      console.error(error);
    }
  };
  
  // Filter FAQs based on search and filters
  const filteredFaqs = faqs.filter(faq => {
    const matchesSearch = faqSearch === "" || 
      faq.question.toLowerCase().includes(faqSearch.toLowerCase()) ||
      faq.answer.toLowerCase().includes(faqSearch.toLowerCase());
    
    const matchesCategory = faqCategoryFilter === "all" || faq.category === faqCategoryFilter;
    
    const matchesStatus = faqStatusFilter === "all" ||
      (faqStatusFilter === "active" && faq.status) ||
      (faqStatusFilter === "inactive" && !faq.status);
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // ============= Website Settings Functions =============
  
  const fetchWebsiteSettings = async () => {
    setIsWebsiteSettingsLoading(true);
    try {
      const response = await axios.get(`${API}/cms/admin/website-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWebsiteSettings(response.data.settings);
    } catch (error) {
      console.error("Error fetching website settings:", error);
      toast.error("Failed to load website settings");
    } finally {
      setIsWebsiteSettingsLoading(false);
    }
  };
  
  const initializeWebsiteSettings = async () => {
    try {
      const response = await axios.post(
        `${API}/cms/admin/website-settings/initialize`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchWebsiteSettings();
    } catch (error) {
      toast.error("Failed to initialize website settings");
      console.error(error);
    }
  };
  
  const updateWebsiteSettings = async (section, data) => {
    try {
      // Log for debugging
      console.log(`🔧 Updating ${section}:`, data);
      
      await axios.patch(
        `${API}/cms/admin/website-settings`,
        { [section]: data },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${section} section updated successfully`);
      fetchWebsiteSettings();
      setEditingSection(null);
    } catch (error) {
      toast.error(`Failed to update ${section} section`);
      console.error('Update error:', error.response?.data || error);
    }
  };
  
  const publishWebsiteSettings = async () => {
    try {
      await axios.post(
        `${API}/cms/admin/website-settings/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Website settings published successfully!");
      fetchWebsiteSettings();
    } catch (error) {
      toast.error("Failed to publish website settings");
      console.error(error);
    }
  };

  const handleAssetUpload = async (file, assetType) => {
    try {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB");
        return;
      }

      // Show uploading toast
      toast.info(`Uploading ${assetType}...`);

      // Create FormData
      const formData = new FormData();
      formData.append('file', file);
      formData.append('asset_type', assetType);

      // Upload file
      const response = await axios.post(
        `${API}/cms/admin/upload-asset`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Update local state with the returned data URL
      const { asset_url } = response.data;
      if (assetType === 'logo') {
        setWebsiteSettings({...websiteSettings, logo_url: asset_url});
      } else if (assetType === 'favicon') {
        setWebsiteSettings({...websiteSettings, favicon_url: asset_url});
      }

      if (assetType) {
        toast.success(`${assetType.charAt(0).toUpperCase() + assetType.slice(1)} uploaded successfully!`);
      }
      
      // Refresh settings from server
      fetchWebsiteSettings();
    } catch (error) {
      console.error("Asset upload error:", error);
      toast.error(error.response?.data?.detail || `Failed to upload ${assetType}`);
    }
  };

  // ============= Courses Management Functions =============
  
  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data.courses || []);
    } catch (error) {
      console.error("Error fetching courses:", error);
      toast.error("Failed to fetch courses");
    }
  };

  // Course Image Upload Handler
  const handleCourseImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        return;
      }
      
      setCourseImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setCourseImagePreview(reader.result);
        setCourseFormData({ ...courseFormData, image: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeCourseImage = () => {
    setCourseImageFile(null);
    setCourseImagePreview(null);
    setCourseFormData({ ...courseFormData, image: '' });
  };

  // Array field handlers
  const addArrayItem = (fieldName) => {
    setCourseFormData({
      ...courseFormData,
      [fieldName]: [...courseFormData[fieldName], '']
    });
  };

  const updateArrayItem = (fieldName, index, value) => {
    const newArray = [...courseFormData[fieldName]];
    newArray[index] = value;
    setCourseFormData({ ...courseFormData, [fieldName]: newArray });
  };

  const removeArrayItem = (fieldName, index) => {
    const newArray = courseFormData[fieldName].filter((_, i) => i !== index);
    setCourseFormData({ ...courseFormData, [fieldName]: newArray });
  };

  // Legacy functions for backwards compatibility
  const addHighlight = () => addArrayItem('prerequisites');
  const updateHighlight = (index, value) => updateArrayItem('prerequisites', index, value);
  const removeHighlight = (index) => removeArrayItem('prerequisites', index);
  
  const addAIFeature = () => addArrayItem('skills_gained');
  const updateAIFeature = (index, value) => updateArrayItem('skills_gained', index, value);
  const removeAIFeature = (index) => removeArrayItem('skills_gained', index);

  // Validate course form
  const validateCourseForm = () => {
    const errors = {};
    
    if (!courseFormData.title.trim()) {
      errors.title = 'Course title is required';
    }
    if (!courseFormData.category.trim()) {
      errors.category = 'Category is required';
    }
    if (!courseFormData.duration.trim()) {
      errors.duration = 'Duration is required';
    }
    if (!courseFormData.level.trim()) {
      errors.level = 'Level is required';
    }
    if (courseFormData.price < 0) {
      errors.price = 'Price cannot be negative';
    }
    if (!courseFormData.description.trim()) {
      errors.description = 'Description is required';
    }
    if (!courseFormData.image && !courseImagePreview) {
      errors.image = 'Course image is required';
    }
    
    setCourseFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateCourse = async () => {
    if (!validateCourseForm()) {
      toast.error('Please fix form errors before submitting');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/admin/courses`,
        courseFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Course created successfully!");
      setIsCourseDialogOpen(false);
      resetCourseForm();
      fetchCourses();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Failed to create course";
      toast.error(errorMsg);
      console.error("Course creation error:", error.response?.data || error);
    }
  };

  const handleUpdateCourse = async () => {
    if (!validateCourseForm()) {
      toast.error('Please fix form errors before submitting');
      return;
    }
    
    try {
      await axios.patch(
        `${API}/admin/courses/${editingCourse.id}`,
        courseFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Course updated successfully!");
      setIsCourseDialogOpen(false);
      setEditingCourse(null);
      resetCourseForm();
      fetchCourses();
    } catch (error) {
      toast.error("Failed to update course");
      console.error(error);
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm("Are you sure you want to delete this course?")) return;
    try {
      await axios.delete(
        `${API}/admin/courses/${courseId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Course deleted successfully!");
      fetchCourses();
    } catch (error) {
      toast.error("Failed to delete course");
      console.error(error);
    }
  };

  const openCourseDialog = (course = null) => {
    if (course) {
      setEditingCourse(course);
      setCourseFormData({
        title: course.title || "",
        category: course.category || "",
        description: course.description || "",
        duration: course.duration || "",
        level: course.level || "",
        price: course.price || 0,
        image: course.image || course.image_url || "",
        prerequisites: course.prerequisites || [],
        syllabus_overview: course.syllabus_overview || [],
        career_paths: course.career_paths || [],
        average_salary_india: course.average_salary_india || "",
        average_salary_global: course.average_salary_global || "",
        top_recruiters: course.top_recruiters || [],
        skills_gained: course.skills_gained || [],
        is_popular: course.is_popular || false,
        is_active: course.is_active !== undefined ? course.is_active : true
      });
      setCourseImagePreview(course.image || course.image_url || null);
    } else {
      resetCourseForm();
    }
    setIsCourseDialogOpen(true);
  };

  const resetCourseForm = () => {
    setCourseFormData({
      title: "",
      category: "",
      description: "",
      duration: "",
      level: "",
      price: 0,
      image: "",
      prerequisites: [],
      syllabus_overview: [],
      career_paths: [],
      average_salary_india: "",
      average_salary_global: "",
      top_recruiters: [],
      skills_gained: [],
      is_popular: false,
      is_active: true
    });
    setEditingCourse(null);
    setCourseImageFile(null);
    setCourseImagePreview(null);
    setCourseFormErrors({});
    setShowCoursePreview(false);
  };

  // ============= Lesson Management Functions =============
  
  const openLessonManager = (course) => {
    setSelectedCourseForLessons(course);
    setIsLessonDialogOpen(true);
  };

  const openLessonForm = (lesson = null) => {
    if (lesson) {
      setEditingLesson(lesson);
      setLessonFormData({
        title: lesson.title || "",
        video_url: lesson.video_url || "",
        duration_minutes: lesson.duration_minutes || 0,
        description: lesson.description || "",
        order: lesson.order || 1,
        materials: lesson.materials || [],
        is_preview: lesson.is_preview || false
      });
    } else {
      setEditingLesson(null);
      const nextOrder = selectedCourseForLessons?.lessons?.length ? 
        Math.max(...selectedCourseForLessons.lessons.map(l => l.order || 0)) + 1 : 1;
      setLessonFormData({
        title: "",
        video_url: "",
        duration_minutes: 0,
        description: "",
        order: nextOrder,
        materials: [],
        is_preview: false
      });
    }
  };

  const openViewLesson = (lesson) => {
    setViewingLesson(lesson);
    setIsViewLessonDialogOpen(true);
  };

  const closeViewLesson = () => {
    setViewingLesson(null);
    setIsViewLessonDialogOpen(false);
  };

  const handleSaveLesson = async () => {
    if (!selectedCourseForLessons) return;
    
    try {
      if (editingLesson) {
        // Update existing lesson
        await axios.put(
          `${API}/courses/admin/${selectedCourseForLessons.id}/lessons/${editingLesson.id}`,
          lessonFormData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Lesson updated successfully!");
      } else {
        // Add new lesson
        await axios.post(
          `${API}/courses/admin/${selectedCourseForLessons.id}/lessons`,
          lessonFormData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Lesson added successfully!");
      }
      
      // Refresh courses
      await fetchCourses();
      
      // Update selected course
      const updatedCourse = courses.find(c => c.id === selectedCourseForLessons.id);
      if (updatedCourse) {
        setSelectedCourseForLessons(updatedCourse);
      }
      
      // Reset form
      setEditingLesson(null);
      openLessonForm();
    } catch (error) {
      console.error("Error saving lesson:", error);
      toast.error(error.response?.data?.detail || "Failed to save lesson");
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    if (!selectedCourseForLessons) return;
    
    const lesson = selectedCourseForLessons.lessons?.find(l => l.id === lessonId);
    const lessonTitle = lesson?.title || 'this lesson';
    
    if (!window.confirm(`⚠️ Are you sure you want to delete "${lessonTitle}"?\n\nThis action cannot be undone.`)) return;
    
    try {
      await axios.delete(
        `${API}/courses/admin/${selectedCourseForLessons.id}/lessons/${lessonId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`✅ Lesson "${lessonTitle}" deleted successfully!`);
      
      // Refresh courses
      await fetchCourses();
      
      // Update selected course
      const updatedCourse = courses.find(c => c.id === selectedCourseForLessons.id);
      if (updatedCourse) {
        setSelectedCourseForLessons(updatedCourse);
      }
    } catch (error) {
      console.error("Error deleting lesson:", error);
      toast.error(error.response?.data?.detail || "❌ Failed to delete lesson");
    }
  };

  // ============= SEO Management Functions =============
  
  const fetchSeoSettings = async () => {
    try {
      const response = await axios.get(`${API}/seo/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSeoSettings(response.data.seo_settings || []);
    } catch (error) {
      console.error("Error fetching SEO settings:", error);
      toast.error("Failed to load SEO settings");
    }
  };
  
  const initializeSeoSettings = async () => {
    try {
      const response = await axios.post(
        `${API}/seo/admin/initialize`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchSeoSettings();
    } catch (error) {
      toast.error("Failed to initialize SEO settings");
      console.error(error);
    }
  };
  
  const handleEditSeo = (seo) => {
    setEditingSeoPage(seo);
    setSeoFormData({
      page_name: seo.page_name,
      page_title: seo.page_title,
      meta_title: seo.meta_title,
      meta_description: seo.meta_description,
      meta_keywords: seo.meta_keywords.join(', '),
      robots: seo.robots
    });
    setIsSeoDialogOpen(true);
  };
  
  const handleSaveSeo = async () => {
    if (!seoFormData.meta_title || !seoFormData.meta_description) {
      toast.error("Meta title and description are required");
      return;
    }
    
    try {
      const payload = {
        page_title: seoFormData.page_title,
        meta_title: seoFormData.meta_title,
        meta_description: seoFormData.meta_description,
        meta_keywords: seoFormData.meta_keywords.split(',').map(k => k.trim()).filter(k => k),
        robots: seoFormData.robots
      };
      
      await axios.patch(
        `${API}/seo/admin/${seoFormData.page_name}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("SEO settings updated successfully");
      setIsSeoDialogOpen(false);
      setEditingSeoPage(null);
      fetchSeoSettings();
    } catch (error) {
      toast.error("Failed to update SEO settings");
      console.error(error);
    }
  };

  // Support ticket functions
  const fetchTicketMessages = async (ticketId) => {
    try {
      const response = await axios.get(`${API}/support/admin/ticket/${ticketId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTicketMessages(response.data.messages);
    } catch (error) {
      toast.error("Failed to load messages");
      console.error(error);
    }
  };

  const sendAdminResponse = async (ticketId) => {
    if (!adminMessage.trim()) return;
    
    try {
      await axios.post(`${API}/support/admin/respond`, {
        ticket_id: ticketId,
        message: adminMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAdminMessage("");
      fetchTicketMessages(ticketId);
      fetchAllData(); // Refresh ticket list
      toast.success("Response sent");
    } catch (error) {
      toast.error("Failed to send response");
      console.error(error);
    }
  };

  const updateTicketStatus = async (ticketId, status) => {
    try {
      await axios.patch(`${API}/support/admin/ticket/status`, {
        ticket_id: ticketId,
        status: status,
        assigned_to: user.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Status updated");
      fetchAllData();
      
      // Update selected ticket status locally
      if (selectedTicket && selectedTicket.id === ticketId) {
        setSelectedTicket({...selectedTicket, status: status});
      }
    } catch (error) {
      toast.error("Failed to update status");
      console.error(error);
    }
  };

  // ============= Landing Page CMS Functions =============
  
  const fetchLandingPageContent = async () => {
    setIsLandingPageLoading(true);
    try {
      const response = await axios.get(`${API}/cms/admin/website-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLandingPageContent(response.data.settings);
    } catch (error) {
      toast.error("Failed to load landing page content");
      console.error(error);
    } finally {
      setIsLandingPageLoading(false);
    }
  };

  const saveLandingPageContent = async () => {
    try {
      await axios.patch(`${API}/cms/admin/website-settings`, landingPageContent, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Landing page updated successfully!");
      await fetchLandingPageContent(); // Refresh
      setEditingLandingSection(null);
    } catch (error) {
      toast.error("Failed to update landing page");
      console.error(error);
    }
  };

  const updateLandingSection = (section, data) => {
    setLandingPageContent(prev => ({
      ...prev,
      [section]: data
    }));
  };

  const addFeature = () => {
    const newFeature = {
      icon: "🎯",
      title: "New Feature",
      description: "Feature description",
      color: "#667eea"
    };
    setLandingPageContent(prev => ({
      ...prev,
      features: {
        ...prev.features,
        features: [...(prev.features?.features || []), newFeature]
      }
    }));
  };

  const removeFeature = (index) => {
    setLandingPageContent(prev => ({
      ...prev,
      features: {
        ...prev.features,
        features: prev.features.features.filter((_, i) => i !== index)
      }
    }));
  };

  const addTestimonial = () => {
    const newTestimonial = {
      name: "User Name",
      role: "Job Title",
      content: "Testimonial content",
      avatar: "https://i.pravatar.cc/150",
      rating: 5
    };
    setLandingPageContent(prev => ({
      ...prev,
      testimonials: {
        ...prev.testimonials,
        testimonials: [...(prev.testimonials?.testimonials || []), newTestimonial]
      }
    }));
  };

  const removeTestimonial = (index) => {
    setLandingPageContent(prev => ({
      ...prev,
      testimonials: {
        ...prev.testimonials,
        testimonials: prev.testimonials.testimonials.filter((_, i) => i !== index)
      }
    }));
  };

  const fetchPlansAndPaymentDetails = async () => {
    try {
      const [plansRes, paymentRes] = await Promise.all([
        axios.get(`${API}/subscriptions/plans`),
        axios.get(`${API}/admin/bank-accounts`, { headers: { Authorization: `Bearer ${token}` } })
          .catch(() => axios.get(`${API}/admin/upi-settings`, { headers: { Authorization: `Bearer ${token}` } }))
          .catch(() => ({data: null}))
      ]);
      
      setSubscriptionPlans(plansRes.data.plans || []);
      
      // Get first available payment detail (bank or UPI)
      if (paymentRes.data?.accounts?.length > 0) {
        setPaymentDetails({type: 'bank', ...paymentRes.data.accounts[0]});
      } else if (paymentRes.data?.upi_id) {
        setPaymentDetails({type: 'upi', ...paymentRes.data});
      }
    } catch (error) {
      console.error("Failed to fetch plans/payment details", error);
    }
  };

  const lockUser = async (userId) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/lock`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("User locked");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to lock user");
    }
  };

  const unlockUser = async (userId) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/unlock`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("User unlocked");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to unlock user");
    }
  };

  const handleVerifyUser = async (userId) => {
    try {
      const response = await axios.post(`${API}/admin/users/${userId}/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.data.already_verified) {
        toast.info("User is already verified");
      } else {
        toast.success("✅ User verified successfully! Welcome email sent.");
      }
      
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to verify user");
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to delete user ${userEmail}?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      toast.success("🗑️ User deleted successfully");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleWalletTopup = async () => {
    if (!walletAmount || parseFloat(walletAmount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    
    try {
      await axios.post(
        `${API}/admin/users/${selectedUser.id}/topup-wallet`,
        { amount: parseFloat(walletAmount) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Wallet topped up successfully");
      setShowWalletModal(false);
      setWalletAmount('');
      fetchAllData();
    } catch (error) {
      toast.error("Failed to topup wallet");
    }
  };

  const handlePlanUpgrade = async () => {
    try {
      await axios.post(
        `${API}/admin/users/${selectedUser.id}/upgrade-plan`,
        { plan_type: selectedPlan },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`User plan upgraded to ${selectedPlan}`);
      setShowPlanModal(false);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to upgrade plan");
    }
  };

  const deleteEntity = async (entity, id) => {
    if (!window.confirm(`Are you sure you want to delete this ${entity}?`)) return;
    try {
      await axios.delete(`${API}/admin/${entity}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${entity} deleted`);
      fetchAllData();
    } catch (error) {
      toast.error(`Failed to delete ${entity}`);
    }
  };

  const viewInterviewDetails = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/admin/mock-interview/session/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedInterview(response.data);
      setShowInterviewModal(true);
    } catch (error) {
      toast.error("Failed to load interview details");
      console.error(error);
    }
  };

  // Modal management
  const openModal = (type, item = null) => {
    setModals(prev => ({...prev, [type]: true}));
    if (item) {
      setEditing(prev => ({...prev, [type]: item}));
      // Pre-fill form for editing
      const formData = {...item};
      
      // Convert arrays to comma-separated strings for form inputs
      if (formData.eligibility && Array.isArray(formData.eligibility)) {
        formData.eligibility = formData.eligibility.join(", ");
      }
      if (formData.subjects && Array.isArray(formData.subjects)) {
        formData.subjects = formData.subjects.join(", ");
      }
      if (formData.career_paths && Array.isArray(formData.career_paths)) {
        formData.career_paths = formData.career_paths.join(", ");
      }
      if (formData.expertise && Array.isArray(formData.expertise)) {
        formData.expertise = formData.expertise.join(", ");
      }
      if (formData.courses && Array.isArray(formData.courses)) {
        formData.courses = formData.courses.join(", ");
      }
      if (formData.admission_requirements && Array.isArray(formData.admission_requirements)) {
        formData.admission_requirements = formData.admission_requirements.join(", ");
      }
      
      // Mentor-specific array fields
      if (formData.qualifications && Array.isArray(formData.qualifications)) {
        formData.qualifications = formData.qualifications.join(", ");
      }
      if (formData.skills && Array.isArray(formData.skills)) {
        formData.skills = formData.skills.join(", ");
      }
      
      // Career-specific array fields
      if (formData.required_education && Array.isArray(formData.required_education)) {
        formData.required_education = formData.required_education.join(", ");
      }
      if (formData.certifications && Array.isArray(formData.certifications)) {
        formData.certifications = formData.certifications.join(", ");
      }
      if (formData.top_companies && Array.isArray(formData.top_companies)) {
        formData.top_companies = formData.top_companies.join(", ");
      }
      if (formData.roadmap && Array.isArray(formData.roadmap)) {
        formData.roadmap = JSON.stringify(formData.roadmap, null, 2);
      }
      
      // Opportunity-specific array fields
      if (formData.skills_required && Array.isArray(formData.skills_required)) {
        formData.skills_required = formData.skills_required.join(", ");
      }
      
      // College-specific fields - flatten nested objects
      if (type === 'college') {
        if (formData.ranking) {
          formData.ranking_nirf = formData.ranking.nirf || "";
          formData.ranking_india_today = formData.ranking.india_today || "";
          formData.ranking_qs_world = formData.ranking.qs_world || "";
        }
        if (formData.courses_offered && Array.isArray(formData.courses_offered)) {
          formData.courses_offered = formData.courses_offered.join(", ");
        }
        if (formData.admission_criteria) {
          formData.admission_ug = formData.admission_criteria.undergraduate || "";
          formData.admission_pg = formData.admission_criteria.postgraduate || "";
        }
        if (formData.fees) {
          formData.fees_ug = formData.fees.undergraduate || "";
          formData.fees_pg = formData.fees.postgraduate || "";
        }
        if (formData.placements) {
          formData.placement_avg = formData.placements.average_package || "";
          formData.placement_highest = formData.placements.highest_package || "";
          formData.placement_rate = formData.placements.placement_rate || "";
          if (formData.placements.top_recruiters && Array.isArray(formData.placements.top_recruiters)) {
            formData.top_recruiters = formData.placements.top_recruiters.join(", ");
          }
        }
        if (formData.scholarships && Array.isArray(formData.scholarships)) {
          formData.scholarships = formData.scholarships.join(", ");
        }
        if (formData.infrastructure) {
          formData.infra_library = formData.infrastructure.library || "";
          formData.infra_hostels = formData.infrastructure.hostels || "";
          formData.infra_labs = formData.infrastructure.labs || "";
          formData.infra_sports = formData.infrastructure.sports || "";
          formData.infra_other = formData.infrastructure.other || "";
        }
        if (formData.contact) {
          formData.contact_phone = formData.contact.phone || "";
          formData.contact_email = formData.contact.email || "";
        }
        if (formData.accreditation && Array.isArray(formData.accreditation)) {
          formData.accreditation = formData.accreditation.join(", ");
        }
      }
      
      setForms(prev => ({...prev, [type]: formData}));
    }
  };

  const closeModal = (type) => {
    setModals(prev => ({...prev, [type]: false}));
    setEditing(prev => ({...prev, [type]: null}));
    // Reset form
    const resetForms = {
      scholarship: {
        name: "", organization: "", amount: "", eligibility: "", deadline: "",
        min_grade: 1, max_grade: 12, category: "", application_link: "", description: ""
      },
      exam: {
        name: "", full_name: "", exam_date: "", registration_deadline: "",
        eligibility: "", subjects: "", career_paths: "", description: ""
      },
      resource: {
        title: "", type: "article", category: "", url: "", description: "", thumbnail: "", duration: ""
      },
      event: {
        title: "", type: "webinar", date: "", time: "", speaker: "", description: "", registration_link: "", max_attendees: ""
      },
      mentor: {
        name: "", profession: "", company: "", designation: "", expertise: "", bio: "", 
        image: "", education: "", qualifications: "", skills: "", experience_years: 0, 
        work_experience: "", email: "", contact_number: "", rate_per_minute: 2.0, 
        availability: true
      },
      quiz: {
        title: "", category: "", difficulty: "medium", points: 10, time_limit: 30
      },
      college: {
        name: "", description: "", location: "", state: "", type: "", established: "", 
        image: "", website: "", ranking_nirf: "", ranking_india_today: "", ranking_qs_world: "",
        courses_offered: "", admission_ug: "", admission_pg: "", cutoff_marks: "",
        fees_ug: "", fees_pg: "", placement_avg: "", placement_highest: "", placement_rate: "",
        top_recruiters: "", scholarships: "", infra_library: "", infra_hostels: "", infra_labs: "",
        infra_sports: "", infra_other: "", contact_phone: "", contact_email: "", accreditation: "",
        is_featured: false, is_active: true
      },
      career: {
        title: "", description: "", category: "", required_education: "", skills: "", 
        salary_range_india: "", salary_range_global: "", job_outlook: "", timeline_to_enter: "", 
        work_life_balance: "", growth_potential: "", work_environment: "",
        certifications: "", top_companies: "", day_in_life: "", roadmap: ""
      },
      opportunity: {
        title: "", company_name: "", type: "internship", location: "", work_mode: "remote",
        field: "", experience_level: "entry", description: "", requirements: "",
        skills_required: "", salary: "", stipend: "", duration: "", start_date: "",
        application_deadline: "", application_link: "", status: "active", spots_available: null
      },
      currency: {
        code: "", symbol: "", name: "", decimal_places: 2, is_active: true
      },
      country: {
        name: "", code: "", currency_code: "", is_active: true
      },
      exchangeRate: {
        from_currency: "USD", to_currency: "", rate: "", is_manual: true
      },
      language: {
        code: "", name: "", native_name: "", is_active: true, is_default: false
      }
    };
    setForms(prev => ({...prev, [type]: resetForms[type]}));
  };

  const updateForm = (type, field, value) => {
    setForms(prev => ({
      ...prev,
      [type]: {...prev[type], [field]: value}
    }));
  };

  const handleSubmit = async (type) => {
    const formData = {...forms[type]};
    
    // Convert comma-separated strings to arrays
    if (formData.eligibility && typeof formData.eligibility === 'string') {
      formData.eligibility = formData.eligibility.split(',').map(s => s.trim()).filter(s => s);
    }
    if (formData.subjects && typeof formData.subjects === 'string') {
      formData.subjects = formData.subjects.split(',').map(s => s.trim()).filter(s => s);
    }
    if (formData.career_paths && typeof formData.career_paths === 'string') {
      formData.career_paths = formData.career_paths.split(',').map(s => s.trim()).filter(s => s);
    }
    if (formData.expertise && typeof formData.expertise === 'string') {
      formData.expertise = formData.expertise.split(',').map(s => s.trim()).filter(s => s);
    }
    if (formData.courses && typeof formData.courses === 'string') {
      formData.courses = formData.courses.split(',').map(s => s.trim()).filter(s => s);
    }
    if (formData.admission_requirements && typeof formData.admission_requirements === 'string') {
      formData.admission_requirements = formData.admission_requirements.split(',').map(s => s.trim()).filter(s => s);
    }
    // Mentor-specific array fields
    if (formData.qualifications && typeof formData.qualifications === 'string') {
      formData.qualifications = formData.qualifications.split(',').map(s => s.trim()).filter(s => s);
    }
    if (formData.skills && typeof formData.skills === 'string') {
      formData.skills = formData.skills.split(',').map(s => s.trim()).filter(s => s);
    }
    // Career-specific array fields
    if (formData.required_education && typeof formData.required_education === 'string') {
      formData.required_education = formData.required_education.split(',').map(s => s.trim()).filter(s => s);
    }
    if (type === 'career' && formData.skills && typeof formData.skills === 'string') {
      formData.skills = formData.skills.split(',').map(s => s.trim()).filter(s => s);
    }
    if (type === 'career' && formData.certifications && typeof formData.certifications === 'string') {
      formData.certifications = formData.certifications.split(',').map(s => s.trim()).filter(s => s);
    }
    if (type === 'career' && formData.top_companies && typeof formData.top_companies === 'string') {
      formData.top_companies = formData.top_companies.split(',').map(s => s.trim()).filter(s => s);
    }
    if (type === 'career' && formData.roadmap && typeof formData.roadmap === 'string') {
      try {
        formData.roadmap = JSON.parse(formData.roadmap);
      } catch (e) {
        // Keep as string if not valid JSON
      }
    }
    // Note: opportunity skills_required stays as string - backend will handle conversion
    
    // College-specific transformations - build nested objects
    if (type === 'college') {
      // Build ranking object
      formData.ranking = {
        nirf: parseInt(formData.ranking_nirf) || null,
        india_today: parseInt(formData.ranking_india_today) || null,
        qs_world: parseInt(formData.ranking_qs_world) || null
      };
      delete formData.ranking_nirf;
      delete formData.ranking_india_today;
      delete formData.ranking_qs_world;
      
      // Build courses_offered array
      if (formData.courses_offered && typeof formData.courses_offered === 'string') {
        formData.courses_offered = formData.courses_offered.split(',').map(s => s.trim()).filter(s => s);
      }
      
      // Build admission_criteria object
      formData.admission_criteria = {
        undergraduate: formData.admission_ug || "",
        postgraduate: formData.admission_pg || "",
        cutoff: formData.cutoff_marks || ""
      };
      delete formData.admission_ug;
      delete formData.admission_pg;
      // Keep cutoff_marks as separate field too
      
      // Build fees object
      formData.fees = {
        undergraduate: formData.fees_ug || "",
        postgraduate: formData.fees_pg || ""
      };
      delete formData.fees_ug;
      delete formData.fees_pg;
      
      // Build placements object
      const topRecruitersArray = formData.top_recruiters && typeof formData.top_recruiters === 'string' 
        ? formData.top_recruiters.split(',').map(s => s.trim()).filter(s => s)
        : [];
      formData.placements = {
        average_package: formData.placement_avg || "",
        highest_package: formData.placement_highest || "",
        placement_rate: formData.placement_rate || "",
        top_recruiters: topRecruitersArray
      };
      delete formData.placement_avg;
      delete formData.placement_highest;
      delete formData.placement_rate;
      delete formData.top_recruiters;
      
      // Build scholarships array
      if (formData.scholarships && typeof formData.scholarships === 'string') {
        formData.scholarships = formData.scholarships.split(',').map(s => s.trim()).filter(s => s);
      }
      
      // Build infrastructure object
      formData.infrastructure = {
        library: formData.infra_library || "",
        hostels: formData.infra_hostels || "",
        labs: formData.infra_labs || "",
        sports: formData.infra_sports || "",
        other: formData.infra_other || ""
      };
      delete formData.infra_library;
      delete formData.infra_hostels;
      delete formData.infra_labs;
      delete formData.infra_sports;
      delete formData.infra_other;
      
      // Build contact object
      formData.contact = {
        phone: formData.contact_phone || "",
        email: formData.contact_email || ""
      };
      delete formData.contact_phone;
      delete formData.contact_email;
      
      // Build accreditation array
      if (formData.accreditation && typeof formData.accreditation === 'string') {
        formData.accreditation = formData.accreditation.split(',').map(s => s.trim()).filter(s => s);
      }
      
      // Convert established to number
      if (formData.established) {
        formData.established = parseInt(formData.established) || null;
      }
    }

    // Convert numeric strings to numbers
    if (formData.min_grade) formData.min_grade = parseInt(formData.min_grade);
    if (formData.max_grade) formData.max_grade = parseInt(formData.max_grade);
    if (formData.ranking) formData.ranking = parseInt(formData.ranking) || null;
    if (formData.max_attendees) formData.max_attendees = parseInt(formData.max_attendees) || null;
    if (formData.points) formData.points = parseInt(formData.points);
    if (formData.time_limit) formData.time_limit = parseInt(formData.time_limit) || null;
    if (formData.experience_years) formData.experience_years = parseInt(formData.experience_years) || 0;

    try {
      const entityTypes = {
        scholarship: 'scholarships',
        exam: 'exams',
        resource: 'resources',
        event: 'events',
        mentor: 'mentors',
        quiz: 'quizzes',
        college: 'colleges',
        career: 'careers',
        course: 'courses',  // ADD THIS - courses were missing!
        opportunity: 'opportunities',
        currency: 'currencies',
        country: 'countries',
        exchangeRate: 'exchange-rates',
        language: 'languages'
      };
      
      if (editing[type] && type !== 'exchangeRate') {
        // Update existing (exchange rates don't support update, only create/delete)
        await axios.patch(
          `${API}/admin/${entityTypes[type]}/${editing[type].id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`${type} updated successfully`);
      } else {
        // Create new
        await axios.post(
          `${API}/admin/${entityTypes[type]}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`${type} created successfully`);
      }
      
      closeModal(type);
      fetchAllData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || `Failed to ${editing[type] ? 'update' : 'create'} ${type}`;
      toast.error(errorMsg);
      console.error(error);
    }
  };
  // Export analytics to CSV
  const handleExportAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics/export?time_filter=${timeFilter}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics_${timeFilter}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Analytics exported successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export analytics");
    }
  };

  const handleBroadcastNotification = async () => {
    if (!broadcastForm.title || !broadcastForm.message) {
      toast.error("Title and message are required");
      return;
    }

    setBroadcastLoading(true);
    setBroadcastResult(null);

    try {
      const response = await axios.post(
        `${API}/admin/notifications/broadcast`,
        broadcastForm,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setBroadcastResult({
        success: true,
        message: response.data.message,
        users_notified: response.data.users_notified,
        emails_sent: response.data.emails_sent,
        emails_failed: response.data.emails_failed
      });

      toast.success(`Broadcast sent to ${response.data.users_notified} users!`);

      // Refresh broadcast history
      fetchBroadcastHistory();

      // Reset form after successful broadcast
      setTimeout(() => {
        setBroadcastForm({
          title: '',
          message: '',
          type: 'system_update',
          priority: 'normal',
          link: '',
          send_email: false,
          target_roles: null,
          target_subscription_tiers: null
        });
        setBroadcastResult(null);
      }, 5000);

    } catch (error) {
      console.error("Broadcast error:", error);
      setBroadcastResult({
        success: false,
        message: error.response?.data?.detail || "Failed to send broadcast notification"
      });
      toast.error(error.response?.data?.detail || "Failed to send broadcast notification");
    } finally {
      setBroadcastLoading(false);
    }
  };

  const fetchBroadcastHistory = async () => {
    if (!token) return; // Don't fetch if no token
    try {
      const response = await axios.get(`${API}/admin/notifications/broadcast/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBroadcastHistory(response.data.broadcasts || []);
    } catch (error) {
      console.error("Failed to fetch broadcast history:", error);
      // Don't show error toast as this is background fetch
    }
  };

  if (user?.role !== "admin" && user?.role !== "school_admin") {
    return null;
  }

  if (loading || !analytics) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Admin Control Panel
              </h1>
              <p className="text-gray-600 text-lg">Complete platform management & control</p>
            </div>
            <Button onClick={() => navigate("/database")} variant="outline">
              <Database className="w-4 h-4 mr-2" />
              Database Viewer
            </Button>
          </div>
        </div>

        {/* Analytics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Users</CardTitle>
              <Users className="w-5 h-5 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{analytics.users.total}</div>
              <p className="text-xs text-gray-500 mt-1">
                {analytics.users.students} students, {analytics.users.parents} parents
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Assessments</CardTitle>
              <FileText className="w-5 h-5 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{analytics.assessments.total}</div>
              <p className="text-xs text-gray-500 mt-1">Total completed</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Consultations</CardTitle>
              <Calendar className="w-5 h-5 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{analytics.bookings.total}</div>
              <p className="text-xs text-gray-500 mt-1">
                {analytics.bookings.pending} pending approval
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Content</CardTitle>
              <BookOpen className="w-5 h-5 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">
                {Object.values(analytics.content).reduce((a, b) => a + b, 0)}
              </div>
              <p className="text-xs text-gray-500 mt-1">Across all categories</p>
            </CardContent>
          </Card>
        </div>

        {/* Management Tabs */}
        <Card>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <CardHeader>
              <TabsList className="flex flex-wrap justify-start gap-2 h-auto bg-transparent p-2">
                <TabsTrigger value="overview" className="text-xs md:text-sm whitespace-nowrap">Overview</TabsTrigger>
                <TabsTrigger value="users" className="text-xs md:text-sm whitespace-nowrap">Users</TabsTrigger>
                <TabsTrigger value="security-monitoring" className="text-xs md:text-sm whitespace-nowrap bg-purple-600 text-white font-semibold" data-testid="security-monitoring-tab">🛡️ Security Monitor</TabsTrigger>
                <TabsTrigger value="assessments" className="text-xs md:text-sm whitespace-nowrap">Assessments</TabsTrigger>
                <TabsTrigger value="pricing" className="text-xs md:text-sm whitespace-nowrap">Pricing</TabsTrigger>
                <TabsTrigger value="bookings" className="text-xs md:text-sm whitespace-nowrap">Bookings</TabsTrigger>
                <TabsTrigger value="scholarships" className="text-xs md:text-sm whitespace-nowrap">Scholarships</TabsTrigger>
                <TabsTrigger value="exams" className="text-xs md:text-sm whitespace-nowrap">Exams</TabsTrigger>
                <TabsTrigger value="resources" className="text-xs md:text-sm whitespace-nowrap">Resources</TabsTrigger>
                <TabsTrigger value="events" className="text-xs md:text-sm whitespace-nowrap">Events</TabsTrigger>
                <TabsTrigger value="mentors" className="text-xs md:text-sm whitespace-nowrap">Mentors</TabsTrigger>
                <TabsTrigger value="quizzes" className="text-xs md:text-sm whitespace-nowrap">Quizzes</TabsTrigger>
                <TabsTrigger value="colleges" className="text-xs md:text-sm whitespace-nowrap">Colleges</TabsTrigger>
                <TabsTrigger value="careers" className="text-xs md:text-sm whitespace-nowrap">Careers</TabsTrigger>
                <TabsTrigger value="opportunities" className="text-xs md:text-sm whitespace-nowrap">Opportunities</TabsTrigger>
                <TabsTrigger value="courses" className="text-xs md:text-sm whitespace-nowrap">Courses</TabsTrigger>
                <TabsTrigger value="support" className="text-xs md:text-sm whitespace-nowrap">Support Tickets</TabsTrigger>
                <TabsTrigger value="faqs" className="text-xs md:text-sm whitespace-nowrap">FAQs</TabsTrigger>
                <TabsTrigger value="website" className="text-xs md:text-sm whitespace-nowrap">Website Settings</TabsTrigger>
                <TabsTrigger value="landing-page" className="text-xs md:text-sm whitespace-nowrap bg-gradient-to-r from-purple-500 to-pink-500 text-white">✨ Landing Page</TabsTrigger>
                <TabsTrigger value="seo" className="text-xs md:text-sm whitespace-nowrap">SEO Settings</TabsTrigger>
                <TabsTrigger value="payments" className="text-xs md:text-sm whitespace-nowrap">Payments</TabsTrigger>
                <TabsTrigger value="moderation" className="text-xs md:text-sm whitespace-nowrap bg-red-100 text-red-700">🛡️ Content Moderation</TabsTrigger>
                <TabsTrigger value="consultations" className="text-xs md:text-sm whitespace-nowrap">Consultations</TabsTrigger>
                <TabsTrigger value="currency" className="text-xs md:text-sm whitespace-nowrap">Currency Settings</TabsTrigger>
                <TabsTrigger value="languages" className="text-xs md:text-sm whitespace-nowrap">Languages</TabsTrigger>
                <TabsTrigger value="email" className="text-xs md:text-sm whitespace-nowrap">Email Settings</TabsTrigger>
                <TabsTrigger value="gamification" className="text-xs md:text-sm whitespace-nowrap">Gamification</TabsTrigger>
                <TabsTrigger value="progress" className="text-xs md:text-sm whitespace-nowrap">User Progress</TabsTrigger>
                <TabsTrigger value="mock-interviews" className="text-xs md:text-sm whitespace-nowrap">Mock Interviews</TabsTrigger>
                <TabsTrigger value="readiness-settings" className="text-xs md:text-sm whitespace-nowrap">Readiness Settings</TabsTrigger>
                <TabsTrigger value="notifications" className="text-xs md:text-sm whitespace-nowrap">🔔 Broadcast Notifications</TabsTrigger>
                <TabsTrigger value="import-data" className="text-xs md:text-sm whitespace-nowrap bg-gradient-to-r from-blue-500 to-purple-500 text-white">📥 Import Data</TabsTrigger>
                <TabsTrigger value="legal-compliance" className="text-xs md:text-sm whitespace-nowrap">Legal & Compliance</TabsTrigger>
              </TabsList>
            </CardHeader>

            <CardContent>
              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-6">
                {/* Quick Admin Actions */}
                <div>
                  <h3 className="text-xl font-semibold mb-3">Quick Admin Actions</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    <Card
                      className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 cursor-pointer hover:shadow-lg transition-shadow"
                      onClick={() => navigate('/admin/exam-prep/pyq')}
                      data-testid="admin-pyq-quick-action"
                    >
                      <CardHeader className="pb-2">
                        <CardTitle className="flex items-center gap-2 text-emerald-900 text-base">
                          <BookOpen className="h-5 w-5" />
                          Exam PYQ Management
                        </CardTitle>
                        <CardDescription className="text-xs">
                          Seed Previous Year Questions to boost AI accuracy
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <Button size="sm" className="w-full" data-testid="quick-go-pyq-btn">
                          Manage PYQs →
                        </Button>
                      </CardContent>
                    </Card>
                    <Card
                      className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 cursor-pointer hover:shadow-lg transition-shadow"
                      onClick={() => navigate('/admin/certificate-designer')}
                    >
                      <CardHeader className="pb-2">
                        <CardTitle className="flex items-center gap-2 text-purple-900 text-base">
                          <Award className="h-5 w-5" />
                          Certificate Designer
                        </CardTitle>
                        <CardDescription className="text-xs">
                          Customize course completion certificates
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <Button size="sm" className="w-full">
                          Design →
                        </Button>
                      </CardContent>
                    </Card>
                    <Card
                      className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 cursor-pointer hover:shadow-lg transition-shadow"
                      onClick={() => navigate('/admin/gamification')}
                    >
                      <CardHeader className="pb-2">
                        <CardTitle className="flex items-center gap-2 text-blue-900 text-base">
                          <Trophy className="h-5 w-5" />
                          Gamification
                        </CardTitle>
                        <CardDescription className="text-xs">
                          Manage badges, points & leaderboards
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <Button size="sm" className="w-full">
                          Configure →
                        </Button>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Filter and Export Controls */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                  <div className="flex items-center gap-3">
                    <Label htmlFor="time-filter" className="text-sm font-medium">Time Period:</Label>
                    <Select value={timeFilter} onValueChange={setTimeFilter}>
                      <SelectTrigger className="w-[180px]" id="time-filter">
                        <SelectValue placeholder="Select period" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="today">Today</SelectItem>
                        <SelectItem value="week">This Week</SelectItem>
                        <SelectItem value="month">This Month</SelectItem>
                        <SelectItem value="year">This Year</SelectItem>
                        <SelectItem value="all">All Time</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleExportAnalytics} variant="outline" className="gap-2">
                    <FileText className="w-4 h-4" />
                    Export CSV
                  </Button>
                </div>

                {/* User Stats */}
                <div>
                  <h3 className="text-xl font-semibold mb-4">User Statistics</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm opacity-90">Total Users</p>
                            <p className="text-3xl font-bold mt-2">{analytics.users.total}</p>
                            {timeFilter !== 'all' && (
                              <div className="flex items-center gap-1 mt-2">
                                <TrendingUp className="w-4 h-4" />
                                <span className="text-sm font-medium">{analytics.users.growth > 0 ? '+' : ''}{analytics.users.growth}%</span>
                              </div>
                            )}
                          </div>
                          <Users className="w-12 h-12 opacity-80" />
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm opacity-90">Active Users</p>
                            <p className="text-3xl font-bold mt-2">{analytics.users.active}</p>
                            <p className="text-sm opacity-90 mt-1">{analytics.engagement.active_users_percentage}% of total</p>
                          </div>
                          <UserCheck className="w-12 h-12 opacity-80" />
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm opacity-90">Students</p>
                            <p className="text-3xl font-bold mt-2">{analytics.users.students}</p>
                            {timeFilter !== 'all' && (
                              <div className="flex items-center gap-1 mt-2">
                                <TrendingUp className="w-4 h-4" />
                                <span className="text-sm font-medium">{analytics.users.students_growth > 0 ? '+' : ''}{analytics.users.students_growth}%</span>
                              </div>
                            )}
                          </div>
                          <GraduationCap className="w-12 h-12 opacity-80" />
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm opacity-90">Parents</p>
                            <p className="text-3xl font-bold mt-2">{analytics.users.parents}</p>
                            {timeFilter !== 'all' && (
                              <div className="flex items-center gap-1 mt-2">
                                <TrendingUp className="w-4 h-4" />
                                <span className="text-sm font-medium">{analytics.users.parents_growth > 0 ? '+' : ''}{analytics.users.parents_growth}%</span>
                              </div>
                            )}
                          </div>
                          <Users className="w-12 h-12 opacity-80" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Content Statistics */}
                <div>
                  <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <BarChart3 className="w-6 h-6 text-indigo-600" />
                    Content Statistics
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    <Card className="border-l-4 border-l-blue-500 hover:shadow-lg transition-shadow">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                          <div>
                            <BookOpen className="w-8 h-8 mb-2 text-blue-600" />
                            <div className="text-3xl font-bold text-blue-700 mb-1">{analytics.content.careers}</div>
                            <p className="text-sm text-gray-600 font-medium">Careers</p>
                          </div>
                          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center opacity-20">
                            <BookOpen className="w-6 h-6 text-blue-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-l-4 border-l-green-500 hover:shadow-lg transition-shadow">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                          <div>
                            <Target className="w-8 h-8 mb-2 text-green-600" />
                            <div className="text-3xl font-bold text-green-700 mb-1">{analytics.content.assessments || 0}</div>
                            <p className="text-sm text-gray-600 font-medium">Assessments</p>
                          </div>
                          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center opacity-20">
                            <Target className="w-6 h-6 text-green-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-l-4 border-l-yellow-500 hover:shadow-lg transition-shadow">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                          <div>
                            <Award className="w-8 h-8 mb-2 text-yellow-600" />
                            <div className="text-3xl font-bold text-yellow-700 mb-1">{analytics.content.scholarships || 0}</div>
                            <p className="text-sm text-gray-600 font-medium">Scholarships</p>
                          </div>
                          <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center opacity-20">
                            <Award className="w-6 h-6 text-yellow-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-l-4 border-l-purple-500 hover:shadow-lg transition-shadow">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                          <div>
                            <FileText className="w-8 h-8 mb-2 text-purple-600" />
                            <div className="text-3xl font-bold text-purple-700 mb-1">{analytics.content.exams || 0}</div>
                            <p className="text-sm text-gray-600 font-medium">Exams</p>
                          </div>
                          <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center opacity-20">
                            <FileText className="w-6 h-6 text-purple-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-l-4 border-l-cyan-500 hover:shadow-lg transition-shadow">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                          <div>
                            <Video className="w-8 h-8 mb-2 text-cyan-600" />
                            <div className="text-3xl font-bold text-cyan-700 mb-1">{analytics.content.resources || 0}</div>
                            <p className="text-sm text-gray-600 font-medium">Resources</p>
                          </div>
                          <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center opacity-20">
                            <Video className="w-6 h-6 text-cyan-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-l-4 border-l-red-500 hover:shadow-lg transition-shadow">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                          <div>
                            <Calendar className="w-8 h-8 mb-2 text-red-600" />
                            <div className="text-3xl font-bold text-red-700 mb-1">{analytics.content.events || 0}</div>
                            <p className="text-sm text-gray-600 font-medium">Events</p>
                          </div>
                          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center opacity-20">
                            <Calendar className="w-6 h-6 text-red-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-l-4 border-l-teal-500 hover:shadow-lg transition-shadow">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                          <div>
                            <UserCheck className="w-8 h-8 mb-2 text-teal-600" />
                            <div className="text-3xl font-bold text-teal-700 mb-1">{analytics.content.mentors || 0}</div>
                            <p className="text-sm text-gray-600 font-medium">Mentors</p>
                          </div>
                          <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center opacity-20">
                            <UserCheck className="w-6 h-6 text-teal-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-l-4 border-l-indigo-500 hover:shadow-lg transition-shadow">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                          <div>
                            <Building2 className="w-8 h-8 mb-2 text-indigo-600" />
                            <div className="text-3xl font-bold text-indigo-700 mb-1">{analytics.content.colleges || 0}</div>
                            <p className="text-sm text-gray-600 font-medium">Colleges</p>
                          </div>
                          <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center opacity-20">
                            <Building2 className="w-6 h-6 text-indigo-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Revenue & Engagement Metrics */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Revenue Section */}
                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                      <Coins className="w-6 h-6 text-green-600" />
                      Revenue Analytics
                    </h3>
                    <div className="space-y-4">
                      <Card className="bg-gradient-to-br from-emerald-500 to-green-600 text-white">
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm opacity-90">Total Revenue</p>
                              <p className="text-3xl font-bold mt-2">
                                {analytics.revenue.currency_symbol || '$'}{analytics.revenue.total_revenue.toFixed(2)}
                              </p>
                              <p className="text-xs opacity-80 mt-1">{analytics.revenue.currency}</p>
                            </div>
                            <Coins className="w-12 h-12 opacity-80" />
                          </div>
                        </CardContent>
                      </Card>
                      
                      <Card>
                        <CardContent className="p-5">
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Consultation Revenue</span>
                              <span className="font-bold text-green-600">
                                {analytics.revenue.currency_symbol || '$'}{analytics.revenue.consultation_revenue.toFixed(2)}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Completed Bookings</span>
                              <span className="font-bold">{analytics.bookings.completed}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Pending Bookings</span>
                              <Badge variant="outline">{analytics.bookings.pending}</Badge>
                            </div>
                            {timeFilter !== 'all' && (
                              <div className="flex justify-between items-center pt-2 border-t">
                                <span className="text-sm text-gray-600">Bookings Growth</span>
                                <span className="font-bold text-blue-600 flex items-center gap-1">
                                  <TrendingUp className="w-4 h-4" />
                                  {analytics.bookings.growth > 0 ? '+' : ''}{analytics.bookings.growth}%
                                </span>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>

                  {/* Engagement Section */}
                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                      <Target className="w-6 h-6 text-indigo-600" />
                      Engagement Metrics
                    </h3>
                    <div className="space-y-4">
                      <Card className="border-l-4 border-l-indigo-500">
                        <CardContent className="p-5">
                          <div className="space-y-4">
                            <div>
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-sm font-medium">Active Users Rate</span>
                                <span className="font-bold text-indigo-600">{analytics.engagement.active_users_percentage}%</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-indigo-600 h-2 rounded-full transition-all duration-500" 
                                  style={{width: `${analytics.engagement.active_users_percentage}%`}}
                                ></div>
                              </div>
                            </div>
                            
                            <div className="pt-3 border-t space-y-3">
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Assessments per User</span>
                                <span className="font-bold">{analytics.engagement.assessments_per_user}</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Bookings per User</span>
                                <span className="font-bold">{analytics.engagement.bookings_per_user}</span>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      
                      <Card>
                        <CardContent className="p-5">
                          <h4 className="font-semibold mb-3 flex items-center gap-2">
                            <BookOpen className="w-5 h-5 text-blue-600" />
                            Course Enrollments
                          </h4>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Total Enrollments</span>
                              <span className="font-bold">{analytics.enrollments.total}</span>
                            </div>
                            {timeFilter !== 'all' && (
                              <>
                                <div className="flex justify-between items-center">
                                  <span className="text-sm text-gray-600">Current Period</span>
                                  <span className="font-bold text-blue-600">{analytics.enrollments.current_period}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-sm text-gray-600">Growth</span>
                                  <span className="font-bold text-green-600 flex items-center gap-1">
                                    <TrendingUp className="w-4 h-4" />
                                    {analytics.enrollments.growth > 0 ? '+' : ''}{analytics.enrollments.growth}%
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </div>

                {/* Recent Activity */}
                <div>
                  <h3 className="text-xl font-semibold mb-4">Recent Activity</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader className="bg-gray-50">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Recent Registrations
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-4">
                        <div className="space-y-3">
                          {analytics.recent_activity.users.slice(0, 5).map((u, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                              <span className="font-medium">{u.name}</span>
                              <Badge variant="outline">{u.role}</Badge>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="bg-gray-50">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Target className="w-4 h-4" />
                          Recent Assessments
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-4">
                        <div className="space-y-3">
                          {analytics.recent_activity.assessments.slice(0, 5).map((a, idx) => (
                            <div key={idx} className="p-2 bg-gray-50 rounded">
                              <p className="font-medium text-sm">{a.assessment_id}</p>
                              <p className="text-xs text-gray-500">{new Date(a.created_at).toLocaleDateString()}</p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>

              {/* Users Tab */}
              <TabsContent value="users">
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold">User Management</h3>
                    <p className="text-sm text-gray-600">{totalUsers} total users</p>
                  </div>

                  {/* Search and Filters */}
                  <Card className="p-4">
                    <div className="space-y-4">
                      {/* Search */}
                      <div>
                        <Label>Search Users</Label>
                        <Input
                          placeholder="Search by name or email..."
                          value={userSearch}
                          onChange={(e) => {
                            setUserSearch(e.target.value);
                            setUserPage(0);
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              fetchAllData();
                            }
                          }}
                          className="max-w-md"
                        />
                      </div>

                      {/* Filters */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <Label>Role</Label>
                          <select
                            value={userFilters.role}
                            onChange={(e) => {
                              setUserFilters({...userFilters, role: e.target.value});
                              setUserPage(0);
                            }}
                            className="w-full p-2 border rounded"
                          >
                            <option value="all">All Roles</option>
                            <option value="student">Student</option>
                            <option value="school_student">School Student</option>
                            <option value="college_student">College Student</option>
                            <option value="jobseeker">Job Seeker</option>
                            <option value="professional">Professional</option>
                            <option value="parent">Parent</option>
                            <option value="admin">Admin</option>
                          </select>
                        </div>

                        <div>
                          <Label>Verification</Label>
                          <select
                            value={userFilters.is_verified === null ? 'all' : userFilters.is_verified}
                            onChange={(e) => {
                              const val = e.target.value === 'all' ? null : e.target.value === 'true';
                              setUserFilters({...userFilters, is_verified: val});
                              setUserPage(0);
                            }}
                            className="w-full p-2 border rounded"
                          >
                            <option value="all">All Users</option>
                            <option value="true">Verified Only</option>
                            <option value="false">Unverified Only</option>
                          </select>
                        </div>

                        <div>
                          <Label>Status</Label>
                          <select
                            value={userFilters.locked === null ? 'all' : userFilters.locked}
                            onChange={(e) => {
                              const val = e.target.value === 'all' ? null : e.target.value === 'true';
                              setUserFilters({...userFilters, locked: val});
                              setUserPage(0);
                            }}
                            className="w-full p-2 border rounded"
                          >
                            <option value="all">All Status</option>
                            <option value="false">Active Only</option>
                            <option value="true">Locked Only</option>
                          </select>
                        </div>

                        <div>
                          <Label>Plan</Label>
                          <select
                            value={userFilters.subscription_plan}
                            onChange={(e) => {
                              setUserFilters({...userFilters, subscription_plan: e.target.value});
                              setUserPage(0);
                            }}
                            className="w-full p-2 border rounded"
                          >
                            <option value="all">All Plans</option>
                            <option value="free">Free</option>
                            <option value="basic">Basic</option>
                            <option value="premium">Premium</option>
                          </select>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <Button onClick={fetchAllData} size="sm">
                          Apply Filters
                        </Button>
                        <Button
                          onClick={() => {
                            setUserSearch('');
                            setUserFilters({role: 'all', is_verified: null, locked: null, subscription_plan: 'all'});
                            setUserPage(0);
                            fetchAllData();
                          }}
                          size="sm"
                          variant="outline"
                        >
                          Clear Filters
                        </Button>
                      </div>
                    </div>
                  </Card>

                  {/* Mobile: Show as cards, Desktop: Show as table */}
                  <div className="block md:hidden space-y-4">
                    {users.map((u) => (
                      <Card key={u.id} className="p-4">
                        <div className="space-y-3">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-semibold text-base">{u.name}</p>
                              <p className="text-sm text-gray-600">{u.email}</p>
                            </div>
                            <div className="flex flex-col gap-1">
                              {u.is_verified ? (
                                <Badge className="bg-green-100 text-green-700">✅ Verified</Badge>
                              ) : u.role === 'admin' || u.role === 'school_admin' ? (
                                <Badge className="bg-blue-100 text-blue-700">👤 Admin</Badge>
                              ) : (
                                <Badge variant="destructive">❌ Not Verified</Badge>
                              )}
                              {u.locked ? (
                                <Badge variant="destructive">Locked</Badge>
                              ) : (
                                <Badge className="bg-green-100 text-green-700">Active</Badge>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-gray-500">Role:</span>
                              <Badge className="ml-2">{u.role}</Badge>
                            </div>
                            <div>
                              <span className="text-gray-500">Plan:</span>
                              <Badge variant="outline" className="ml-2">{u.subscription_plan || 'Free'}</Badge>
                            </div>
                          </div>
                          <div className="flex gap-2 pt-2 border-t flex-wrap">
                            <Button size="sm" variant="outline" onClick={() => setSelectedUser(u)} className="flex-1">
                              <Eye className="w-3 h-3 mr-1" />
                              View
                            </Button>
                            {!u.is_verified && u.role !== 'admin' && u.role !== 'school_admin' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex-1 text-green-600 border-green-600"
                                onClick={() => handleVerifyUser(u.id)}
                              >
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Verify
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant={u.locked ? "default" : "destructive"}
                              onClick={() => handleToggleUserLock(u.id, u.locked)}
                              className="flex-1"
                            >
                              {u.locked ? "Unlock" : "Lock"}
                            </Button>
                            {u.role !== 'admin' && u.role !== 'school_admin' && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteUser(u.id, u.email)}
                                className="flex-1"
                              >
                                <Trash2 className="w-3 h-3 mr-1" />
                                Delete
                              </Button>
                            )}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                  {/* Desktop Table View */}
                  <div className="hidden md:block rounded-md border overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Plan</TableHead>
                          <TableHead>Verification</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {users.map((u) => (
                          <TableRow key={u.id}>
                            <TableCell className="font-medium">{u.name}</TableCell>
                            <TableCell>{u.email}</TableCell>
                            <TableCell>
                              <Badge>{u.role}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{u.subscription_plan || 'Free'}</Badge>
                            </TableCell>
                            <TableCell>
                              {u.is_verified ? (
                                <Badge className="bg-green-100 text-green-700">✅ Verified</Badge>
                              ) : u.role === 'admin' || u.role === 'school_admin' ? (
                                <Badge className="bg-blue-100 text-blue-700">👤 Admin</Badge>
                              ) : (
                                <Badge variant="destructive">❌ Not Verified</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {u.locked ? (
                                <Badge variant="destructive">Locked</Badge>
                              ) : (
                                <Badge className="bg-green-100 text-green-700">Active</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button size="sm" variant="ghost" onClick={() => setSelectedUser(u)} title="View Profile">
                                  <Eye className="w-3 h-3" />
                                </Button>
                                {!u.is_verified && u.role !== 'admin' && u.role !== 'school_admin' && (
                                  <Button size="sm" variant="outline" className="text-green-600 border-green-600" onClick={() => handleVerifyUser(u.id)} title="Verify User">
                                    <CheckCircle className="w-3 h-3" />
                                  </Button>
                                )}
                                <Button size="sm" variant="outline" onClick={() => {setSelectedUser(u); setShowWalletModal(true);}} title="Topup Wallet">
                                  <DollarSign className="w-3 h-3" />
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => {setSelectedUser(u); setShowPlanModal(true);}} title="Upgrade Plan">
                                  <TrendingUp className="w-3 h-3" />
                                </Button>
                                {u.locked ? (
                                  <Button size="sm" variant="outline" onClick={() => unlockUser(u.id)} title="Unlock User">
                                    <Unlock className="w-3 h-3" />
                                  </Button>
                                ) : (
                                  <Button size="sm" variant="outline" onClick={() => lockUser(u.id)} title="Lock User">
                                    <Lock className="w-3 h-3" />
                                  </Button>
                                )}
                                {u.role !== 'admin' && u.role !== 'school_admin' && (
                                  <Button size="sm" variant="destructive" onClick={() => handleDeleteUser(u.id, u.email)} title="Delete User">
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  <Card className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-600">
                        Showing {userPage * userLimit + 1} - {Math.min((userPage + 1) * userLimit, totalUsers)} of {totalUsers} users
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => {
                            setUserPage(Math.max(0, userPage - 1));
                            setTimeout(fetchAllData, 100);
                          }}
                          disabled={userPage === 0}
                          size="sm"
                          variant="outline"
                        >
                          Previous
                        </Button>
                        <div className="flex items-center gap-2 px-3">
                          <span className="text-sm">Page {userPage + 1} of {Math.ceil(totalUsers / userLimit)}</span>
                        </div>
                        <Button
                          onClick={() => {
                            setUserPage(userPage + 1);
                            setTimeout(fetchAllData, 100);
                          }}
                          disabled={(userPage + 1) * userLimit >= totalUsers}
                          size="sm"
                          variant="outline"
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </Card>
                </div>
              </TabsContent>

              {/* Security Monitoring Tab */}
              <TabsContent value="security-monitoring" data-testid="security-monitoring-content">
                <UserActivityMonitor token={token} />
              </TabsContent>

              {/* Assessments Tab */}
              <TabsContent value="assessments">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-semibold">Assessment Management</h3>
                    <Button onClick={() => window.location.href = '/admin/assessments'}>
                      <Target className="w-4 h-4 mr-2" />
                      Manage Assessments
                    </Button>
                  </div>
                  <Card>
                    <CardContent className="py-6">
                      <div className="text-center">
                        <Target className="w-12 h-12 mx-auto mb-3 text-blue-600" />
                        <h4 className="text-lg font-semibold mb-2">Assessment Test Management</h4>
                        <p className="text-gray-600 mb-4">
                          Create, edit, and manage grade-wise assessment tests for students.
                          Click the button above to access the full assessment management interface.
                        </p>
                        <Button onClick={() => window.location.href = '/admin/assessments'}>
                          Go to Assessment Management
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Pricing Tab */}
              <TabsContent value="pricing">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-semibold">Pricing Management</h3>
                    <Button onClick={() => window.location.href = '/admin/pricing'}>
                      <DollarSign className="w-4 h-4 mr-2" />
                      Manage Pricing
                    </Button>
                  </div>
                  <Card>
                    <CardContent className="py-6">
                      <div className="text-center">
                        <DollarSign className="w-12 h-12 mx-auto mb-3 text-green-600" />
                        <h4 className="text-lg font-semibold mb-2">Subscription Plans & Consultation Rates</h4>
                        <p className="text-gray-600 mb-4">
                          Create and manage subscription plans for users and set consultation rates for different services.
                          Control pricing, features, and billing cycles from the comprehensive pricing management interface.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                          <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
                            <CardContent className="p-6">
                              <CreditCard className="w-10 h-10 mb-3 text-blue-600 mx-auto" />
                              <h5 className="font-semibold text-blue-900 mb-2">Subscription Plans</h5>
                              <p className="text-sm text-blue-700">
                                Manage subscription tiers, pricing, and features for users
                              </p>
                            </CardContent>
                          </Card>
                          <Card className="bg-gradient-to-br from-green-50 to-green-100">
                            <CardContent className="p-6">
                              <DollarSign className="w-10 h-10 mb-3 text-green-600 mx-auto" />
                              <h5 className="font-semibold text-green-900 mb-2">Consultation Rates</h5>
                              <p className="text-sm text-green-700">
                                Set pricing for one-on-one, group, and emergency consultations
                              </p>
                            </CardContent>
                          </Card>
                        </div>
                        <Button onClick={() => window.location.href = '/admin/pricing'} className="mt-6">
                          Go to Pricing Management
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Bookings Tab */}
              <TabsContent value="bookings">
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold">Consultation Bookings</h3>
                  
                  {/* Mobile Card View */}
                  <div className="md:hidden space-y-3">
                    {bookings.map((b) => (
                      <Card key={b.id} className="p-4">
                        <div className="space-y-3">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-semibold">{b.user_name || (b.user_id ? b.user_id.slice(0, 12) + '...' : 'N/A')}</p>
                              <p className="text-sm text-gray-600">Counselor: {b.counselor_name || 'Not assigned'}</p>
                            </div>
                            <Badge className={b.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}>
                              {b.status}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-gray-500">Date:</span>
                              <p className="font-medium">
                                {b.scheduled_date 
                                  ? new Date(b.scheduled_date).toLocaleDateString() 
                                  : (b.date ? new Date(b.date).toLocaleDateString() : 'N/A')}
                              </p>
                            </div>
                            <div>
                              <span className="text-gray-500">Time:</span>
                              <p className="font-medium">{convertTo12Hour(b.scheduled_time || b.time)}</p>
                            </div>
                            <div className="col-span-2">
                              <span className="text-gray-500">Topic:</span>
                              <Badge variant="outline" className="ml-2">{b.topic || b.type || 'Consultation'}</Badge>
                            </div>
                          </div>
                          <div className="flex gap-2 pt-2 border-t">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => window.location.href = `/admin/consultations`}
                              className="flex-1"
                            >
                              <Edit className="w-3 h-3 mr-1" />
                              Manage
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => deleteEntity('bookings', b.id)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                  
                  {/* Desktop Table View */}
                  <div className="hidden md:block rounded-md border overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>User Name</TableHead>
                          <TableHead>Counselor</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Time</TableHead>
                          <TableHead>Topic/Type</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bookings.map((b) => (
                          <TableRow key={b.id}>
                            <TableCell className="font-mono text-xs">
                              {b.user_name || (b.user_id ? b.user_id.slice(0, 8) + '...' : 'N/A')}
                            </TableCell>
                            <TableCell>{b.counselor_name || 'Not assigned'}</TableCell>
                            <TableCell>
                              {b.scheduled_date 
                                ? new Date(b.scheduled_date).toLocaleDateString() 
                                : (b.date ? new Date(b.date).toLocaleDateString() : 'N/A')}
                            </TableCell>
                            <TableCell>{convertTo12Hour(b.scheduled_time || b.time)}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{b.topic || b.type || 'Consultation'}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className={b.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}>
                                {b.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => window.location.href = `/admin/consultations`}
                                  title="Manage in Consultations"
                                >
                                  <Edit className="w-3 h-3" />
                                </Button>
                                <Button size="sm" variant="destructive" onClick={() => deleteEntity('bookings', b.id)}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </TabsContent>

              {/* Scholarships Tab */}
              <TabsContent value="scholarships">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-semibold">Scholarships Management</h3>
                    <Button onClick={() => openModal('scholarship')}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Scholarship
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {scholarships.map((s) => (
                      <Card key={s.id}>
                        <CardHeader>
                          <CardTitle className="text-base">{s.name}</CardTitle>
                          <CardDescription>{s.organization}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm mb-2"><strong>Amount:</strong> {s.amount}</p>
                          <p className="text-sm mb-2"><strong>Deadline:</strong> {new Date(s.deadline).toLocaleDateString()}</p>
                          <p className="text-sm mb-3"><strong>Grades:</strong> {s.min_grade}-{s.max_grade}</p>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => openModal('scholarship', s)}>
                              <Edit className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => deleteEntity('scholarships', s.id)}>
                              <Trash2 className="w-3 h-3 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Exams Tab */}
              <TabsContent value="exams">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-semibold">Exams Management</h3>
                    <Button onClick={() => openModal('exam')}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Exam
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {exams.map((e) => (
                      <Card key={e.id}>
                        <CardHeader>
                          <CardTitle className="text-base">{e.name}</CardTitle>
                          <CardDescription>{e.full_name}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm mb-2"><strong>Exam Date:</strong> {new Date(e.exam_date).toLocaleDateString()}</p>
                          <p className="text-sm mb-2"><strong>Registration Deadline:</strong> {new Date(e.registration_deadline).toLocaleDateString()}</p>
                          <p className="text-sm mb-3"><strong>Subjects:</strong> {e.subjects?.join(", ")}</p>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => openModal('exam', e)}>
                              <Edit className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => deleteEntity('exams', e.id)}>
                              <Trash2 className="w-3 h-3 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="resources">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-semibold">Resources Management</h3>
                    <Button onClick={() => openModal('resource')}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Resource
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {resources.map((r) => (
                      <Card key={r.id}>
                        <CardHeader>
                          <CardTitle className="text-base">{r.title}</CardTitle>
                          <Badge variant="outline">{r.type}</Badge>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm mb-2"><strong>Category:</strong> {r.category}</p>
                          <p className="text-sm mb-2"><strong>Duration:</strong> {r.duration || "N/A"}</p>
                          <p className="text-sm mb-3 line-clamp-2">{r.description}</p>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => openModal('resource', r)}>
                              <Edit className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => deleteEntity('resources', r.id)}>
                              <Trash2 className="w-3 h-3 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="events">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-semibold">Events Management</h3>
                    <Button onClick={() => openModal('event')}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Event
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {events.map((ev) => (
                      <Card key={ev.id}>
                        <CardHeader>
                          <CardTitle className="text-base">{ev.title}</CardTitle>
                          <Badge variant="outline">{ev.type}</Badge>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm mb-2"><strong>Date:</strong> {new Date(ev.date).toLocaleDateString()}</p>
                          <p className="text-sm mb-2"><strong>Time:</strong> {ev.time}</p>
                          <p className="text-sm mb-3"><strong>Speaker:</strong> {ev.speaker}</p>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => openModal('event', ev)}>
                              <Edit className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => deleteEntity('events', ev.id)}>
                              <Trash2 className="w-3 h-3 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="mentors">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-semibold">Mentors Management</h3>
                    <Button onClick={() => openModal('mentor')}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Mentor
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {mentors.map((m) => (
                      <Card key={m.id}>
                        <CardHeader>
                          <div className="flex items-start gap-4">
                            {m.image && (
                              <img 
                                src={m.image} 
                                alt={m.name} 
                                className="w-16 h-16 rounded-full object-cover border-2 border-blue-200"
                              />
                            )}
                            {!m.image && (
                              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold text-xl">
                                {m.name && m.name.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div className="flex-1">
                              <CardTitle className="text-base">{m.name}</CardTitle>
                              <CardDescription>{m.profession} at {m.company}</CardDescription>
                              {m.designation && (
                                <p className="text-xs text-gray-500 mt-1">{m.designation}</p>
                              )}
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm mb-2"><strong>Expertise:</strong> {m.expertise?.join(", ")}</p>
                          {m.education && (
                            <p className="text-sm mb-2"><strong>Education:</strong> {m.education}</p>
                          )}
                          {m.experience_years > 0 && (
                            <p className="text-sm mb-2"><strong>Experience:</strong> {m.experience_years} years</p>
                          )}
                          <p className="text-sm mb-3 line-clamp-2">{m.bio}</p>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => openModal('mentor', m)}>
                              <Edit className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => deleteEntity('mentors', m.id)}>
                              <Trash2 className="w-3 h-3 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="quizzes">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-semibold">Quizzes Management</h3>
                    <Button onClick={() => openModal('quiz')}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Quiz
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {quizzes.map((q) => (
                      <Card key={q.id}>
                        <CardHeader>
                          <CardTitle className="text-base">{q.title}</CardTitle>
                          <div className="flex gap-2">
                            <Badge variant="outline">{q.category}</Badge>
                            <Badge>{q.difficulty}</Badge>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm mb-2"><strong>Points:</strong> {q.points}</p>
                          <p className="text-sm mb-3"><strong>Time Limit:</strong> {q.time_limit} minutes</p>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => openModal('quiz', q)}>
                              <Edit className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => deleteEntity('quizzes', q.id)}>
                              <Trash2 className="w-3 h-3 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="colleges">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-semibold">Colleges Management</h3>
                    <Button onClick={() => openModal('college')}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add College
                    </Button>
                  </div>
                  {colleges && colleges.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {colleges.map((c) => (
                        <Card key={c.id}>
                          <CardHeader>
                            <CardTitle className="text-base">{c.name}</CardTitle>
                            <CardDescription>{c.location}</CardDescription>
                          </CardHeader>
                          <CardContent>
                            <p className="text-sm mb-2"><strong>NIRF Ranking:</strong> {c.ranking?.nirf || "N/A"}</p>
                            <p className="text-sm mb-2"><strong>Type:</strong> {c.type || "N/A"}</p>
                            <p className="text-sm mb-2"><strong>Courses:</strong> {c.courses_offered?.slice(0, 2).join(", ") || "N/A"}</p>
                            <div className="flex gap-2 mt-3">
                              <Button size="sm" variant="outline" onClick={() => openModal('college', c)}>
                                <Edit className="w-3 h-3 mr-1" />
                                Edit
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => deleteEntity('colleges', c.id)}>
                                <Trash2 className="w-3 h-3 mr-1" />
                                Delete
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <Card className="p-8 text-center">
                      <Building2 className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                      <h3 className="text-lg font-semibold mb-2">No Colleges Added Yet</h3>
                      <p className="text-muted-foreground mb-4">Get started by adding your first college</p>
                      <Button onClick={() => openModal('college')}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Your First College
                      </Button>
                    </Card>
                  )}
                </div>
              </TabsContent>

              {/* Careers Tab */}
              <TabsContent value="careers">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-semibold">Career Management</h3>
                    <Button onClick={() => openModal('career')}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Career
                    </Button>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {careers.map((career) => (
                      <Card key={career.id}>
                        <CardHeader>
                          <CardTitle className="text-lg">{career.title}</CardTitle>
                          <Badge variant="outline">{career.category}</Badge>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          <p className="text-sm text-gray-600 line-clamp-2">{career.description}</p>
                          <div className="space-y-1 text-xs">
                            {career.salary_range_india && (
                              <p><strong>Salary (India):</strong> <span className="text-green-600">{career.salary_range_india}</span></p>
                            )}
                            {career.salary_range_global && (
                              <p><strong>Salary (Global):</strong> <span className="text-blue-600">{career.salary_range_global}</span></p>
                            )}
                            <p><strong>Timeline:</strong> {career.timeline_to_enter || 'N/A'}</p>
                            <p><strong>Work-Life:</strong> {career.work_life_balance || 'N/A'}</p>
                            <p><strong>Growth:</strong> {career.growth_potential || 'N/A'}</p>
                          </div>
                          <div className="flex gap-2 mt-3">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openModal('career', career)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => deleteEntity('careers', career.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Opportunities Tab */}
              <TabsContent value="opportunities">
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-2xl font-bold">Internship & Job Board</h3>
                      <p className="text-gray-600">Manage internships and job opportunities</p>
                    </div>
                    <Button onClick={() => openModal('opportunity')}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Opportunity
                    </Button>
                  </div>

                  {/* Opportunities List */}
                  <div className="grid grid-cols-1 gap-4">
                    {opportunities && Array.isArray(opportunities) && opportunities.length > 0 ? (
                      opportunities.map((opp) => (
                      <Card key={opp.id} className="border-l-4" style={{borderLeftColor: opp.type === 'internship' ? '#10b981' : '#3b82f6'}}>
                        <CardHeader>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <CardTitle className="text-lg">{opp.title}</CardTitle>
                                <Badge variant={opp.type === 'internship' ? 'default' : 'secondary'}>
                                  {opp.type}
                                </Badge>
                                <Badge variant={opp.status === 'active' ? 'default' : 'outline'}>
                                  {opp.status}
                                </Badge>
                              </div>
                              <CardDescription>
                                <div className="flex flex-wrap gap-3 text-sm">
                                  <span>🏢 {opp.company_name}</span>
                                  <span>📍 {opp.location}</span>
                                  <span>💼 {opp.work_mode}</span>
                                  <span>🎯 {opp.field}</span>
                                </div>
                              </CardDescription>
                            </div>
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" onClick={() => {
                                openModal('opportunity', opp);
                              }}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => handleDelete('opportunity', opp.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                            <div>
                              <p className="text-gray-600">Experience</p>
                              <p className="font-semibold">{opp.experience_level}</p>
                            </div>
                            {opp.salary && (
                              <div>
                                <p className="text-gray-600">Salary</p>
                                <p className="font-semibold">{opp.salary}</p>
                              </div>
                            )}
                            {opp.stipend && (
                              <div>
                                <p className="text-gray-600">Stipend</p>
                                <p className="font-semibold">{opp.stipend}</p>
                              </div>
                            )}
                            {opp.duration && (
                              <div>
                                <p className="text-gray-600">Duration</p>
                                <p className="font-semibold">{opp.duration}</p>
                              </div>
                            )}
                            <div>
                              <p className="text-gray-600">Deadline</p>
                              <p className="font-semibold">{new Date(opp.application_deadline).toLocaleDateString()}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                    ) : (
                      <div className="text-center py-12">
                        <p className="text-gray-500 mb-4">No opportunities found</p>
                        <Button onClick={() => openModal('opportunity')}>
                          <Plus className="w-4 h-4 mr-2" />
                          Add First Opportunity
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Support Tickets Tab */}
              <TabsContent value="support">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Tickets List */}
                  <div className="lg:col-span-1">
                    <Card className="border-none shadow-md">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-lg">All Tickets</CardTitle>
                          <Badge variant="outline">{supportTickets.length}</Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="p-0">
                        <div className="max-h-[600px] overflow-y-auto">
                          {supportTickets.map((ticket) => (
                            <div
                              key={ticket.id}
                              onClick={() => {
                                setSelectedTicket(ticket);
                                fetchTicketMessages(ticket.id);
                              }}
                              className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                                selectedTicket?.id === ticket.id ? "bg-primary/5 border-l-4 border-l-primary" : ""
                              }`}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <h3 className="font-semibold text-sm line-clamp-1">{ticket.subject}</h3>
                                <Badge className={
                                  ticket.status === "resolved" ? "bg-green-100 text-green-700" :
                                  ticket.status === "escalated" ? "bg-orange-100 text-orange-700" :
                                  ticket.status === "in_progress" ? "bg-yellow-100 text-yellow-700" :
                                  "bg-blue-100 text-blue-700"
                                }>
                                  {ticket.status}
                                </Badge>
                              </div>
                              <div className="text-xs text-gray-600 mb-2">
                                <p>From: {ticket.user_name}</p>
                                <p>{new Date(ticket.created_at).toLocaleDateString()}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">{ticket.category}</Badge>
                                <Badge variant="outline" className="text-xs">Level {ticket.level}</Badge>
                                {ticket.priority === "high" && (
                                  <Badge className="text-xs bg-red-100 text-red-700">High Priority</Badge>
                                )}
                              </div>
                            </div>
                          ))}
                          {supportTickets.length === 0 && (
                            <div className="p-6 text-center text-gray-500">
                              <p>No tickets yet</p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Chat Area */}
                  <div className="lg:col-span-2">
                    {selectedTicket ? (
                      <Card className="border-none shadow-md h-[700px] flex flex-col">
                        <CardHeader className="border-b">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <CardTitle>{selectedTicket.subject}</CardTitle>
                              <div className="flex items-center gap-2 mt-2">
                                <Badge className={
                                  selectedTicket.status === "resolved" ? "bg-green-100 text-green-700" :
                                  selectedTicket.status === "escalated" ? "bg-orange-100 text-orange-700" :
                                  selectedTicket.status === "in_progress" ? "bg-yellow-100 text-yellow-700" :
                                  "bg-blue-100 text-blue-700"
                                }>
                                  {selectedTicket.status}
                                </Badge>
                                <Badge variant="outline">Level {selectedTicket.level}</Badge>
                                <Badge variant="outline">{selectedTicket.category}</Badge>
                              </div>
                              <div className="text-sm text-gray-600 mt-2">
                                <p>User: {selectedTicket.user_name} ({selectedTicket.user_email})</p>
                                <p>Created: {new Date(selectedTicket.created_at).toLocaleString()}</p>
                              </div>
                            </div>
                            <div className="flex flex-col gap-2">
                              <Select
                                value={selectedTicket.status}
                                onValueChange={(value) => updateTicketStatus(selectedTicket.id, value)}
                              >
                                <SelectTrigger className="w-40">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="open">Open</SelectItem>
                                  <SelectItem value="in_progress">In Progress</SelectItem>
                                  <SelectItem value="resolved">Resolved</SelectItem>
                                  <SelectItem value="closed">Closed</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </CardHeader>
                        
                        {/* Messages */}
                        <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
                          {ticketMessages.map((msg) => (
                            <div
                              key={msg.id}
                              className={`flex gap-3 ${msg.sender_type === "user" ? "flex-row" : "flex-row-reverse"}`}
                            >
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                msg.sender_type === "user" ? "bg-blue-100 text-blue-600" :
                                msg.sender_type === "ai" ? "bg-purple-100 text-purple-600" :
                                "bg-green-100 text-green-600"
                              }`}>
                                {msg.sender_type === "user" ? "U" : msg.sender_type === "ai" ? "AI" : "A"}
                              </div>
                              <div className={`flex-1 max-w-[80%] ${msg.sender_type !== "user" ? "text-right" : ""}`}>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-sm font-medium">{msg.sender_name}</span>
                                  <span className="text-xs text-gray-500">
                                    {new Date(msg.timestamp).toLocaleTimeString()}
                                  </span>
                                </div>
                                <div className={`p-3 rounded-lg ${
                                  msg.sender_type === "user" ? "bg-blue-50 text-gray-800" :
                                  msg.sender_type === "ai" ? "bg-purple-50 text-gray-800" :
                                  "bg-green-500 text-white"
                                }`}>
                                  <p className="whitespace-pre-wrap">{msg.message}</p>
                                  {msg.confidence_score && (
                                    <p className="text-xs mt-1 opacity-70">
                                      Confidence: {(msg.confidence_score * 100).toFixed(0)}%
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </CardContent>
                        
                        {/* Admin Response Input */}
                        {selectedTicket.status !== "closed" && (
                          <div className="border-t p-4">
                            <div className="flex gap-2">
                              <Textarea
                                value={adminMessage}
                                onChange={(e) => setAdminMessage(e.target.value)}
                                placeholder="Type your response..."
                                rows={2}
                                className="flex-1"
                              />
                              <Button onClick={() => sendAdminResponse(selectedTicket.id)}>
                                <Send className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </Card>
                    ) : (
                      <Card className="border-none shadow-md h-[700px] flex items-center justify-center">
                        <div className="text-center text-gray-500">
                          <MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                          <p className="text-lg font-medium">Select a ticket to view details</p>
                        </div>
                      </Card>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* FAQs Tab */}
              <TabsContent value="faqs" className="space-y-6">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-bold">FAQ Management</h2>
                    <p className="text-gray-600">Manage Frequently Asked Questions for AI Chat Support</p>
                  </div>
                  <Button onClick={() => {
                    setFaqFormData({
                      question: "",
                      answer: "",
                      category: "Account",
                      status: true
                    });
                    setEditingFaq(null);
                    setIsFaqDialogOpen(true);
                  }}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add FAQ
                  </Button>
                </div>

                {/* Filters */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <Label>Search</Label>
                    <Input
                      placeholder="Search questions or answers..."
                      value={faqSearch}
                      onChange={(e) => setFaqSearch(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Category Filter</Label>
                    <Select value={faqCategoryFilter} onValueChange={setFaqCategoryFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="All Categories" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        <SelectItem value="Account">Account</SelectItem>
                        <SelectItem value="Payment">Payment</SelectItem>
                        <SelectItem value="Technical">Technical</SelectItem>
                        <SelectItem value="Usage">Usage</SelectItem>
                        <SelectItem value="Support">Support</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Status Filter</Label>
                    <Select value={faqStatusFilter} onValueChange={setFaqStatusFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="All Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Initialize Button */}
                {faqs.length === 0 && (
                  <Card className="p-6 text-center">
                    <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <h3 className="text-lg font-semibold mb-2">No FAQs Found</h3>
                    <p className="text-gray-600 mb-4">Initialize default FAQs or create your first FAQ</p>
                    <Button onClick={handleInitializeFaqs} variant="outline">
                      <Plus className="w-4 h-4 mr-2" />
                      Initialize Default FAQs
                    </Button>
                  </Card>
                )}

                {/* FAQ Table */}
                {faqs.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[30%]">Question</TableHead>
                          <TableHead className="w-[35%]">Answer</TableHead>
                          <TableHead className="w-[15%]">Category</TableHead>
                          <TableHead className="w-[10%]">Status</TableHead>
                          <TableHead className="w-[10%]">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredFaqs.map((faq) => (
                          <TableRow key={faq.id}>
                            <TableCell className="font-medium">{faq.question}</TableCell>
                            <TableCell className="text-sm text-gray-600">
                              {faq.answer.length > 100 ? faq.answer.substring(0, 100) + "..." : faq.answer}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{faq.category}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant={faq.status ? "default" : "secondary"}>
                                {faq.status ? "Active" : "Inactive"}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleEditFaq(faq)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleToggleFaqStatus(faq)}
                                >
                                  {faq.status ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleDeleteFaq(faq.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* FAQ Form Dialog */}
                <Dialog open={isFaqDialogOpen} onOpenChange={setIsFaqDialogOpen}>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>{editingFaq ? "Edit FAQ" : "Add New FAQ"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Question *</Label>
                        <Input
                          placeholder="Enter FAQ question"
                          value={faqFormData.question}
                          onChange={(e) => setFaqFormData({...faqFormData, question: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Answer *</Label>
                        <Textarea
                          placeholder="Enter FAQ answer"
                          value={faqFormData.answer}
                          onChange={(e) => setFaqFormData({...faqFormData, answer: e.target.value})}
                          rows={5}
                        />
                      </div>
                      <div>
                        <Label>Category *</Label>
                        <Select
                          value={faqFormData.category}
                          onValueChange={(value) => setFaqFormData({...faqFormData, category: value})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Account">Account</SelectItem>
                            <SelectItem value="Payment">Payment</SelectItem>
                            <SelectItem value="Technical">Technical</SelectItem>
                            <SelectItem value="Usage">Usage</SelectItem>
                            <SelectItem value="Support">Support</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={faqFormData.status}
                          onChange={(e) => setFaqFormData({...faqFormData, status: e.target.checked})}
                          className="w-4 h-4"
                        />
                        <Label>Active</Label>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setIsFaqDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleSaveFaq}>
                        {editingFaq ? "Update FAQ" : "Create FAQ"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </TabsContent>

              {/* Courses Tab */}
              <TabsContent value="courses" className="space-y-6">
                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <Card className="bg-gradient-to-br from-indigo-50 to-blue-50 border-2 border-indigo-200 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/admin/courses-management')}>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-indigo-900">
                        <BookOpen className="h-6 w-6" />
                        Courses Management
                      </CardTitle>
                      <CardDescription>
                        Manage courses with lessons, pricing, and discounts
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button className="w-full">
                        Open Courses Management
                      </Button>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/admin/certificate-designer')}>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-purple-900">
                        <Award className="h-6 w-6" />
                        Certificate Designer
                      </CardTitle>
                      <CardDescription>
                        Customize course completion certificates
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button className="w-full">
                        Design Certificates
                      </Button>
                    </CardContent>
                  </Card>
                  <Card
                    className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => navigate('/admin/exam-prep/pyq')}
                    data-testid="admin-pyq-card"
                  >
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-emerald-900">
                        <BookOpen className="h-6 w-6" />
                        Exam PYQ Management
                      </CardTitle>
                      <CardDescription>
                        Seed Previous Year Questions to boost AI accuracy
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button className="w-full" data-testid="go-to-pyq-btn">
                        Manage PYQs
                      </Button>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle>All Courses</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {courses.map((course) => (
                        <Card key={course.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                          <div className="relative h-40 bg-gradient-to-br from-gray-100 to-gray-200">
                            {(course.image || course.image_url) ? (
                              <img 
                                src={
                                  (course.image || course.image_url).startsWith('data:') 
                                    ? (course.image || course.image_url)
                                    : `${course.image || course.image_url}?w=400&h=300&fit=crop&auto=format&q=60`
                                }
                                alt={course.title}
                                loading="lazy"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-100 to-purple-100"><span class="text-4xl">📚</span></div>';
                                }}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <span className="text-4xl">📚</span>
                              </div>
                            )}
                            {course.is_popular && (
                              <span className="absolute top-2 right-2 bg-yellow-400 text-xs font-bold px-2 py-1 rounded shadow-md">
                                ⭐ POPULAR
                              </span>
                            )}
                            {!course.is_active && (
                              <span className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded shadow-md">
                                INACTIVE
                              </span>
                            )}
                          </div>
                          <div className="p-4">
                            <div className="mb-2">
                              <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded font-semibold">
                                {course.category || 'General'}
                              </span>
                            </div>
                            <h3 className="font-bold text-lg mb-2 line-clamp-1">{course.title}</h3>
                            <div className="flex justify-between text-sm text-gray-600 mb-2">
                              <span>⏱️ {course.duration}</span>
                              <span className="font-bold text-indigo-600">₹{course.price || 'Free'}</span>
                            </div>
                            <p className="text-sm text-gray-600 mb-3 line-clamp-2">{course.description}</p>
                            <div className="space-y-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => openLessonManager(course)}
                                className="w-full"
                              >
                                <Video className="w-4 h-4 mr-1" />
                                Manage Lessons ({course.lessons?.length || 0})
                              </Button>
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => openCourseDialog(course)}
                                  className="flex-1"
                                >
                                  <Edit className="w-4 h-4 mr-1" />
                                  Edit
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="destructive"
                                  onClick={() => handleDeleteCourse(course.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Course Dialog */}
                <Dialog open={isCourseDialogOpen} onOpenChange={setIsCourseDialogOpen}>
                  <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingCourse ? 'Edit Course' : 'Add New Course'}</DialogTitle>
                      <div className="flex gap-2 mt-2">
                        <Button
                          size="sm"
                          variant={showCoursePreview ? "default" : "outline"}
                          onClick={() => setShowCoursePreview(!showCoursePreview)}
                        >
                          {showCoursePreview ? 'Hide Preview' : 'Show Preview'}
                        </Button>
                      </div>
                    </DialogHeader>

                    {!showCoursePreview ? (
                      <div className="space-y-4">
                        {/* Course Title */}
                        <div>
                          <Label>Course Title <span className="text-red-500">*</span></Label>
                          <Input
                            value={courseFormData.title}
                            onChange={(e) => {
                              setCourseFormData({...courseFormData, title: e.target.value});
                              setCourseFormErrors({...courseFormErrors, title: ''});
                            }}
                            placeholder="e.g., Python for Beginners"
                            className={courseFormErrors.title ? 'border-red-500' : ''}
                          />
                          {courseFormErrors.title && (
                            <p className="text-red-500 text-xs mt-1">{courseFormErrors.title}</p>
                          )}
                        </div>

                        {/* Category and Level */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Category <span className="text-red-500">*</span></Label>
                            <Input
                              value={courseFormData.category}
                              onChange={(e) => {
                                setCourseFormData({...courseFormData, category: e.target.value});
                                setCourseFormErrors({...courseFormErrors, category: ''});
                              }}
                              placeholder="e.g., Technology"
                              className={courseFormErrors.category ? 'border-red-500' : ''}
                            />
                            {courseFormErrors.category && (
                              <p className="text-red-500 text-xs mt-1">{courseFormErrors.category}</p>
                            )}
                          </div>
                          <div>
                            <Label>Level <span className="text-red-500">*</span></Label>
                            <Input
                              value={courseFormData.level}
                              onChange={(e) => {
                                setCourseFormData({...courseFormData, level: e.target.value});
                                setCourseFormErrors({...courseFormErrors, level: ''});
                              }}
                              placeholder="e.g., Beginner"
                              className={courseFormErrors.level ? 'border-red-500' : ''}
                            />
                            {courseFormErrors.level && (
                              <p className="text-red-500 text-xs mt-1">{courseFormErrors.level}</p>
                            )}
                          </div>
                        </div>
                        
                        {/* Duration and Price */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Duration <span className="text-red-500">*</span></Label>
                            <Input
                              value={courseFormData.duration}
                              onChange={(e) => {
                                setCourseFormData({...courseFormData, duration: e.target.value});
                                setCourseFormErrors({...courseFormErrors, duration: ''});
                              }}
                              placeholder="e.g., 6 Weeks"
                              className={courseFormErrors.duration ? 'border-red-500' : ''}
                            />
                            {courseFormErrors.duration && (
                              <p className="text-red-500 text-xs mt-1">{courseFormErrors.duration}</p>
                            )}
                          </div>
                          <div>
                            <Label>Price (₹) <span className="text-red-500">*</span></Label>
                            <Input
                              type="number"
                              value={courseFormData.price}
                              onChange={(e) => {
                                setCourseFormData({...courseFormData, price: parseInt(e.target.value) || 0});
                                setCourseFormErrors({...courseFormErrors, price: ''});
                              }}
                              placeholder="e.g., 1499"
                              className={courseFormErrors.price ? 'border-red-500' : ''}
                            />
                            {courseFormErrors.price && (
                              <p className="text-red-500 text-xs mt-1">{courseFormErrors.price}</p>
                            )}
                          </div>
                        </div>

                        {/* Description */}
                        <div>
                          <Label>Description <span className="text-red-500">*</span></Label>
                          <Textarea
                            rows={3}
                            value={courseFormData.description}
                            onChange={(e) => {
                              setCourseFormData({...courseFormData, description: e.target.value});
                              setCourseFormErrors({...courseFormErrors, description: ''});
                            }}
                            placeholder="Brief course description..."
                            className={courseFormErrors.description ? 'border-red-500' : ''}
                          />
                          {courseFormErrors.description && (
                            <p className="text-red-500 text-xs mt-1">{courseFormErrors.description}</p>
                          )}
                        </div>

                        {/* Image Upload */}
                        <div>
                          <Label>Course Image <span className="text-red-500">*</span></Label>
                          <div className="mt-2">
                            {courseImagePreview || courseFormData.image ? (
                              <div className="relative inline-block w-full">
                                <img
                                  src={courseImagePreview || courseFormData.image}
                                  alt="Course preview"
                                  className="w-full h-48 object-cover rounded-lg border-2 border-gray-300"
                                />
                                <button
                                  type="button"
                                  onClick={removeCourseImage}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            ) : (
                              <div>
                                <input
                                  type="file"
                                  id="course-image"
                                  accept="image/*"
                                  onChange={handleCourseImageUpload}
                                  className="hidden"
                                />
                                <label htmlFor="course-image">
                                  <div className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 ${courseFormErrors.image ? 'border-red-500' : 'border-gray-300'}`}>
                                    <Upload className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                                    <p className="text-sm text-gray-600">Click to upload course image</p>
                                    <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 5MB</p>
                                  </div>
                                </label>
                              </div>
                            )}
                            {courseFormErrors.image && (
                              <p className="text-red-500 text-xs mt-1">{courseFormErrors.image}</p>
                            )}
                          </div>
                        </div>

                        {/* Salary Information */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Avg Salary (India)</Label>
                            <Input
                              value={courseFormData.average_salary_india}
                              onChange={(e) => setCourseFormData({...courseFormData, average_salary_india: e.target.value})}
                              placeholder="e.g., ₹6-20 LPA"
                            />
                          </div>
                          <div>
                            <Label>Avg Salary (Global)</Label>
                            <Input
                              value={courseFormData.average_salary_global}
                              onChange={(e) => setCourseFormData({...courseFormData, average_salary_global: e.target.value})}
                              placeholder="e.g., $60-120K/year"
                            />
                          </div>
                        </div>

                        {/* Prerequisites */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <Label>Prerequisites</Label>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => addArrayItem('prerequisites')}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Add Prerequisite
                            </Button>
                          </div>
                          <div className="space-y-2">
                            {courseFormData.prerequisites.map((item, index) => (
                              <div key={index} className="flex gap-2">
                                <Input
                                  value={item}
                                  onChange={(e) => updateArrayItem('prerequisites', index, e.target.value)}
                                  placeholder="e.g., Basic Programming"
                                  className="flex-1"
                                />
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => removeArrayItem('prerequisites', index)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                            {courseFormData.prerequisites.length === 0 && (
                              <p className="text-sm text-gray-500 italic">No prerequisites added yet</p>
                            )}
                          </div>
                        </div>

                        {/* Skills Gained */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <Label>Skills Gained</Label>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => addArrayItem('skills_gained')}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Add Skill
                            </Button>
                          </div>
                          <div className="space-y-2">
                            {courseFormData.skills_gained.map((item, index) => (
                              <div key={index} className="flex gap-2">
                                <Input
                                  value={item}
                                  onChange={(e) => updateArrayItem('skills_gained', index, e.target.value)}
                                  placeholder="e.g., Python, Data Analysis"
                                  className="flex-1"
                                />
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => removeArrayItem('skills_gained', index)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                            {courseFormData.skills_gained.length === 0 && (
                              <p className="text-sm text-gray-500 italic">No skills added yet</p>
                            )}
                          </div>
                        </div>

                        {/* Syllabus Overview */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <Label>Syllabus Overview</Label>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => addArrayItem('syllabus_overview')}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Add Topic
                            </Button>
                          </div>
                          <div className="space-y-2 max-h-60 overflow-y-auto">
                            {courseFormData.syllabus_overview.map((item, index) => (
                              <div key={index} className="flex gap-2">
                                <Input
                                  value={item}
                                  onChange={(e) => updateArrayItem('syllabus_overview', index, e.target.value)}
                                  placeholder="e.g., Introduction to Python"
                                  className="flex-1"
                                />
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => removeArrayItem('syllabus_overview', index)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                            {courseFormData.syllabus_overview.length === 0 && (
                              <p className="text-sm text-gray-500 italic">No syllabus topics added yet</p>
                            )}
                          </div>
                        </div>

                        {/* Career Paths */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <Label>Career Paths</Label>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => addArrayItem('career_paths')}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Add Career
                            </Button>
                          </div>
                          <div className="space-y-2">
                            {courseFormData.career_paths.map((item, index) => (
                              <div key={index} className="flex gap-2">
                                <Input
                                  value={item}
                                  onChange={(e) => updateArrayItem('career_paths', index, e.target.value)}
                                  placeholder="e.g., Data Scientist"
                                  className="flex-1"
                                />
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => removeArrayItem('career_paths', index)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                            {courseFormData.career_paths.length === 0 && (
                              <p className="text-sm text-gray-500 italic">No career paths added yet</p>
                            )}
                          </div>
                        </div>

                        {/* Top Recruiters */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <Label>Top Recruiters</Label>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => addArrayItem('top_recruiters')}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Add Company
                            </Button>
                          </div>
                          <div className="space-y-2">
                            {courseFormData.top_recruiters.map((item, index) => (
                              <div key={index} className="flex gap-2">
                                <Input
                                  value={item}
                                  onChange={(e) => updateArrayItem('top_recruiters', index, e.target.value)}
                                  placeholder="e.g., Google, Microsoft"
                                  className="flex-1"
                                />
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => removeArrayItem('top_recruiters', index)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                            {courseFormData.top_recruiters.length === 0 && (
                              <p className="text-sm text-gray-500 italic">No companies added yet</p>
                            )}
                          </div>
                        </div>

                        {/* Checkboxes */}
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={courseFormData.is_popular}
                              onChange={(e) => setCourseFormData({...courseFormData, is_popular: e.target.checked})}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">Mark as Popular</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={courseFormData.is_active}
                              onChange={(e) => setCourseFormData({...courseFormData, is_active: e.target.checked})}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">Active</span>
                          </label>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end gap-2 pt-4 border-t">
                          <Button variant="outline" onClick={() => {
                            setIsCourseDialogOpen(false);
                            resetCourseForm();
                          }}>
                            Cancel
                          </Button>
                          <Button onClick={editingCourse ? handleUpdateCourse : handleCreateCourse}>
                            {editingCourse ? 'Update' : 'Create'} Course
                          </Button>
                        </div>
                      </div>
                    ) : (
                      // Preview Section
                      <div className="space-y-4">
                        <div className="bg-gray-50 p-6 rounded-lg">
                          <h3 className="text-sm font-semibold text-gray-500 mb-4">COURSE PREVIEW</h3>
                          
                          {/* Course Image */}
                          {(courseImagePreview || courseFormData.image_url) && (
                            <img
                              src={courseImagePreview || courseFormData.image_url}
                              alt="Course"
                              className="w-full h-64 object-cover rounded-lg mb-4"
                            />
                          )}

                          {/* Course Title */}
                          <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            {courseFormData.title || 'Course Title'}
                          </h2>

                          {/* Duration and Price */}
                          <div className="flex items-center gap-4 mb-4">
                            <Badge variant="outline">{courseFormData.duration || 'Duration'}</Badge>
                            <span className="text-2xl font-bold text-blue-600">
                              ₹{courseFormData.price || 0}
                            </span>
                            {courseFormData.is_popular && (
                              <Badge className="bg-yellow-500">Popular</Badge>
                            )}
                          </div>

                          {/* Description */}
                          <p className="text-gray-600 mb-4">
                            {courseFormData.description || 'Course description will appear here...'}
                          </p>

                          {/* Highlights */}
                          {courseFormData.highlights.length > 0 && (
                            <div className="mb-4">
                              <h4 className="font-semibold text-gray-900 mb-2">Highlights:</h4>
                              <ul className="space-y-1">
                                {courseFormData.highlights.filter(h => h.trim()).map((highlight, idx) => (
                                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                                    <span className="text-green-600 mt-1">✓</span>
                                    {highlight}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* AI Features */}
                          {courseFormData.ai_features.length > 0 && (
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2">AI Features:</h4>
                              <div className="flex flex-wrap gap-2">
                                {courseFormData.ai_features.filter(f => f.trim()).map((feature, idx) => (
                                  <Badge key={idx} variant="secondary">{feature}</Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex justify-end gap-2 pt-4 border-t">
                          <Button variant="outline" onClick={() => setShowCoursePreview(false)}>
                            Back to Edit
                          </Button>
                        </div>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>

                {/* Lesson Management Dialog */}
                <Dialog open={isLessonDialogOpen} onOpenChange={setIsLessonDialogOpen}>
                  <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>
                        Manage Lessons - {selectedCourseForLessons?.title}
                      </DialogTitle>
                      <DialogDescription>
                        Add, edit, and organize video lessons for this course
                      </DialogDescription>
                    </DialogHeader>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Existing Lessons List */}
                      <div>
                        <h3 className="font-semibold mb-3 flex items-center gap-2">
                          <Video className="w-5 h-5" />
                          Lessons ({selectedCourseForLessons?.lessons?.length || 0})
                        </h3>
                        
                        {selectedCourseForLessons?.lessons && selectedCourseForLessons.lessons.length > 0 ? (
                          <div className="space-y-2 max-h-96 overflow-y-auto">
                            {[...selectedCourseForLessons.lessons]
                              .sort((a, b) => (a.order || 0) - (b.order || 0))
                              .map((lesson, idx) => (
                              <Card key={lesson.id} className="p-3 hover:shadow-md transition-shadow">
                                <div className="flex items-start gap-3">
                                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-sm font-medium">
                                    {lesson.order}
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm truncate">{lesson.title}</p>
                                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                                      <span>{lesson.duration_minutes} min</span>
                                      {lesson.is_preview && (
                                        <Badge variant="outline" className="text-xs">Preview</Badge>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex gap-1">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => openViewLesson(lesson)}
                                      title="View lesson details"
                                    >
                                      <Eye className="w-3 h-3 text-blue-600" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => openLessonForm(lesson)}
                                      title="Edit lesson"
                                    >
                                      <Edit className="w-3 h-3 text-green-600" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDeleteLesson(lesson.id)}
                                      title="Delete lesson"
                                    >
                                      <Trash2 className="w-3 h-3 text-red-600" />
                                    </Button>
                                  </div>
                                </div>
                              </Card>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <Video className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No lessons added yet</p>
                          </div>
                        )}
                      </div>

                      {/* Lesson Form */}
                      <div>
                        <h3 className="font-semibold mb-3">
                          {editingLesson ? 'Edit Lesson' : 'Add New Lesson'}
                        </h3>
                        
                        <div className="space-y-4">
                          <div>
                            <Label>Lesson Title *</Label>
                            <Input
                              value={lessonFormData.title}
                              onChange={(e) => setLessonFormData({...lessonFormData, title: e.target.value})}
                              placeholder="e.g., Introduction to React"
                            />
                          </div>

                          <div>
                            <Label>Video URL *</Label>
                            <Input
                              value={lessonFormData.video_url}
                              onChange={(e) => setLessonFormData({...lessonFormData, video_url: e.target.value})}
                              placeholder="https://youtube.com/... or video URL"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Supports YouTube, Vimeo, and direct video URLs
                            </p>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>Duration (minutes) *</Label>
                              <Input
                                type="number"
                                value={lessonFormData.duration_minutes}
                                onChange={(e) => setLessonFormData({...lessonFormData, duration_minutes: parseInt(e.target.value) || 0})}
                                placeholder="15"
                              />
                            </div>
                            <div>
                              <Label>Order *</Label>
                              <Input
                                type="number"
                                value={lessonFormData.order}
                                onChange={(e) => setLessonFormData({...lessonFormData, order: parseInt(e.target.value) || 1})}
                                placeholder="1"
                              />
                            </div>
                          </div>

                          <div>
                            <Label>Description</Label>
                            <Textarea
                              rows={3}
                              value={lessonFormData.description}
                              onChange={(e) => setLessonFormData({...lessonFormData, description: e.target.value})}
                              placeholder="Brief lesson description..."
                            />
                          </div>

                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id="is_preview"
                              checked={lessonFormData.is_preview}
                              onChange={(e) => setLessonFormData({...lessonFormData, is_preview: e.target.checked})}
                              className="rounded"
                            />
                            <Label htmlFor="is_preview" className="cursor-pointer">
                              Allow preview (can be viewed without enrollment)
                            </Label>
                          </div>

                          <div className="flex gap-2">
                            <Button
                              onClick={handleSaveLesson}
                              className="flex-1"
                              disabled={!lessonFormData.title || !lessonFormData.video_url}
                            >
                              {editingLesson ? 'Update Lesson' : 'Add Lesson'}
                            </Button>
                            {editingLesson && (
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setEditingLesson(null);
                                  openLessonForm();
                                }}
                              >
                                Cancel Edit
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <DialogFooter>
                      <Button variant="outline" onClick={() => {
                        setIsLessonDialogOpen(false);
                        setSelectedCourseForLessons(null);
                        setEditingLesson(null);
                      }}>
                        Close
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* View Lesson Dialog */}
                <Dialog open={isViewLessonDialogOpen} onOpenChange={setIsViewLessonDialogOpen}>
                  <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Eye className="w-5 h-5 text-blue-600" />
                        View Lesson Details
                      </DialogTitle>
                      <DialogDescription>
                        Read-only view of lesson information
                      </DialogDescription>
                    </DialogHeader>

                    {viewingLesson && (
                      <div className="space-y-4">
                        {/* Lesson Order Badge */}
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-sm">
                            Lesson #{viewingLesson.order}
                          </Badge>
                          {viewingLesson.is_preview && (
                            <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                              Preview Enabled
                            </Badge>
                          )}
                        </div>

                        {/* Title */}
                        <div>
                          <Label className="text-sm font-semibold text-gray-700">Lesson Title</Label>
                          <div className="mt-1 p-3 bg-gray-50 rounded-md border">
                            <p className="text-gray-900">{viewingLesson.title}</p>
                          </div>
                        </div>

                        {/* Video URL */}
                        <div>
                          <Label className="text-sm font-semibold text-gray-700">Video URL</Label>
                          <div className="mt-1 p-3 bg-gray-50 rounded-md border">
                            <a 
                              href={viewingLesson.video_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline break-all"
                            >
                              {viewingLesson.video_url}
                            </a>
                          </div>
                        </div>

                        {/* Duration */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-sm font-semibold text-gray-700">Duration</Label>
                            <div className="mt-1 p-3 bg-gray-50 rounded-md border">
                              <p className="text-gray-900">{viewingLesson.duration_minutes} minutes</p>
                            </div>
                          </div>
                          <div>
                            <Label className="text-sm font-semibold text-gray-700">Lesson ID</Label>
                            <div className="mt-1 p-3 bg-gray-50 rounded-md border">
                              <p className="text-gray-600 text-xs font-mono">{viewingLesson.id}</p>
                            </div>
                          </div>
                        </div>

                        {/* Description */}
                        <div>
                          <Label className="text-sm font-semibold text-gray-700">Description</Label>
                          <div className="mt-1 p-3 bg-gray-50 rounded-md border min-h-[80px]">
                            <p className="text-gray-900 whitespace-pre-wrap">
                              {viewingLesson.description || <span className="text-gray-400 italic">No description provided</span>}
                            </p>
                          </div>
                        </div>

                        {/* Materials */}
                        {viewingLesson.materials && viewingLesson.materials.length > 0 && (
                          <div>
                            <Label className="text-sm font-semibold text-gray-700">Learning Materials</Label>
                            <div className="mt-1 p-3 bg-gray-50 rounded-md border">
                              <ul className="space-y-2">
                                {viewingLesson.materials.map((material, idx) => (
                                  <li key={idx} className="flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-gray-500" />
                                    <a 
                                      href={material.url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:underline"
                                    >
                                      {material.name}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        )}

                        {/* Video Preview */}
                        <div>
                          <Label className="text-sm font-semibold text-gray-700 mb-2 block">Video Preview</Label>
                          <div className="relative w-full" style={{paddingTop: '56.25%'}}>
                            <ReactPlayer
                              url={viewingLesson.video_url}
                              className="absolute top-0 left-0"
                              width="100%"
                              height="100%"
                              controls={true}
                              config={{
                                youtube: {
                                  playerVars: { 
                                    showinfo: 1,
                                    modestbranding: 1,
                                    rel: 0
                                  }
                                }
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    <DialogFooter className="flex gap-2">
                      <Button 
                        variant="outline" 
                        onClick={closeViewLesson}
                      >
                        Close
                      </Button>
                      <Button 
                        onClick={() => {
                          closeViewLesson();
                          if (viewingLesson) {
                            openLessonForm(viewingLesson);
                          }
                        }}
                        className="flex items-center gap-2"
                      >
                        <Edit className="w-4 h-4" />
                        Edit This Lesson
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </TabsContent>

              {/* Payments Tab */}
              {/* Website Settings Tab */}
              <TabsContent value="website" className="space-y-6">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-bold">Website Settings</h2>
                    <p className="text-gray-600">Platform-wide settings (site name, logo, branding, favicon)</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => window.open('/', '_blank')}>
                      <Eye className="w-4 h-4 mr-2" />
                      Preview Landing Page
                    </Button>
                    <Button onClick={publishWebsiteSettings} className="bg-green-600 hover:bg-green-700">
                      <Send className="w-4 h-4 mr-2" />
                      Publish Changes
                    </Button>
                  </div>
                </div>

                {isWebsiteSettingsLoading && (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading website settings...</p>
                  </div>
                )}

                {!isWebsiteSettingsLoading && !websiteSettings && (
                  <Card className="p-8 text-center">
                    <h3 className="text-lg font-semibold mb-2">No Website Settings Found</h3>
                    <p className="text-gray-600 mb-4">Initialize default website settings to get started</p>
                    <Button onClick={initializeWebsiteSettings}>
                      <Plus className="w-4 h-4 mr-2" />
                      Initialize Website Settings
                    </Button>
                  </Card>
                )}

                {!isWebsiteSettingsLoading && websiteSettings && (
                  <div className="space-y-6">
                    {/* Site Info Card - Platform-Wide Settings Only */}
                    <Card className="p-6">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-bold">Site Information & Branding</h3>
                        <Button size="sm" onClick={() => setEditingSection('site_info')}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="font-semibold">Site Name</Label>
                          <p className="text-gray-700">{websiteSettings.site_name}</p>
                        </div>
                        <div>
                          <Label className="font-semibold">Tagline</Label>
                          <p className="text-gray-700">{websiteSettings.site_tagline}</p>
                        </div>
                        <div>
                          <Label className="font-semibold">Logo</Label>
                          {websiteSettings.logo_url ? (
                            <img src={websiteSettings.logo_url} alt="Logo" className="h-12 mt-2 object-contain" />
                          ) : (
                            <p className="text-gray-500 text-sm">No logo uploaded</p>
                          )}
                        </div>
                        <div>
                          <Label className="font-semibold">Favicon</Label>
                          {websiteSettings.favicon_url ? (
                            <img src={websiteSettings.favicon_url} alt="Favicon" className="h-8 mt-2" />
                          ) : (
                            <p className="text-gray-500 text-sm">No favicon uploaded</p>
                          )}
                        </div>
                      </div>
                    </Card>

                    {/* Info Card - Landing Page Content Moved */}
                    <Card className="p-6 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-purple-500 rounded-lg">
                          <Sparkles className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-purple-900 mb-2">Landing Page Content Moved!</h3>
                          <p className="text-purple-700 text-sm mb-3">
                            All landing page content (Hero, Features, Testimonials, etc.) is now managed in the 
                            <span className="font-bold"> ✨ Landing Page</span> tab for better organization.
                          </p>
                          <Button 
                            size="sm" 
                            className="bg-purple-600 hover:bg-purple-700"
                            onClick={() => setActiveTab('landing-page')}
                          >
                            <ArrowRight className="w-4 h-4 mr-2" />
                            Go to Landing Page Tab
                          </Button>
                        </div>
                      </div>
                    </Card>

                    {/* Landing Page Popup Section */}
                    <Card className="p-6 border-2 border-purple-200 bg-purple-50/30">
                      <div className="flex justify-between items-center mb-4">
                        <div>
                          <h3 className="text-xl font-bold flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-purple-600" />
                            Landing Page Popup
                          </h3>
                          <p className="text-sm text-gray-600">Engage visitors with a promotional popup</p>
                        </div>
                        <Button size="sm" onClick={() => setEditingSection('popup')}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </Button>
                      </div>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <Label className="font-semibold">Status</Label>
                          <p className={`text-sm font-medium ${websiteSettings.popup?.enabled ? 'text-green-600' : 'text-gray-500'}`}>
                            {websiteSettings.popup?.enabled ? '🟢 Enabled' : '⚪ Disabled'}
                          </p>
                        </div>
                        <div>
                          <Label className="font-semibold">Title</Label>
                          <p className="text-gray-700">{websiteSettings.popup?.title || 'Spark AI'}</p>
                        </div>
                        <div>
                          <Label className="font-semibold">Delay</Label>
                          <p className="text-gray-700">{websiteSettings.popup?.delay_seconds || 3} seconds</p>
                        </div>
                      </div>
                    </Card>

                    {/* Footer Section */}
                    <Card className="p-6">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-bold">Footer Content</h3>
                        <Button size="sm" onClick={() => setEditingSection('footer')}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </Button>
                      </div>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <Label className="font-semibold">Contact Email</Label>
                          <p className="text-gray-700">{websiteSettings.footer.contact_email}</p>
                        </div>
                        <div>
                          <Label className="font-semibold">Contact Phone</Label>
                          <p className="text-gray-700">{websiteSettings.footer.contact_phone}</p>
                        </div>
                        <div>
                          <Label className="font-semibold">Social Links</Label>
                          <p className="text-gray-700">{Object.keys(websiteSettings.footer.social_links).length} links</p>
                        </div>
                      </div>
                    </Card>

                    {/* Status Info */}
                    <Card className="p-4 bg-blue-50 border-blue-200">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${websiteSettings.is_published ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                        <span className="font-semibold">
                          Status: {websiteSettings.is_published ? 'Published' : 'Draft'}
                        </span>
                        <span className="text-gray-600 text-sm ml-auto">
                          Last updated: {new Date(websiteSettings.updated_at).toLocaleString()}
                        </span>
                      </div>
                    </Card>
                  </div>
                )}

                {/* Edit Dialog - Functional Forms */}
                {editingSection && (
                  <Dialog open={!!editingSection} onOpenChange={() => setEditingSection(null)}>
                    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Edit {editingSection.replace('_', ' ')} Section</DialogTitle>
                      </DialogHeader>
                      
                      <div className="py-4 space-y-4">
                        {/* Site Info Edit Form */}
                        {editingSection === 'site_info' && websiteSettings && (
                          <div className="space-y-4">
                            <div>
                              <Label>Site Name</Label>
                              <Input
                                value={websiteSettings.site_name}
                                onChange={(e) => setWebsiteSettings({...websiteSettings, site_name: e.target.value})}
                              />
                            </div>
                            <div>
                              <Label>Site Tagline</Label>
                              <Input
                                value={websiteSettings.site_tagline}
                                onChange={(e) => setWebsiteSettings({...websiteSettings, site_tagline: e.target.value})}
                              />
                            </div>
                            <div className="border-t pt-4">
                              <h4 className="font-semibold mb-3">Branding Assets</h4>
                              
                              {/* Logo Section */}
                              <div className="mb-6 p-4 border rounded-lg">
                                <Label className="text-base font-semibold">Logo</Label>
                                <div className="mt-2 space-y-3">
                                  {/* URL Input */}
                                  <div>
                                    <Label className="text-sm">Option 1: Enter URL</Label>
                                    <Input
                                      value={websiteSettings.logo_url || ''}
                                      onChange={(e) => setWebsiteSettings({...websiteSettings, logo_url: e.target.value})}
                                      placeholder="https://your-domain.com/logo.png"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Enter logo image URL (recommended: 200x50px, PNG with transparency)</p>
                                  </div>
                                  
                                  {/* OR Divider */}
                                  <div className="flex items-center gap-2">
                                    <div className="flex-1 border-t"></div>
                                    <span className="text-xs text-gray-500">OR</span>
                                    <div className="flex-1 border-t"></div>
                                  </div>
                                  
                                  {/* File Upload */}
                                  <div>
                                    <Label className="text-sm">Option 2: Upload File</Label>
                                    <Input
                                      type="file"
                                      accept="image/png,image/jpeg,image/svg+xml"
                                      onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                          handleAssetUpload(file, 'logo');
                                        }
                                      }}
                                      className="mt-1"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Upload PNG, JPG, or SVG (max 5MB)</p>
                                  </div>
                                  
                                  {/* Preview */}
                                  {websiteSettings.logo_url && (
                                    <div className="mt-2 p-2 bg-gray-100 rounded">
                                      <div className="flex items-center justify-between mb-1">
                                        <Label className="text-xs">Preview:</Label>
                                        <Button
                                          size="sm"
                                          variant="destructive"
                                          onClick={() => setWebsiteSettings({...websiteSettings, logo_url: ''})}
                                          className="h-7 px-2 text-xs"
                                        >
                                          <Trash2 className="w-3 h-3 mr-1" />
                                          Delete Logo
                                        </Button>
                                      </div>
                                      <img src={websiteSettings.logo_url} alt="Logo preview" className="h-12 mt-1 object-contain" />
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Favicon Section */}
                              <div className="p-4 border rounded-lg">
                                <Label className="text-base font-semibold">Favicon</Label>
                                <div className="mt-2 space-y-3">
                                  {/* URL Input */}
                                  <div>
                                    <Label className="text-sm">Option 1: Enter URL</Label>
                                    <Input
                                      value={websiteSettings.favicon_url || ''}
                                      onChange={(e) => setWebsiteSettings({...websiteSettings, favicon_url: e.target.value})}
                                      placeholder="https://your-domain.com/favicon.ico"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Enter favicon URL (recommended: 32x32px or 64x64px, ICO or PNG format)</p>
                                  </div>
                                  
                                  {/* OR Divider */}
                                  <div className="flex items-center gap-2">
                                    <div className="flex-1 border-t"></div>
                                    <span className="text-xs text-gray-500">OR</span>
                                    <div className="flex-1 border-t"></div>
                                  </div>
                                  
                                  {/* File Upload */}
                                  <div>
                                    <Label className="text-sm">Option 2: Upload File</Label>
                                    <Input
                                      type="file"
                                      accept="image/png,image/x-icon,image/jpeg"
                                      onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                          handleAssetUpload(file, 'favicon');
                                        }
                                      }}
                                      className="mt-1"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Upload ICO or PNG (max 5MB)</p>
                                  </div>
                                  
                                  {/* Preview */}
                                  {websiteSettings.favicon_url && (
                                    <div className="mt-2 p-2 bg-gray-100 rounded">
                                      <div className="flex items-center justify-between mb-1">
                                        <Label className="text-xs">Preview:</Label>
                                        <Button
                                          size="sm"
                                          variant="destructive"
                                          onClick={() => setWebsiteSettings({...websiteSettings, favicon_url: ''})}
                                          className="h-7 px-2 text-xs"
                                        >
                                          <Trash2 className="w-3 h-3 mr-1" />
                                          Delete Favicon
                                        </Button>
                                      </div>
                                      <img src={websiteSettings.favicon_url} alt="Favicon preview" className="h-8 mt-1" />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Hero Section Edit Form */}
                        {editingSection === 'hero' && websiteSettings && (
                          <div className="space-y-4">
                            <div>
                              <Label>Title</Label>
                              <Input
                                value={websiteSettings.hero.title}
                                onChange={(e) => setWebsiteSettings({
                                  ...websiteSettings,
                                  hero: {...websiteSettings.hero, title: e.target.value}
                                })}
                              />
                            </div>
                            <div>
                              <Label>Subtitle</Label>
                              <Input
                                value={websiteSettings.hero.subtitle}
                                onChange={(e) => setWebsiteSettings({
                                  ...websiteSettings,
                                  hero: {...websiteSettings.hero, subtitle: e.target.value}
                                })}
                              />
                            </div>
                            <div>
                              <Label>Description</Label>
                              <Textarea
                                rows={3}
                                value={websiteSettings.hero.description}
                                onChange={(e) => setWebsiteSettings({
                                  ...websiteSettings,
                                  hero: {...websiteSettings.hero, description: e.target.value}
                                })}
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label>Primary Button Text</Label>
                                <Input
                                  value={websiteSettings.hero.primary_cta_text}
                                  onChange={(e) => setWebsiteSettings({
                                    ...websiteSettings,
                                    hero: {...websiteSettings.hero, primary_cta_text: e.target.value}
                                  })}
                                />
                              </div>
                              <div>
                                <Label>Primary Button Link</Label>
                                <Input
                                  value={websiteSettings.hero.primary_cta_link}
                                  onChange={(e) => setWebsiteSettings({
                                    ...websiteSettings,
                                    hero: {...websiteSettings.hero, primary_cta_link: e.target.value}
                                  })}
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label>Secondary Button Text</Label>
                                <Input
                                  value={websiteSettings.hero.secondary_cta_text}
                                  onChange={(e) => setWebsiteSettings({
                                    ...websiteSettings,
                                    hero: {...websiteSettings.hero, secondary_cta_text: e.target.value}
                                  })}
                                />
                              </div>
                              <div>
                                <Label>Secondary Button Link</Label>
                                <Input
                                  value={websiteSettings.hero.secondary_cta_link}
                                  onChange={(e) => setWebsiteSettings({
                                    ...websiteSettings,
                                    hero: {...websiteSettings.hero, secondary_cta_link: e.target.value}
                                  })}
                                />
                              </div>
                            </div>
                            <div>
                              <Label>Banner Image URL</Label>
                              <Input
                                value={websiteSettings.hero.banner_image}
                                onChange={(e) => setWebsiteSettings({
                                  ...websiteSettings,
                                  hero: {...websiteSettings.hero, banner_image: e.target.value}
                                })}
                                placeholder="https://images.unsplash.com/..."
                              />
                              <p className="text-xs text-gray-500 mt-1">Use Unsplash or any image URL</p>
                            </div>
                          </div>
                        )}

                        {/* About Section Edit Form */}
                        {editingSection === 'about' && websiteSettings && (
                          <div className="space-y-4">
                            <div>
                              <Label>Title</Label>
                              <Input
                                value={websiteSettings.about.title}
                                onChange={(e) => setWebsiteSettings({
                                  ...websiteSettings,
                                  about: {...websiteSettings.about, title: e.target.value}
                                })}
                              />
                            </div>
                            <div>
                              <Label>Description</Label>
                              <Textarea
                                rows={3}
                                value={websiteSettings.about.description}
                                onChange={(e) => setWebsiteSettings({
                                  ...websiteSettings,
                                  about: {...websiteSettings.about, description: e.target.value}
                                })}
                              />
                            </div>
                            <div>
                              <Label>Mission</Label>
                              <Textarea
                                rows={2}
                                value={websiteSettings.about.mission}
                                onChange={(e) => setWebsiteSettings({
                                  ...websiteSettings,
                                  about: {...websiteSettings.about, mission: e.target.value}
                                })}
                              />
                            </div>
                            <div>
                              <Label>Vision</Label>
                              <Textarea
                                rows={2}
                                value={websiteSettings.about.vision}
                                onChange={(e) => setWebsiteSettings({
                                  ...websiteSettings,
                                  about: {...websiteSettings.about, vision: e.target.value}
                                })}
                              />
                            </div>
                            <div>
                              <Label>Image URL</Label>
                              <Input
                                value={websiteSettings.about.image}
                                onChange={(e) => setWebsiteSettings({
                                  ...websiteSettings,
                                  about: {...websiteSettings.about, image: e.target.value}
                                })}
                                placeholder="https://images.unsplash.com/..."
                              />
                            </div>
                          </div>
                        )}

                        {/* Popup Section Edit Form */}
                        {editingSection === 'popup' && websiteSettings && (
                          <div className="space-y-4">
                            <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
                              <div>
                                <Label className="text-base font-semibold">Enable Popup</Label>
                                <p className="text-sm text-gray-600">Show promotional popup on landing page</p>
                              </div>
                              <input
                                type="checkbox"
                                className="w-6 h-6 rounded"
                                checked={websiteSettings.popup?.enabled || false}
                                onChange={(e) => setWebsiteSettings({
                                  ...websiteSettings,
                                  popup: {
                                    ...(websiteSettings.popup || {}),
                                    enabled: e.target.checked
                                  }
                                })}
                              />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label>Popup Title <span className="text-red-500">*</span></Label>
                                <Input
                                  value={websiteSettings.popup?.title || 'Spark AI'}
                                  onChange={(e) => setWebsiteSettings({
                                    ...websiteSettings,
                                    popup: {...(websiteSettings.popup || {}), title: e.target.value}
                                  })}
                                  placeholder="Spark AI"
                                />
                              </div>
                              <div>
                                <Label>Delay (seconds)</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  max="10"
                                  value={websiteSettings.popup?.delay_seconds || 3}
                                  onChange={(e) => setWebsiteSettings({
                                    ...websiteSettings,
                                    popup: {...(websiteSettings.popup || {}), delay_seconds: parseInt(e.target.value)}
                                  })}
                                />
                              </div>
                            </div>

                            <div>
                              <Label>Subtitle <span className="text-red-500">*</span></Label>
                              <Input
                                value={websiteSettings.popup?.subtitle || ''}
                                onChange={(e) => setWebsiteSettings({
                                  ...websiteSettings,
                                  popup: {...(websiteSettings.popup || {}), subtitle: e.target.value}
                                })}
                                placeholder="Your Personal Career Guidance Buddy"
                              />
                            </div>

                            <div>
                              <Label>Description</Label>
                              <Textarea
                                rows={2}
                                value={websiteSettings.popup?.description || ''}
                                onChange={(e) => setWebsiteSettings({
                                  ...websiteSettings,
                                  popup: {...(websiteSettings.popup || {}), description: e.target.value}
                                })}
                                placeholder="Ask Spark to answer all your career-related queries"
                              />
                            </div>

                            <div>
                              <Label>Features (one per line)</Label>
                              <Textarea
                                rows={4}
                                value={(websiteSettings.popup?.features || []).join('\n')}
                                onChange={(e) => setWebsiteSettings({
                                  ...websiteSettings,
                                  popup: {
                                    ...(websiteSettings.popup || {}),
                                    features: e.target.value.split('\n').filter(f => f.trim())
                                  }
                                })}
                                placeholder="Best Fit Advice&#10;Instant Career Insights&#10;Interactive Career Support"
                              />
                              <p className="text-xs text-gray-500 mt-1">Enter each feature on a new line</p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label>Button Text</Label>
                                <Input
                                  value={websiteSettings.popup?.button_text || 'Start Now'}
                                  onChange={(e) => setWebsiteSettings({
                                    ...websiteSettings,
                                    popup: {...(websiteSettings.popup || {}), button_text: e.target.value}
                                  })}
                                  placeholder="Start Now"
                                />
                              </div>
                              <div>
                                <Label>Button Link</Label>
                                <Input
                                  value={websiteSettings.popup?.button_link || '/chatbot'}
                                  onChange={(e) => setWebsiteSettings({
                                    ...websiteSettings,
                                    popup: {...(websiteSettings.popup || {}), button_link: e.target.value}
                                  })}
                                  placeholder="/chatbot"
                                />
                              </div>
                            </div>

                            <div>
                              <Label>Image URL (optional)</Label>
                              <Input
                                value={websiteSettings.popup?.image_url || ''}
                                onChange={(e) => setWebsiteSettings({
                                  ...websiteSettings,
                                  popup: {...(websiteSettings.popup || {}), image_url: e.target.value}
                                })}
                                placeholder="https://example.com/robot-ai.png"
                              />
                              <p className="text-xs text-gray-500 mt-1">Leave empty to use default AI icon</p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label>Background Color</Label>
                                <div className="flex gap-2">
                                  <Input
                                    value={websiteSettings.popup?.background_color || '#0f172a'}
                                    onChange={(e) => setWebsiteSettings({
                                      ...websiteSettings,
                                      popup: {...(websiteSettings.popup || {}), background_color: e.target.value}
                                    })}
                                  />
                                  <input
                                    type="color"
                                    className="w-12 h-10 rounded border"
                                    value={websiteSettings.popup?.background_color || '#0f172a'}
                                    onChange={(e) => setWebsiteSettings({
                                      ...websiteSettings,
                                      popup: {...(websiteSettings.popup || {}), background_color: e.target.value}
                                    })}
                                  />
                                </div>
                              </div>
                              <div>
                                <Label>Primary Color (accents)</Label>
                                <div className="flex gap-2">
                                  <Input
                                    value={websiteSettings.popup?.primary_color || '#38bdf8'}
                                    onChange={(e) => setWebsiteSettings({
                                      ...websiteSettings,
                                      popup: {...(websiteSettings.popup || {}), primary_color: e.target.value}
                                    })}
                                  />
                                  <input
                                    type="color"
                                    className="w-12 h-10 rounded border"
                                    value={websiteSettings.popup?.primary_color || '#38bdf8'}
                                    onChange={(e) => setWebsiteSettings({
                                      ...websiteSettings,
                                      popup: {...(websiteSettings.popup || {}), primary_color: e.target.value}
                                    })}
                                  />
                                </div>
                              </div>
                            </div>

                            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                              <p className="text-sm text-gray-700">
                                💡 <strong>Tip:</strong> The popup will appear {websiteSettings.popup?.delay_seconds || 3} seconds after visitors land on your homepage. It shows every time they visit.
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Footer Section Edit Form */}
                        {editingSection === 'footer' && websiteSettings && (
                          <div className="space-y-4">
                            <div>
                              <Label>About Text</Label>
                              <Textarea
                                rows={2}
                                value={websiteSettings.footer.about_text}
                                onChange={(e) => setWebsiteSettings({
                                  ...websiteSettings,
                                  footer: {...websiteSettings.footer, about_text: e.target.value}
                                })}
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label>Contact Email</Label>
                                <Input
                                  value={websiteSettings.footer.contact_email}
                                  onChange={(e) => setWebsiteSettings({
                                    ...websiteSettings,
                                    footer: {...websiteSettings.footer, contact_email: e.target.value}
                                  })}
                                />
                              </div>
                              <div>
                                <Label>Contact Phone</Label>
                                <Input
                                  value={websiteSettings.footer.contact_phone}
                                  onChange={(e) => setWebsiteSettings({
                                    ...websiteSettings,
                                    footer: {...websiteSettings.footer, contact_phone: e.target.value}
                                  })}
                                />
                              </div>
                            </div>
                            <div>
                              <Label>Social Media Links</Label>
                              <div className="space-y-2 mt-2">
                                <Input
                                  placeholder="Facebook URL"
                                  value={websiteSettings.footer.social_links.facebook || ''}
                                  onChange={(e) => setWebsiteSettings({
                                    ...websiteSettings,
                                    footer: {
                                      ...websiteSettings.footer,
                                      social_links: {...websiteSettings.footer.social_links, facebook: e.target.value}
                                    }
                                  })}
                                />
                                <Input
                                  placeholder="Twitter URL"
                                  value={websiteSettings.footer.social_links.twitter || ''}
                                  onChange={(e) => setWebsiteSettings({
                                    ...websiteSettings,
                                    footer: {
                                      ...websiteSettings.footer,
                                      social_links: {...websiteSettings.footer.social_links, twitter: e.target.value}
                                    }
                                  })}
                                />
                                <Input
                                  placeholder="LinkedIn URL"
                                  value={websiteSettings.footer.social_links.linkedin || ''}
                                  onChange={(e) => setWebsiteSettings({
                                    ...websiteSettings,
                                    footer: {
                                      ...websiteSettings.footer,
                                      social_links: {...websiteSettings.footer.social_links, linkedin: e.target.value}
                                    }
                                  })}
                                />
                                <Input
                                  placeholder="Instagram URL"
                                  value={websiteSettings.footer.social_links.instagram || ''}
                                  onChange={(e) => setWebsiteSettings({
                                    ...websiteSettings,
                                    footer: {
                                      ...websiteSettings.footer,
                                      social_links: {...websiteSettings.footer.social_links, instagram: e.target.value}
                                    }
                                  })}
                                />
                              </div>
                            </div>
                            <div>
                              <Label>Copyright Text</Label>
                              <Input
                                value={websiteSettings.footer.copyright_text}
                                onChange={(e) => setWebsiteSettings({
                                  ...websiteSettings,
                                  footer: {...websiteSettings.footer, copyright_text: e.target.value}
                                })}
                              />
                            </div>
                          </div>
                        )}

                        {/* Why Choose Us Section Edit Form */}
                        {editingSection === 'why_choose_us' && websiteSettings && websiteSettings.why_choose_us && (
                          <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label>Section Title</Label>
                                <Input
                                  value={websiteSettings.why_choose_us.section_title}
                                  onChange={(e) => setWebsiteSettings({
                                    ...websiteSettings,
                                    why_choose_us: {...websiteSettings.why_choose_us, section_title: e.target.value}
                                  })}
                                />
                              </div>
                              <div>
                                <Label>Section Subtitle</Label>
                                <Input
                                  value={websiteSettings.why_choose_us.section_subtitle}
                                  onChange={(e) => setWebsiteSettings({
                                    ...websiteSettings,
                                    why_choose_us: {...websiteSettings.why_choose_us, section_subtitle: e.target.value}
                                  })}
                                />
                              </div>
                            </div>

                            <div className="border-t pt-4">
                              <div className="flex justify-between items-center mb-4">
                                <Label className="text-base font-semibold">Benefit Cards</Label>
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    const newBenefit = {
                                      icon: "✨",
                                      title: "New Benefit",
                                      description: "Benefit description"
                                    };
                                    setWebsiteSettings({
                                      ...websiteSettings,
                                      why_choose_us: {
                                        ...websiteSettings.why_choose_us,
                                        benefits: [...(websiteSettings.why_choose_us.benefits || []), newBenefit]
                                      }
                                    });
                                  }}
                                >
                                  <Plus className="w-4 h-4 mr-2" />
                                  Add Benefit
                                </Button>
                              </div>

                              {(websiteSettings.why_choose_us.benefits || []).map((benefit, idx) => (
                                <Card key={idx} className="p-4 mb-4">
                                  <div className="flex justify-between items-start mb-3">
                                    <span className="font-semibold">Benefit {idx + 1}</span>
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      onClick={() => {
                                        const newBenefits = websiteSettings.why_choose_us.benefits.filter((_, i) => i !== idx);
                                        setWebsiteSettings({
                                          ...websiteSettings,
                                          why_choose_us: {...websiteSettings.why_choose_us, benefits: newBenefits}
                                        });
                                      }}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                  <div className="grid grid-cols-1 gap-3">
                                    <div>
                                      <Label>Icon (Emoji)</Label>
                                      <Input
                                        value={benefit.icon}
                                        onChange={(e) => {
                                          const newBenefits = [...websiteSettings.why_choose_us.benefits];
                                          newBenefits[idx].icon = e.target.value;
                                          setWebsiteSettings({
                                            ...websiteSettings,
                                            why_choose_us: {...websiteSettings.why_choose_us, benefits: newBenefits}
                                          });
                                        }}
                                        placeholder="✨"
                                      />
                                    </div>
                                    <div>
                                      <Label>Title</Label>
                                      <Input
                                        value={benefit.title}
                                        onChange={(e) => {
                                          const newBenefits = [...websiteSettings.why_choose_us.benefits];
                                          newBenefits[idx].title = e.target.value;
                                          setWebsiteSettings({
                                            ...websiteSettings,
                                            why_choose_us: {...websiteSettings.why_choose_us, benefits: newBenefits}
                                          });
                                        }}
                                      />
                                    </div>
                                    <div>
                                      <Label>Description</Label>
                                      <Textarea
                                        rows={3}
                                        value={benefit.description}
                                        onChange={(e) => {
                                          const newBenefits = [...websiteSettings.why_choose_us.benefits];
                                          newBenefits[idx].description = e.target.value;
                                          setWebsiteSettings({
                                            ...websiteSettings,
                                            why_choose_us: {...websiteSettings.why_choose_us, benefits: newBenefits}
                                          });
                                        }}
                                      />
                                    </div>
                                  </div>
                                </Card>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Features Section Edit Form */}
                        {editingSection === 'features' && websiteSettings && (
                          <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label>Section Title</Label>
                                <Input
                                  value={websiteSettings.features.section_title}
                                  onChange={(e) => setWebsiteSettings({
                                    ...websiteSettings,
                                    features: {...websiteSettings.features, section_title: e.target.value}
                                  })}
                                />
                              </div>
                              <div>
                                <Label>Section Subtitle</Label>
                                <Input
                                  value={websiteSettings.features.section_subtitle}
                                  onChange={(e) => setWebsiteSettings({
                                    ...websiteSettings,
                                    features: {...websiteSettings.features, section_subtitle: e.target.value}
                                  })}
                                />
                              </div>
                            </div>

                            <div className="border-t pt-4">
                              <div className="flex justify-between items-center mb-4">
                                <Label className="text-base font-semibold">Feature Cards</Label>
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    const newFeature = {
                                      icon: "🎯",
                                      title: "New Feature",
                                      description: "Feature description",
                                      color: "#667eea"
                                    };
                                    setWebsiteSettings({
                                      ...websiteSettings,
                                      features: {
                                        ...websiteSettings.features,
                                        features: [...websiteSettings.features.features, newFeature]
                                      }
                                    });
                                  }}
                                >
                                  <Plus className="w-4 h-4 mr-2" />
                                  Add Feature
                                </Button>
                              </div>

                              {websiteSettings.features.features.map((feature, idx) => (
                                <Card key={idx} className="p-4 mb-4">
                                  <div className="flex justify-between items-start mb-3">
                                    <span className="font-semibold">Feature {idx + 1}</span>
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      onClick={() => {
                                        const newFeatures = websiteSettings.features.features.filter((_, i) => i !== idx);
                                        setWebsiteSettings({
                                          ...websiteSettings,
                                          features: {...websiteSettings.features, features: newFeatures}
                                        });
                                      }}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <Label>Icon (Emoji)</Label>
                                      <Input
                                        value={feature.icon}
                                        onChange={(e) => {
                                          const newFeatures = [...websiteSettings.features.features];
                                          newFeatures[idx].icon = e.target.value;
                                          setWebsiteSettings({
                                            ...websiteSettings,
                                            features: {...websiteSettings.features, features: newFeatures}
                                          });
                                        }}
                                        placeholder="🎯"
                                      />
                                    </div>
                                    <div>
                                      <Label>Color (Hex)</Label>
                                      <Input
                                        value={feature.color}
                                        onChange={(e) => {
                                          const newFeatures = [...websiteSettings.features.features];
                                          newFeatures[idx].color = e.target.value;
                                          setWebsiteSettings({
                                            ...websiteSettings,
                                            features: {...websiteSettings.features, features: newFeatures}
                                          });
                                        }}
                                        placeholder="#667eea"
                                      />
                                    </div>
                                    <div className="col-span-2">
                                      <Label>Title</Label>
                                      <Input
                                        value={feature.title}
                                        onChange={(e) => {
                                          const newFeatures = [...websiteSettings.features.features];
                                          newFeatures[idx].title = e.target.value;
                                          setWebsiteSettings({
                                            ...websiteSettings,
                                            features: {...websiteSettings.features, features: newFeatures}
                                          });
                                        }}
                                      />
                                    </div>
                                    <div className="col-span-2">
                                      <Label>Description</Label>
                                      <Textarea
                                        rows={2}
                                        value={feature.description}
                                        onChange={(e) => {
                                          const newFeatures = [...websiteSettings.features.features];
                                          newFeatures[idx].description = e.target.value;
                                          setWebsiteSettings({
                                            ...websiteSettings,
                                            features: {...websiteSettings.features, features: newFeatures}
                                          });
                                        }}
                                      />
                                    </div>
                                  </div>
                                </Card>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Other sections info */}
                        {!['site_info', 'hero', 'about', 'footer', 'features', 'why_choose_us'].includes(editingSection) && (
                          <div className="text-center py-8 border-2 border-dashed rounded-lg">
                            <p className="text-gray-600 mb-2 font-semibold">
                              {editingSection.replace('_', ' ').toUpperCase()} Section
                            </p>
                            <p className="text-sm text-gray-500 mb-4">
                              Complex nested data editing for pricing, stats, and testimonials.
                            </p>
                            <p className="text-xs text-gray-400">
                              For MVP: Use MongoDB or API to edit these sections directly.<br/>
                              Advanced editor will be added in future updates.
                            </p>
                          </div>
                        )}
                      </div>
                      
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setEditingSection(null)}>Cancel</Button>
                        {['site_info', 'hero', 'about', 'footer', 'features', 'popup'].includes(editingSection) && (
                          <Button onClick={() => {
                            if (editingSection === 'site_info') {
                              updateWebsiteSettings('site_name', websiteSettings.site_name);
                              updateWebsiteSettings('site_tagline', websiteSettings.site_tagline);
                              if (websiteSettings.logo_url !== undefined) {
                                updateWebsiteSettings('logo_url', websiteSettings.logo_url);
                              }
                              if (websiteSettings.favicon_url !== undefined) {
                                updateWebsiteSettings('favicon_url', websiteSettings.favicon_url);
                              }
                            } else {
                              updateWebsiteSettings(editingSection, websiteSettings[editingSection]);
                            }
                          }}>
                            Save Changes
                          </Button>
                        )}
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
              </TabsContent>

              {/* Landing Page CMS Tab */}
              <TabsContent value="landing-page" className="space-y-6">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-bold">Landing Page Management</h2>
                    <p className="text-gray-600">Edit all content on your landing page</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => window.open('/', '_blank')}>
                      <Eye className="w-4 h-4 mr-2" />
                      Preview
                    </Button>
                    <Button onClick={saveLandingPageContent} className="bg-green-600 hover:bg-green-700">
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </Button>
                  </div>
                </div>

                {isLandingPageLoading && (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading...</p>
                  </div>
                )}

                {!isLandingPageLoading && landingPageContent && (
                  <Tabs defaultValue="hero" className="space-y-4">
                    <TabsList className="mb-4 flex-wrap h-auto">
                      <TabsTrigger value="hero">Hero</TabsTrigger>
                      <TabsTrigger value="features">Features</TabsTrigger>
                      <TabsTrigger value="how-it-works">How It Works</TabsTrigger>
                      <TabsTrigger value="why-choose-us">Why Choose Us</TabsTrigger>
                      <TabsTrigger value="about">About</TabsTrigger>
                      <TabsTrigger value="pricing">Pricing</TabsTrigger>
                      <TabsTrigger value="stats">Stats</TabsTrigger>
                      <TabsTrigger value="testimonials">Testimonials</TabsTrigger>
                      <TabsTrigger value="footer">Footer</TabsTrigger>
                    </TabsList>

                    {/* Hero Section */}
                    <TabsContent value="hero">
                      <Card className="p-6">
                        <h3 className="text-xl font-bold mb-4">Hero Section</h3>
                        <div className="space-y-4">
                          <div>
                            <Label>Title</Label>
                            <Input
                              value={landingPageContent.hero?.title || ''}
                              onChange={(e) => updateLandingSection('hero', {...landingPageContent.hero, title: e.target.value})}
                              placeholder="Main headline"
                            />
                          </div>
                          <div>
                            <Label>Subtitle</Label>
                            <Input
                              value={landingPageContent.hero?.subtitle || ''}
                              onChange={(e) => updateLandingSection('hero', {...landingPageContent.hero, subtitle: e.target.value})}
                              placeholder="AI-Powered Career Guidance Platform"
                            />
                          </div>
                          <div>
                            <Label>Description</Label>
                            <Textarea
                              value={landingPageContent.hero?.description || ''}
                              onChange={(e) => updateLandingSection('hero', {...landingPageContent.hero, description: e.target.value})}
                              placeholder="Longer description text"
                              rows={3}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>Primary CTA Text</Label>
                              <Input
                                value={landingPageContent.hero?.primary_cta_text || ''}
                                onChange={(e) => updateLandingSection('hero', {...landingPageContent.hero, primary_cta_text: e.target.value})}
                                placeholder="Get Started Free"
                              />
                            </div>
                            <div>
                              <Label>Primary CTA Link</Label>
                              <Input
                                value={landingPageContent.hero?.primary_cta_link || ''}
                                onChange={(e) => updateLandingSection('hero', {...landingPageContent.hero, primary_cta_link: e.target.value})}
                                placeholder="/register"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>Secondary CTA Text</Label>
                              <Input
                                value={landingPageContent.hero?.secondary_cta_text || ''}
                                onChange={(e) => updateLandingSection('hero', {...landingPageContent.hero, secondary_cta_text: e.target.value})}
                                placeholder="Login"
                              />
                            </div>
                            <div>
                              <Label>Secondary CTA Link</Label>
                              <Input
                                value={landingPageContent.hero?.secondary_cta_link || ''}
                                onChange={(e) => updateLandingSection('hero', {...landingPageContent.hero, secondary_cta_link: e.target.value})}
                                placeholder="/login"
                              />
                            </div>
                          </div>
                          <div>
                            <Label>Banner Image URL</Label>
                            <Input
                              value={landingPageContent.hero?.banner_image || ''}
                              onChange={(e) => updateLandingSection('hero', {...landingPageContent.hero, banner_image: e.target.value})}
                              placeholder="https://..."
                            />
                            {landingPageContent.hero?.banner_image && (
                              <img src={landingPageContent.hero.banner_image} alt="Banner Preview" className="mt-2 rounded-lg h-32 w-full object-cover" />
                            )}
                          </div>
                          <div>
                            <Label>Background Gradient (CSS)</Label>
                            <Input
                              value={landingPageContent.hero?.background_gradient || ''}
                              onChange={(e) => updateLandingSection('hero', {...landingPageContent.hero, background_gradient: e.target.value})}
                              placeholder="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                            />
                          </div>
                        </div>
                      </Card>
                    </TabsContent>

                    {/* Features Section */}
                    <TabsContent value="features">
                      <Card className="p-6">
                        <h3 className="text-xl font-bold mb-4">Features Section</h3>
                        <div className="space-y-4 mb-6">
                          <div>
                            <Label>Section Title</Label>
                            <Input
                              value={landingPageContent.features?.section_title || ''}
                              onChange={(e) => updateLandingSection('features', {...landingPageContent.features, section_title: e.target.value})}
                              placeholder="Why Choose SkillEdge?"
                            />
                          </div>
                          <div>
                            <Label>Section Subtitle</Label>
                            <Input
                              value={landingPageContent.features?.section_subtitle || ''}
                              onChange={(e) => updateLandingSection('features', {...landingPageContent.features, section_subtitle: e.target.value})}
                              placeholder="Everything you need to make informed career decisions"
                            />
                          </div>
                        </div>
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-bold">Feature Cards</h4>
                          <Button onClick={addFeature} size="sm">
                            <Plus className="w-4 h-4 mr-2" />
                            Add Feature
                          </Button>
                        </div>
                        <div className="space-y-4">
                          {landingPageContent.features?.features?.map((feature, index) => (
                            <Card key={index} className="p-4 border-l-4 border-l-blue-500">
                              <div className="flex justify-between items-start mb-2">
                                <h4 className="font-semibold">Feature {index + 1}</h4>
                                <Button variant="ghost" size="sm" onClick={() => removeFeature(index)}>
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </Button>
                              </div>
                              <div className="space-y-3">
                                <div>
                                  <Label>Icon (Emoji or Lucide name)</Label>
                                  <Input
                                    value={feature.icon}
                                    onChange={(e) => {
                                      const updatedFeatures = [...landingPageContent.features.features];
                                      updatedFeatures[index].icon = e.target.value;
                                      updateLandingSection('features', {...landingPageContent.features, features: updatedFeatures});
                                    }}
                                    placeholder="🎯 or brain, book, users"
                                  />
                                </div>
                                <div>
                                  <Label>Title</Label>
                                  <Input
                                    value={feature.title}
                                    onChange={(e) => {
                                      const updatedFeatures = [...landingPageContent.features.features];
                                      updatedFeatures[index].title = e.target.value;
                                      updateLandingSection('features', {...landingPageContent.features, features: updatedFeatures});
                                    }}
                                  />
                                </div>
                                <div>
                                  <Label>Description</Label>
                                  <Textarea
                                    value={feature.description}
                                    onChange={(e) => {
                                      const updatedFeatures = [...landingPageContent.features.features];
                                      updatedFeatures[index].description = e.target.value;
                                      updateLandingSection('features', {...landingPageContent.features, features: updatedFeatures});
                                    }}
                                    rows={3}
                                  />
                                </div>
                                <div>
                                  <Label>Color (Hex)</Label>
                                  <div className="flex gap-2">
                                    <Input
                                      type="color"
                                      value={feature.color}
                                      onChange={(e) => {
                                        const updatedFeatures = [...landingPageContent.features.features];
                                        updatedFeatures[index].color = e.target.value;
                                        updateLandingSection('features', {...landingPageContent.features, features: updatedFeatures});
                                      }}
                                      className="w-20 h-10"
                                    />
                                    <Input
                                      value={feature.color}
                                      onChange={(e) => {
                                        const updatedFeatures = [...landingPageContent.features.features];
                                        updatedFeatures[index].color = e.target.value;
                                        updateLandingSection('features', {...landingPageContent.features, features: updatedFeatures});
                                      }}
                                      placeholder="#667eea"
                                    />
                                  </div>
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      </Card>
                    </TabsContent>

                    {/* How It Works Section */}
                    <TabsContent value="how-it-works">
                      <Card className="p-6">
                        <h3 className="text-xl font-bold mb-4">How It Works Section</h3>
                        <div className="space-y-4 mb-6">
                          <div>
                            <Label>Section Title</Label>
                            <Input
                              value={landingPageContent.how_it_works?.section_title || ''}
                              onChange={(e) => updateLandingSection('how_it_works', {...landingPageContent.how_it_works, section_title: e.target.value})}
                              placeholder="How It Works"
                            />
                          </div>
                          <div>
                            <Label>Section Subtitle</Label>
                            <Input
                              value={landingPageContent.how_it_works?.section_subtitle || ''}
                              onChange={(e) => updateLandingSection('how_it_works', {...landingPageContent.how_it_works, section_subtitle: e.target.value})}
                              placeholder="Simple steps to get started"
                            />
                          </div>
                        </div>
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-bold">Steps</h4>
                          <Button onClick={() => {
                            const nextStepNumber = (landingPageContent.how_it_works?.steps?.length || 0) + 1;
                            const newStep = { 
                              step_number: nextStepNumber, 
                              icon: "🎯", 
                              title: `Step ${nextStepNumber}`, 
                              description: "Step description",
                              color: "#667eea"
                            };
                            setLandingPageContent(prev => ({
                              ...prev,
                              how_it_works: {
                                ...prev.how_it_works,
                                section_title: prev.how_it_works?.section_title || "How It Works",
                                section_subtitle: prev.how_it_works?.section_subtitle || "Simple steps to get started",
                                steps: [...(prev.how_it_works?.steps || []), newStep]
                              }
                            }));
                          }} size="sm">
                            <Plus className="w-4 h-4 mr-2" />
                            Add Step
                          </Button>
                        </div>
                        <div className="space-y-4">
                          {landingPageContent.how_it_works?.steps?.map((step, index) => (
                            <Card key={index} className="p-4 border-l-4 border-l-green-500">
                              <div className="flex justify-between items-start mb-2">
                                <h4 className="font-semibold">Step {step.step_number}</h4>
                                <Button variant="ghost" size="sm" onClick={() => {
                                  setLandingPageContent(prev => ({
                                    ...prev,
                                    how_it_works: {
                                      ...prev.how_it_works,
                                      steps: prev.how_it_works.steps.filter((_, i) => i !== index)
                                    }
                                  }));
                                }}>
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </Button>
                              </div>
                              <div className="space-y-3">
                                <div>
                                  <Label>Step Number</Label>
                                  <Input
                                    type="number"
                                    value={step.step_number}
                                    onChange={(e) => {
                                      const updatedSteps = [...landingPageContent.how_it_works.steps];
                                      updatedSteps[index].step_number = parseInt(e.target.value);
                                      updateLandingSection('how_it_works', {...landingPageContent.how_it_works, steps: updatedSteps});
                                    }}
                                  />
                                </div>
                                <div>
                                  <Label>Icon (Emoji)</Label>
                                  <Input
                                    value={step.icon}
                                    onChange={(e) => {
                                      const updatedSteps = [...landingPageContent.how_it_works.steps];
                                      updatedSteps[index].icon = e.target.value;
                                      updateLandingSection('how_it_works', {...landingPageContent.how_it_works, steps: updatedSteps});
                                    }}
                                    placeholder="🎯"
                                  />
                                </div>
                                <div>
                                  <Label>Title</Label>
                                  <Input
                                    value={step.title}
                                    onChange={(e) => {
                                      const updatedSteps = [...landingPageContent.how_it_works.steps];
                                      updatedSteps[index].title = e.target.value;
                                      updateLandingSection('how_it_works', {...landingPageContent.how_it_works, steps: updatedSteps});
                                    }}
                                  />
                                </div>
                                <div>
                                  <Label>Description</Label>
                                  <Textarea
                                    value={step.description}
                                    onChange={(e) => {
                                      const updatedSteps = [...landingPageContent.how_it_works.steps];
                                      updatedSteps[index].description = e.target.value;
                                      updateLandingSection('how_it_works', {...landingPageContent.how_it_works, steps: updatedSteps});
                                    }}
                                    rows={3}
                                  />
                                </div>
                                <div>
                                  <Label>Color (Hex)</Label>
                                  <div className="flex gap-2">
                                    <Input
                                      type="color"
                                      value={step.color}
                                      onChange={(e) => {
                                        const updatedSteps = [...landingPageContent.how_it_works.steps];
                                        updatedSteps[index].color = e.target.value;
                                        updateLandingSection('how_it_works', {...landingPageContent.how_it_works, steps: updatedSteps});
                                      }}
                                      className="w-20 h-10"
                                    />
                                    <Input
                                      value={step.color}
                                      onChange={(e) => {
                                        const updatedSteps = [...landingPageContent.how_it_works.steps];
                                        updatedSteps[index].color = e.target.value;
                                        updateLandingSection('how_it_works', {...landingPageContent.how_it_works, steps: updatedSteps});
                                      }}
                                      placeholder="#667eea"
                                    />
                                  </div>
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      </Card>
                    </TabsContent>

                    {/* Why Choose Us Section */}
                    <TabsContent value="why-choose-us">
                      <Card className="p-6">
                        <h3 className="text-xl font-bold mb-4">Why Choose Us Section</h3>
                        <div className="space-y-4 mb-6">
                          <div>
                            <Label>Section Title</Label>
                            <Input
                              value={landingPageContent.why_choose_us?.section_title || ''}
                              onChange={(e) => updateLandingSection('why_choose_us', {...landingPageContent.why_choose_us, section_title: e.target.value})}
                              placeholder="Why Choose Us"
                            />
                          </div>
                          <div>
                            <Label>Section Subtitle</Label>
                            <Input
                              value={landingPageContent.why_choose_us?.section_subtitle || ''}
                              onChange={(e) => updateLandingSection('why_choose_us', {...landingPageContent.why_choose_us, section_subtitle: e.target.value})}
                              placeholder="Empowering your learning journey"
                            />
                          </div>
                        </div>
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-bold">Benefits</h4>
                          <Button onClick={() => {
                            const newBenefit = { icon: "💡", title: "New Benefit", description: "Benefit description" };
                            setLandingPageContent(prev => ({
                              ...prev,
                              why_choose_us: {
                                ...prev.why_choose_us,
                                benefits: [...(prev.why_choose_us?.benefits || []), newBenefit]
                              }
                            }));
                          }} size="sm">
                            <Plus className="w-4 h-4 mr-2" />
                            Add Benefit
                          </Button>
                        </div>
                        <div className="space-y-4">
                          {landingPageContent.why_choose_us?.benefits?.map((benefit, index) => (
                            <Card key={index} className="p-4">
                              <div className="flex justify-between items-start mb-2">
                                <h4 className="font-semibold">Benefit {index + 1}</h4>
                                <Button variant="ghost" size="sm" onClick={() => {
                                  setLandingPageContent(prev => ({
                                    ...prev,
                                    why_choose_us: {
                                      ...prev.why_choose_us,
                                      benefits: prev.why_choose_us.benefits.filter((_, i) => i !== index)
                                    }
                                  }));
                                }}>
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </Button>
                              </div>
                              <div className="space-y-3">
                                <div>
                                  <Label>Icon (Emoji)</Label>
                                  <Input
                                    value={benefit.icon}
                                    onChange={(e) => {
                                      const updated = [...landingPageContent.why_choose_us.benefits];
                                      updated[index].icon = e.target.value;
                                      updateLandingSection('why_choose_us', {...landingPageContent.why_choose_us, benefits: updated});
                                    }}
                                    placeholder="💡"
                                  />
                                </div>
                                <div>
                                  <Label>Title</Label>
                                  <Input
                                    value={benefit.title}
                                    onChange={(e) => {
                                      const updated = [...landingPageContent.why_choose_us.benefits];
                                      updated[index].title = e.target.value;
                                      updateLandingSection('why_choose_us', {...landingPageContent.why_choose_us, benefits: updated});
                                    }}
                                  />
                                </div>
                                <div>
                                  <Label>Description</Label>
                                  <Textarea
                                    value={benefit.description}
                                    onChange={(e) => {
                                      const updated = [...landingPageContent.why_choose_us.benefits];
                                      updated[index].description = e.target.value;
                                      updateLandingSection('why_choose_us', {...landingPageContent.why_choose_us, benefits: updated});
                                    }}
                                    rows={3}
                                  />
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      </Card>
                    </TabsContent>

                    {/* About Section */}
                    <TabsContent value="about">
                      <Card className="p-6">
                        <h3 className="text-xl font-bold mb-4">About Section</h3>
                        <div className="space-y-4">
                          <div>
                            <Label>Title</Label>
                            <Input
                              value={landingPageContent.about?.title || ''}
                              onChange={(e) => updateLandingSection('about', {...landingPageContent.about, title: e.target.value})}
                              placeholder="About SkillEdge"
                            />
                          </div>
                          <div>
                            <Label>Description</Label>
                            <Textarea
                              value={landingPageContent.about?.description || ''}
                              onChange={(e) => updateLandingSection('about', {...landingPageContent.about, description: e.target.value})}
                              rows={4}
                            />
                          </div>
                          <div>
                            <Label>Mission Statement</Label>
                            <Textarea
                              value={landingPageContent.about?.mission || ''}
                              onChange={(e) => updateLandingSection('about', {...landingPageContent.about, mission: e.target.value})}
                              rows={3}
                            />
                          </div>
                          <div>
                            <Label>Vision Statement</Label>
                            <Textarea
                              value={landingPageContent.about?.vision || ''}
                              onChange={(e) => updateLandingSection('about', {...landingPageContent.about, vision: e.target.value})}
                              rows={3}
                            />
                          </div>
                          <div>
                            <Label>Image URL</Label>
                            <Input
                              value={landingPageContent.about?.image || ''}
                              onChange={(e) => updateLandingSection('about', {...landingPageContent.about, image: e.target.value})}
                              placeholder="https://..."
                            />
                            {landingPageContent.about?.image && (
                              <img src={landingPageContent.about.image} alt="About" className="mt-2 rounded-lg h-48 w-full object-cover" />
                            )}
                          </div>
                        </div>
                      </Card>
                    </TabsContent>

                    {/* Pricing Section */}
                    <TabsContent value="pricing">
                      <Card className="p-6">
                        <h3 className="text-xl font-bold mb-4">Pricing Section</h3>
                        <div className="space-y-4 mb-6">
                          <div>
                            <Label>Section Title</Label>
                            <Input
                              value={landingPageContent.pricing?.section_title || ''}
                              onChange={(e) => updateLandingSection('pricing', {...landingPageContent.pricing, section_title: e.target.value})}
                              placeholder="Choose Your Plan"
                            />
                          </div>
                          <div>
                            <Label>Section Subtitle</Label>
                            <Input
                              value={landingPageContent.pricing?.section_subtitle || ''}
                              onChange={(e) => updateLandingSection('pricing', {...landingPageContent.pricing, section_subtitle: e.target.value})}
                              placeholder="Flexible pricing for everyone"
                            />
                          </div>
                        </div>
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-bold">Pricing Plans</h4>
                          <Button onClick={() => {
                            const newPlan = {
                              name: "New Plan",
                              price: "$0",
                              period: "month",
                              features: ["Feature 1", "Feature 2", "Feature 3"],
                              is_popular: false,
                              cta_text: "Get Started",
                              cta_link: "/register",
                              color: "#667eea"
                            };
                            setLandingPageContent(prev => ({
                              ...prev,
                              pricing: {
                                ...prev.pricing,
                                plans: [...(prev.pricing?.plans || []), newPlan]
                              }
                            }));
                          }} size="sm">
                            <Plus className="w-4 h-4 mr-2" />
                            Add Plan
                          </Button>
                        </div>
                        <div className="space-y-4">
                          {landingPageContent.pricing?.plans?.map((plan, index) => (
                            <Card key={index} className="p-4 border-l-4 border-l-purple-500">
                              <div className="flex justify-between items-start mb-2">
                                <h4 className="font-semibold">Plan {index + 1}</h4>
                                <Button variant="ghost" size="sm" onClick={() => {
                                  setLandingPageContent(prev => ({
                                    ...prev,
                                    pricing: {
                                      ...prev.pricing,
                                      plans: prev.pricing.plans.filter((_, i) => i !== index)
                                    }
                                  }));
                                }}>
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </Button>
                              </div>
                              <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <Label>Plan Name</Label>
                                    <Input
                                      value={plan.name}
                                      onChange={(e) => {
                                        const updated = [...landingPageContent.pricing.plans];
                                        updated[index].name = e.target.value;
                                        updateLandingSection('pricing', {...landingPageContent.pricing, plans: updated});
                                      }}
                                      placeholder="Basic"
                                    />
                                  </div>
                                  <div>
                                    <Label>Price</Label>
                                    <Input
                                      value={plan.price}
                                      onChange={(e) => {
                                        const updated = [...landingPageContent.pricing.plans];
                                        updated[index].price = e.target.value;
                                        updateLandingSection('pricing', {...landingPageContent.pricing, plans: updated});
                                      }}
                                      placeholder="$9.99"
                                    />
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <Label>Period</Label>
                                    <Input
                                      value={plan.period}
                                      onChange={(e) => {
                                        const updated = [...landingPageContent.pricing.plans];
                                        updated[index].period = e.target.value;
                                        updateLandingSection('pricing', {...landingPageContent.pricing, plans: updated});
                                      }}
                                      placeholder="month"
                                    />
                                  </div>
                                  <div>
                                    <Label>Color (Hex)</Label>
                                    <div className="flex gap-2">
                                      <Input
                                        type="color"
                                        value={plan.color}
                                        onChange={(e) => {
                                          const updated = [...landingPageContent.pricing.plans];
                                          updated[index].color = e.target.value;
                                          updateLandingSection('pricing', {...landingPageContent.pricing, plans: updated});
                                        }}
                                        className="w-16 h-9"
                                      />
                                      <Input
                                        value={plan.color}
                                        onChange={(e) => {
                                          const updated = [...landingPageContent.pricing.plans];
                                          updated[index].color = e.target.value;
                                          updateLandingSection('pricing', {...landingPageContent.pricing, plans: updated});
                                        }}
                                        placeholder="#667eea"
                                      />
                                    </div>
                                  </div>
                                </div>
                                <div>
                                  <Label>Features (one per line)</Label>
                                  <Textarea
                                    value={plan.features?.join('\n') || ''}
                                    onChange={(e) => {
                                      const updated = [...landingPageContent.pricing.plans];
                                      updated[index].features = e.target.value.split('\n').filter(f => f.trim());
                                      updateLandingSection('pricing', {...landingPageContent.pricing, plans: updated});
                                    }}
                                    rows={4}
                                    placeholder="Feature 1&#10;Feature 2&#10;Feature 3"
                                  />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <Label>CTA Button Text</Label>
                                    <Input
                                      value={plan.cta_text}
                                      onChange={(e) => {
                                        const updated = [...landingPageContent.pricing.plans];
                                        updated[index].cta_text = e.target.value;
                                        updateLandingSection('pricing', {...landingPageContent.pricing, plans: updated});
                                      }}
                                      placeholder="Get Started"
                                    />
                                  </div>
                                  <div>
                                    <Label>CTA Link</Label>
                                    <Input
                                      value={plan.cta_link}
                                      onChange={(e) => {
                                        const updated = [...landingPageContent.pricing.plans];
                                        updated[index].cta_link = e.target.value;
                                        updateLandingSection('pricing', {...landingPageContent.pricing, plans: updated});
                                      }}
                                      placeholder="/register"
                                    />
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={plan.is_popular || false}
                                    onChange={(e) => {
                                      const updated = [...landingPageContent.pricing.plans];
                                      updated[index].is_popular = e.target.checked;
                                      updateLandingSection('pricing', {...landingPageContent.pricing, plans: updated});
                                    }}
                                    className="w-4 h-4"
                                  />
                                  <Label>Mark as Popular (adds badge)</Label>
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      </Card>
                    </TabsContent>

                    {/* Stats Section */}
                    <TabsContent value="stats">
                      <Card className="p-6">
                        <h3 className="text-xl font-bold mb-4">Statistics Section</h3>
                        <div className="space-y-4 mb-6">
                          <div>
                            <Label>Section Title</Label>
                            <Input
                              value={landingPageContent.stats?.section_title || ''}
                              onChange={(e) => updateLandingSection('stats', {...landingPageContent.stats, section_title: e.target.value})}
                              placeholder="Trusted by Thousands"
                            />
                          </div>
                          <div>
                            <Label>Background Color (Hex)</Label>
                            <div className="flex gap-2">
                              <Input
                                type="color"
                                value={landingPageContent.stats?.background_color || '#667eea'}
                                onChange={(e) => updateLandingSection('stats', {...landingPageContent.stats, background_color: e.target.value})}
                                className="w-20 h-10"
                              />
                              <Input
                                value={landingPageContent.stats?.background_color || ''}
                                onChange={(e) => updateLandingSection('stats', {...landingPageContent.stats, background_color: e.target.value})}
                                placeholder="#667eea"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-bold">Statistics</h4>
                          <Button onClick={() => {
                            const newStat = { label: "New Stat", value: "100+", icon: "⭐" };
                            setLandingPageContent(prev => ({
                              ...prev,
                              stats: {
                                ...prev.stats,
                                stats: [...(prev.stats?.stats || []), newStat]
                              }
                            }));
                          }} size="sm">
                            <Plus className="w-4 h-4 mr-2" />
                            Add Stat
                          </Button>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                          {landingPageContent.stats?.stats?.map((stat, index) => (
                            <Card key={index} className="p-4">
                              <div className="flex justify-between items-start mb-2">
                                <h4 className="font-semibold">Stat {index + 1}</h4>
                                <Button variant="ghost" size="sm" onClick={() => {
                                  setLandingPageContent(prev => ({
                                    ...prev,
                                    stats: {
                                      ...prev.stats,
                                      stats: prev.stats.stats.filter((_, i) => i !== index)
                                    }
                                  }));
                                }}>
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </Button>
                              </div>
                              <div className="space-y-3">
                                <div>
                                  <Label>Icon (Emoji)</Label>
                                  <Input
                                    value={stat.icon}
                                    onChange={(e) => {
                                      const updated = [...landingPageContent.stats.stats];
                                      updated[index].icon = e.target.value;
                                      updateLandingSection('stats', {...landingPageContent.stats, stats: updated});
                                    }}
                                    placeholder="👥"
                                  />
                                </div>
                                <div>
                                  <Label>Value</Label>
                                  <Input
                                    value={stat.value}
                                    onChange={(e) => {
                                      const updated = [...landingPageContent.stats.stats];
                                      updated[index].value = e.target.value;
                                      updateLandingSection('stats', {...landingPageContent.stats, stats: updated});
                                    }}
                                    placeholder="50,000+"
                                  />
                                </div>
                                <div>
                                  <Label>Label</Label>
                                  <Input
                                    value={stat.label}
                                    onChange={(e) => {
                                      const updated = [...landingPageContent.stats.stats];
                                      updated[index].label = e.target.value;
                                      updateLandingSection('stats', {...landingPageContent.stats, stats: updated});
                                    }}
                                    placeholder="Active Users"
                                  />
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      </Card>
                    </TabsContent>

                    {/* Testimonials Section */}
                    <TabsContent value="testimonials">
                      <Card className="p-6">
                        <h3 className="text-xl font-bold mb-4">Testimonials Section</h3>
                        <div className="space-y-4 mb-6">
                          <div>
                            <Label>Section Title</Label>
                            <Input
                              value={landingPageContent.testimonials?.section_title || ''}
                              onChange={(e) => updateLandingSection('testimonials', {...landingPageContent.testimonials, section_title: e.target.value})}
                              placeholder="What Our Users Say"
                            />
                          </div>
                        </div>
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-bold">Testimonials</h4>
                          <Button onClick={addTestimonial} size="sm">
                            <Plus className="w-4 h-4 mr-2" />
                            Add Testimonial
                          </Button>
                        </div>
                        <div className="space-y-4">
                          {landingPageContent.testimonials?.testimonials?.map((testimonial, index) => (
                            <Card key={index} className="p-4 border-l-4 border-l-green-500">
                              <div className="flex justify-between items-start mb-2">
                                <h4 className="font-semibold">Testimonial {index + 1}</h4>
                                <Button variant="ghost" size="sm" onClick={() => removeTestimonial(index)}>
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </Button>
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label>Name</Label>
                                  <Input
                                    value={testimonial.name}
                                    onChange={(e) => {
                                      const updated = [...landingPageContent.testimonials.testimonials];
                                      updated[index].name = e.target.value;
                                      updateLandingSection('testimonials', {...landingPageContent.testimonials, testimonials: updated});
                                    }}
                                  />
                                </div>
                                <div>
                                  <Label>Role/Title</Label>
                                  <Input
                                    value={testimonial.role}
                                    onChange={(e) => {
                                      const updated = [...landingPageContent.testimonials.testimonials];
                                      updated[index].role = e.target.value;
                                      updateLandingSection('testimonials', {...landingPageContent.testimonials, testimonials: updated});
                                    }}
                                  />
                                </div>
                                <div className="col-span-2">
                                  <Label>Content</Label>
                                  <Textarea
                                    value={testimonial.content}
                                    onChange={(e) => {
                                      const updated = [...landingPageContent.testimonials.testimonials];
                                      updated[index].content = e.target.value;
                                      updateLandingSection('testimonials', {...landingPageContent.testimonials, testimonials: updated});
                                    }}
                                    rows={3}
                                  />
                                </div>
                                <div>
                                  <Label>Avatar URL</Label>
                                  <Input
                                    value={testimonial.avatar}
                                    onChange={(e) => {
                                      const updated = [...landingPageContent.testimonials.testimonials];
                                      updated[index].avatar = e.target.value;
                                      updateLandingSection('testimonials', {...landingPageContent.testimonials, testimonials: updated});
                                    }}
                                    placeholder="https://i.pravatar.cc/150"
                                  />
                                </div>
                                <div>
                                  <Label>Rating (1-5)</Label>
                                  <Input
                                    type="number"
                                    min="1"
                                    max="5"
                                    value={testimonial.rating}
                                    onChange={(e) => {
                                      const updated = [...landingPageContent.testimonials.testimonials];
                                      updated[index].rating = parseInt(e.target.value);
                                      updateLandingSection('testimonials', {...landingPageContent.testimonials, testimonials: updated});
                                    }}
                                  />
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      </Card>
                    </TabsContent>

                    {/* FAQ Section */}
                    {/* Footer Section */}
                    <TabsContent value="footer">
                      <Card className="p-6">
                        <h3 className="text-xl font-bold mb-4">Footer Section</h3>
                        <div className="space-y-4">
                          <div>
                            <Label>About Text</Label>
                            <Textarea
                              value={landingPageContent.footer?.about_text || ''}
                              onChange={(e) => updateLandingSection('footer', {...landingPageContent.footer, about_text: e.target.value})}
                              rows={3}
                              placeholder="Brief description of your platform"
                            />
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <Label>Contact Email</Label>
                              <Input
                                value={landingPageContent.footer?.contact_email || ''}
                                onChange={(e) => updateLandingSection('footer', {...landingPageContent.footer, contact_email: e.target.value})}
                                placeholder="support@skilledge.com"
                              />
                            </div>
                            <div>
                              <Label>Contact Phone</Label>
                              <Input
                                value={landingPageContent.footer?.contact_phone || ''}
                                onChange={(e) => updateLandingSection('footer', {...landingPageContent.footer, contact_phone: e.target.value})}
                                placeholder="+91-9876543210"
                              />
                            </div>
                          </div>
                          <div>
                            <Label>Copyright Text</Label>
                            <Input
                              value={landingPageContent.footer?.copyright_text || ''}
                              onChange={(e) => updateLandingSection('footer', {...landingPageContent.footer, copyright_text: e.target.value})}
                              placeholder="© 2025 SkillEdge. All rights reserved."
                            />
                          </div>
                          <div>
                            <Label className="mb-2 block">Social Media Links</Label>
                            <div className="grid md:grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs">Facebook</Label>
                                <Input
                                  value={landingPageContent.footer?.social_links?.facebook || ''}
                                  onChange={(e) => updateLandingSection('footer', {
                                    ...landingPageContent.footer,
                                    social_links: {...(landingPageContent.footer?.social_links || {}), facebook: e.target.value}
                                  })}
                                  placeholder="https://facebook.com/..."
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Twitter</Label>
                                <Input
                                  value={landingPageContent.footer?.social_links?.twitter || ''}
                                  onChange={(e) => updateLandingSection('footer', {
                                    ...landingPageContent.footer,
                                    social_links: {...(landingPageContent.footer?.social_links || {}), twitter: e.target.value}
                                  })}
                                  placeholder="https://twitter.com/..."
                                />
                              </div>
                              <div>
                                <Label className="text-xs">LinkedIn</Label>
                                <Input
                                  value={landingPageContent.footer?.social_links?.linkedin || ''}
                                  onChange={(e) => updateLandingSection('footer', {
                                    ...landingPageContent.footer,
                                    social_links: {...(landingPageContent.footer?.social_links || {}), linkedin: e.target.value}
                                  })}
                                  placeholder="https://linkedin.com/..."
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Instagram</Label>
                                <Input
                                  value={landingPageContent.footer?.social_links?.instagram || ''}
                                  onChange={(e) => updateLandingSection('footer', {
                                    ...landingPageContent.footer,
                                    social_links: {...(landingPageContent.footer?.social_links || {}), instagram: e.target.value}
                                  })}
                                  placeholder="https://instagram.com/..."
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </TabsContent>
                  </Tabs>
                )}
              </TabsContent>

              {/* SEO Settings Tab */}
              <TabsContent value="seo" className="space-y-6">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-bold">SEO Settings</h2>
                    <p className="text-gray-600">Manage meta tags for all pages to improve search visibility</p>
                  </div>
                  <Button onClick={initializeSeoSettings} variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    Initialize Default SEO
                  </Button>
                </div>

                {seoSettings.length === 0 && (
                  <Card className="p-8 text-center">
                    <h3 className="text-lg font-semibold mb-2">No SEO Settings Found</h3>
                    <p className="text-gray-600 mb-4">Initialize default SEO settings for all pages</p>
                    <Button onClick={initializeSeoSettings}>
                      <Plus className="w-4 h-4 mr-2" />
                      Initialize SEO Settings
                    </Button>
                  </Card>
                )}

                {seoSettings.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[20%]">Page</TableHead>
                          <TableHead className="w-[30%]">Meta Title</TableHead>
                          <TableHead className="w-[35%]">Meta Description</TableHead>
                          <TableHead className="w-[10%]">Status</TableHead>
                          <TableHead className="w-[5%]">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {seoSettings.map((seo) => (
                          <TableRow key={seo.page_name}>
                            <TableCell className="font-medium">{seo.page_title}</TableCell>
                            <TableCell className="text-sm">{seo.meta_title}</TableCell>
                            <TableCell className="text-sm text-gray-600">
                              {seo.meta_description.substring(0, 80)}...
                            </TableCell>
                            <TableCell>
                              <Badge variant={seo.is_active ? "default" : "secondary"}>
                                {seo.is_active ? "Active" : "Inactive"}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditSeo(seo)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* SEO Edit Dialog */}
                <Dialog open={isSeoDialogOpen} onOpenChange={setIsSeoDialogOpen}>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Edit SEO Settings - {seoFormData.page_title}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label>Meta Title *</Label>
                        <Input
                          value={seoFormData.meta_title}
                          onChange={(e) => setSeoFormData({...seoFormData, meta_title: e.target.value})}
                          placeholder="SkillEdge - AI-Powered Career Guidance"
                          maxLength={60}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {seoFormData.meta_title.length}/60 characters (recommended: 50-60)
                        </p>
                      </div>
                      <div>
                        <Label>Meta Description *</Label>
                        <Textarea
                          rows={3}
                          value={seoFormData.meta_description}
                          onChange={(e) => setSeoFormData({...seoFormData, meta_description: e.target.value})}
                          placeholder="Discover your dream career with personalized guidance..."
                          maxLength={160}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {seoFormData.meta_description.length}/160 characters (recommended: 150-160)
                        </p>
                      </div>
                      <div>
                        <Label>Meta Keywords</Label>
                        <Input
                          value={seoFormData.meta_keywords}
                          onChange={(e) => setSeoFormData({...seoFormData, meta_keywords: e.target.value})}
                          placeholder="career guidance, AI assessment, career counseling"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Comma-separated keywords (optional but helpful)
                        </p>
                      </div>
                      <div>
                        <Label>Robots</Label>
                        <Select 
                          value={seoFormData.robots} 
                          onValueChange={(val) => setSeoFormData({...seoFormData, robots: val})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="index, follow">Index, Follow (Default)</SelectItem>
                            <SelectItem value="noindex, follow">No Index, Follow</SelectItem>
                            <SelectItem value="index, nofollow">Index, No Follow</SelectItem>
                            <SelectItem value="noindex, nofollow">No Index, No Follow</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500 mt-1">
                          Controls how search engines index this page
                        </p>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setIsSeoDialogOpen(false)}>Cancel</Button>
                      <Button onClick={handleSaveSeo}>Save Changes</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </TabsContent>

              <TabsContent value="payments">
                <Tabs defaultValue="gateways">
                  <TabsList className="mb-4">
                    <TabsTrigger value="gateways">Payment Gateways</TabsTrigger>
                    <TabsTrigger value="transactions">Transactions</TabsTrigger>
                  </TabsList>

                  <TabsContent value="gateways">
                    <PaymentGatewaySettings API={API} token={token} />
                  </TabsContent>

                  <TabsContent value="transactions">
                    <PaymentTransactionsViewer API={API} token={token} />
                  </TabsContent>
                </Tabs>
              </TabsContent>

              {/* Content Moderation Tab */}
              <TabsContent value="moderation">
                <ContentModerationDashboard API={API} token={token} />
              </TabsContent>

              {/* Consultations Tab */}
              <TabsContent value="consultations">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-semibold">Consultation Bookings</h3>
                    <Button onClick={() => window.location.href = '/admin/consultations'}>
                      <Video className="w-4 h-4 mr-2" />
                      Manage Consultations
                    </Button>
                  </div>
                  <Card>
                    <CardContent className="py-6">
                      <div className="text-center">
                        <Video className="w-12 h-12 mx-auto mb-3 text-blue-600" />
                        <h4 className="text-lg font-semibold mb-2">Live Video Consultation Management</h4>
                        <p className="text-gray-600 mb-4">
                          Manage all consultation bookings, add video meeting links (Zoom/Google Meet/Teams), 
                          assign counselors, update status, and track payments.
                        </p>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 text-left">
                          <h5 className="font-semibold text-blue-900 mb-2">✨ Features:</h5>
                          <ul className="text-sm text-blue-800 space-y-1">
                            <li>• Add Zoom/Meet/Teams meeting links to bookings</li>
                            <li>• Update consultation status (pending → confirmed → completed)</li>
                            <li>• Assign counselors to consultations</li>
                            <li>• Add admin notes for internal tracking</li>
                            <li>• Students automatically see meeting links once confirmed</li>
                          </ul>
                        </div>
                        <Button onClick={() => window.location.href = '/admin/consultations'} size="lg">
                          Go to Consultation Management
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Currency Settings Tab */}
              <TabsContent value="currency">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-2xl font-bold">Currency & Country Management</h3>
                    <Button onClick={async () => {
                      try {
                        const response = await axios.post(`${API}/admin/initialize-currency-data`, {}, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        toast.success(`Initialized: ${response.data.currencies_added} currencies, ${response.data.countries_added} countries`);
                        fetchAllData();
                      } catch (error) {
                        toast.error("Failed to initialize data");
                      }
                    }}>
                      <Database className="w-4 h-4 mr-2" />
                      Initialize Data
                    </Button>
                  </div>

                  {/* Currencies Section */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle>Currencies ({currencies.length})</CardTitle>
                          {currencySettings?.base_currency && (
                            <p className="text-sm text-gray-600 mt-1">
                              Primary Currency: <span className="font-mono font-bold text-indigo-600">{currencySettings.base_currency}</span>
                            </p>
                          )}
                        </div>
                        <Button size="sm" onClick={() => setModals(prev => ({...prev, currency: true}))}>
                          <Plus className="w-4 h-4 mr-2" />
                          Add Currency
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {currencies.length > 0 ? (
                        <div className="rounded-md border max-h-96 overflow-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Primary</TableHead>
                                <TableHead>Code</TableHead>
                                <TableHead>Symbol</TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>Decimals</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {currencies.slice(0, 50).map((curr) => (
                                <TableRow key={curr.id}>
                                  <TableCell>
                                    {currencySettings?.base_currency === curr.code ? (
                                      <Award className="w-5 h-5 text-yellow-500" title="Primary Currency" />
                                    ) : (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={async () => {
                                          try {
                                            await axios.patch(`${API}/admin/currency-settings`, {
                                              base_currency: curr.code
                                            }, {
                                              headers: { Authorization: `Bearer ${token}` }
                                            });
                                            toast.success(`${curr.code} set as primary currency`);
                                            fetchAllData();
                                          } catch (error) {
                                            toast.error("Failed to set primary currency");
                                          }
                                        }}
                                        title="Set as Primary"
                                      >
                                        <Award className="w-4 h-4 text-gray-400" />
                                      </Button>
                                    )}
                                  </TableCell>
                                  <TableCell className="font-mono font-bold">{curr.code}</TableCell>
                                  <TableCell className="text-lg">{curr.symbol}</TableCell>
                                  <TableCell>{curr.name}</TableCell>
                                  <TableCell>{curr.decimal_places}</TableCell>
                                  <TableCell>
                                    {curr.is_active ? (
                                      <Badge className="bg-green-100 text-green-700">Active</Badge>
                                    ) : (
                                      <Badge variant="secondary">Inactive</Badge>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex gap-2">
                                      <Button size="sm" variant="outline" onClick={() => openModal('currency', curr)}>
                                        <Edit className="w-3 h-3" />
                                      </Button>
                                      <Button size="sm" variant="destructive" onClick={() => deleteEntity('currencies', curr.id)}>
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      ) : (
                        <div className="text-center text-gray-500 py-8">
                          <p>No currencies found. Click "Initialize Data" to load world currencies.</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Countries Section */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle>Countries ({countries.length})</CardTitle>
                        <Button size="sm" onClick={() => setModals(prev => ({...prev, country: true}))}>
                          <Plus className="w-4 h-4 mr-2" />
                          Add Country
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {countries.length > 0 ? (
                        <div className="rounded-md border max-h-96 overflow-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Country</TableHead>
                                <TableHead>Code</TableHead>
                                <TableHead>Currency</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {countries.slice(0, 50).map((country) => (
                                <TableRow key={country.id}>
                                  <TableCell className="font-medium">{country.name}</TableCell>
                                  <TableCell className="font-mono">{country.code}</TableCell>
                                  <TableCell className="font-mono font-bold">{country.currency_code}</TableCell>
                                  <TableCell>
                                    {country.is_active ? (
                                      <Badge className="bg-green-100 text-green-700">Active</Badge>
                                    ) : (
                                      <Badge variant="secondary">Inactive</Badge>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex gap-2">
                                      <Button size="sm" variant="outline" onClick={() => openModal('country', country)}>
                                        <Edit className="w-3 h-3" />
                                      </Button>
                                      <Button size="sm" variant="destructive" onClick={() => deleteEntity('countries', country.id)}>
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      ) : (
                        <div className="text-center text-gray-500 py-8">
                          <p>No countries found. Click "Initialize Data" to load world countries.</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Exchange Rates Section */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle>Exchange Rates ({exchangeRates.length})</CardTitle>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={async () => {
                            try {
                              const response = await axios.post(`${API}/admin/exchange-rates/auto-fetch`, {}, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              toast.success(`Updated ${response.data.rates_updated} exchange rates`);
                              fetchAllData();
                            } catch (error) {
                              toast.error(error.response?.data?.detail || "Failed to fetch rates. Check API key in settings.");
                            }
                          }}>
                            <TrendingUp className="w-4 h-4 mr-2" />
                            Auto-Fetch Rates
                          </Button>
                          <Button size="sm" onClick={() => setModals(prev => ({...prev, exchangeRate: true}))}>
                            <Plus className="w-4 h-4 mr-2" />
                            Add Rate
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {exchangeRates.length > 0 ? (
                        <div className="rounded-md border max-h-96 overflow-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>From</TableHead>
                                <TableHead>To</TableHead>
                                <TableHead>Rate</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Updated</TableHead>
                                <TableHead>Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {exchangeRates.slice(0, 50).map((rate) => (
                                <TableRow key={rate.id}>
                                  <TableCell className="font-mono font-bold">{rate.from_currency}</TableCell>
                                  <TableCell className="font-mono font-bold">{rate.to_currency}</TableCell>
                                  <TableCell className="font-mono">{rate.rate.toFixed(4)}</TableCell>
                                  <TableCell>
                                    {rate.is_manual ? (
                                      <Badge variant="outline">Manual</Badge>
                                    ) : (
                                      <Badge className="bg-blue-100 text-blue-700">Auto</Badge>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-xs text-gray-500">
                                    {new Date(rate.updated_at).toLocaleDateString()}
                                  </TableCell>
                                  <TableCell>
                                    <Button size="sm" variant="destructive" onClick={() => deleteEntity('exchange-rates', rate.id)}>
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      ) : (
                        <div className="text-center text-gray-500 py-8">
                          <p>No exchange rates found. Click "Auto-Fetch Rates" to load rates from API.</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Currency Settings */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Currency Settings</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div>
                          <Label>Base Currency (for conversions)</Label>
                          <Input 
                            value={currencySettings?.base_currency || "USD"} 
                            onChange={(e) => setCurrencySettings({...currencySettings, base_currency: e.target.value})}
                            className="max-w-xs" 
                            placeholder="USD"
                          />
                        </div>
                        <div>
                          <Label>Exchange Rate API Key (exchangerate-api.com)</Label>
                          <Input 
                            value={currencySettings?.exchange_rate_api_key || ""} 
                            onChange={(e) => setCurrencySettings({...currencySettings, exchange_rate_api_key: e.target.value})}
                            className="max-w-md" 
                            placeholder="Enter API key or leave as PLACEHOLDER_API_KEY"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Get your free API key from <a href="https://www.exchangerate-api.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">exchangerate-api.com</a>
                          </p>
                        </div>
                        <Button onClick={async () => {
                          try {
                            await axios.patch(`${API}/admin/currency-settings`, {
                              base_currency: currencySettings?.base_currency,
                              exchange_rate_api_key: currencySettings?.exchange_rate_api_key
                            }, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            toast.success("Currency settings updated");
                          } catch (error) {
                            toast.error("Failed to update settings");
                          }
                        }}>Save Settings</Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Languages Tab */}
              <TabsContent value="languages">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-2xl font-bold">Language Management</h3>
                    <div className="flex gap-2">
                      <Button onClick={async () => {
                        try {
                          const response = await axios.post(`${API}/admin/initialize-languages`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          toast.success(`Initialized ${response.data.languages_added} languages`);
                          fetchAllData();
                        } catch (error) {
                          toast.error("Failed to initialize languages");
                        }
                      }} variant="outline">
                        <Database className="w-4 h-4 mr-2" />
                        Initialize Languages
                      </Button>
                      <Button onClick={() => setModals(prev => ({...prev, language: true}))}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Language
                      </Button>
                    </div>
                  </div>

                  {/* Languages Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Available Languages ({(languages || []).length})</CardTitle>
                      <CardDescription>Manage languages available in the application</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {(languages || []).length > 0 ? (
                        <div className="rounded-md border">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Default</TableHead>
                                <TableHead>Code</TableHead>
                                <TableHead>English Name</TableHead>
                                <TableHead>Native Name</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {(languages || []).map((lang) => (
                                <TableRow key={lang.id}>
                                  <TableCell>
                                    {lang.is_default ? (
                                      <Award className="w-5 h-5 text-yellow-500" title="Default Language" />
                                    ) : (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={async () => {
                                          try {
                                            await axios.patch(`${API}/admin/languages/${lang.id}`, {
                                              is_default: true
                                            }, {
                                              headers: { Authorization: `Bearer ${token}` }
                                            });
                                            toast.success(`${lang.name} set as default language`);
                                            fetchAllData();
                                          } catch (error) {
                                            toast.error("Failed to set default language");
                                          }
                                        }}
                                        title="Set as Default"
                                      >
                                        <Award className="w-4 h-4 text-gray-400" />
                                      </Button>
                                    )}
                                  </TableCell>
                                  <TableCell className="font-mono font-bold uppercase">{lang.code}</TableCell>
                                  <TableCell>{lang.name}</TableCell>
                                  <TableCell className="text-lg">{lang.native_name}</TableCell>
                                  <TableCell>
                                    {lang.is_active ? (
                                      <Badge className="bg-green-100 text-green-700">Active</Badge>
                                    ) : (
                                      <Badge variant="secondary">Inactive</Badge>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex gap-2">
                                      <Button size="sm" variant="outline" onClick={() => openModal('language', lang)}>
                                        <Edit className="w-3 h-3" />
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant="destructive" 
                                        onClick={() => deleteEntity('languages', lang.id)}
                                        disabled={lang.is_default}
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      ) : (
                        <div className="text-center text-gray-500 py-8">
                          <p>No languages found. Click "Initialize Languages" to load default languages.</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Translation Management Info */}
                  <Card className="bg-blue-50 border-blue-200">
                    <CardHeader>
                      <CardTitle className="text-blue-900">Content Translation</CardTitle>
                      <CardDescription className="text-blue-700">
                        Translate careers, scholarships, exams, and other content into multiple languages
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-blue-800 mb-4">
                        To translate content, go to the specific content tab (e.g., Scholarships, Exams) and use the "Translate" button next to each item.
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <Badge variant="outline" className="justify-center py-2">Careers</Badge>
                        <Badge variant="outline" className="justify-center py-2">Scholarships</Badge>
                        <Badge variant="outline" className="justify-center py-2">Exams</Badge>
                        <Badge variant="outline" className="justify-center py-2">Resources</Badge>
                        <Badge variant="outline" className="justify-center py-2">Events</Badge>
                        <Badge variant="outline" className="justify-center py-2">Mentors</Badge>
                        <Badge variant="outline" className="justify-center py-2">Colleges</Badge>
                        <Badge variant="outline" className="justify-center py-2">Quizzes</Badge>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Email Settings Tab */}
              <TabsContent value="email">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-2xl font-bold">Email Configuration</h3>
                    <Badge className={emailSettings.enabled ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                      {emailSettings.enabled ? "Enabled" : "Disabled"}
                    </Badge>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle>SMTP Settings</CardTitle>
                      <CardDescription>Configure email server settings for sending notifications</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="email_host">SMTP Host</Label>
                          <Input
                            id="email_host"
                            placeholder="smtp.gmail.com"
                            value={emailSettings.host}
                            onChange={(e) => setEmailSettings(prev => ({...prev, host: e.target.value}))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email_port">SMTP Port</Label>
                          <Input
                            id="email_port"
                            type="number"
                            placeholder="587"
                            value={emailSettings.port}
                            onChange={(e) => setEmailSettings(prev => ({...prev, port: parseInt(e.target.value)}))}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="email_username">Username / Email</Label>
                          <Input
                            id="email_username"
                            placeholder="your-email@gmail.com"
                            value={emailSettings.username}
                            onChange={(e) => setEmailSettings(prev => ({...prev, username: e.target.value}))}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email_password">Password / App Password</Label>
                          <Input
                            id="email_password"
                            type="password"
                            placeholder="••••••••"
                            value={emailSettings.password}
                            onChange={(e) => setEmailSettings(prev => ({...prev, password: e.target.value}))}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="from_email">From Email Address</Label>
                        <Input
                          id="from_email"
                          placeholder="SkillEdge <noreply@skilledge.com>"
                          value={emailSettings.from_email}
                          onChange={(e) => setEmailSettings(prev => ({...prev, from_email: e.target.value}))}
                        />
                        <p className="text-sm text-gray-500">This will appear as the sender in user emails</p>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t">
                        <div className="space-y-1">
                          <h4 className="text-sm font-medium">Enable Email Notifications</h4>
                          <p className="text-sm text-gray-500">Turn on to start sending emails to users</p>
                        </div>
                        <Button
                          variant={emailSettings.enabled ? "destructive" : "default"}
                          onClick={() => setEmailSettings(prev => ({...prev, enabled: !prev.enabled}))}
                        >
                          {emailSettings.enabled ? (
                            <><Unlock className="w-4 h-4 mr-2" />Disable Emails</>
                          ) : (
                            <><Lock className="w-4 h-4 mr-2" />Enable Emails</>
                          )}
                        </Button>
                      </div>

                      <div className="flex gap-2 pt-4">
                        <Button 
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/admin/email-settings`, emailSettings, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              toast.success("Email settings saved successfully!");
                            } catch (error) {
                              toast.error("Failed to save email settings");
                            }
                          }}
                          className="flex-1"
                        >
                          Save Settings
                        </Button>
                        <Button 
                          variant="outline"
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/admin/test-email`, { 
                                to_email: user.email 
                              }, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              toast.success(`Test email sent to ${user.email}!`);
                            } catch (error) {
                              toast.error("Failed to send test email");
                            }
                          }}
                        >
                          Send Test Email
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Email Templates Info */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Email Templates</CardTitle>
                      <CardDescription>Emails are automatically sent for these events</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                          <div>
                            <h4 className="font-medium">Welcome Email</h4>
                            <p className="text-sm text-gray-600">Sent when a new user registers</p>
                          </div>
                          <Badge className="bg-blue-100 text-blue-700">Active</Badge>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                          <div>
                            <h4 className="font-medium">Assessment Completion</h4>
                            <p className="text-sm text-gray-600">Sent when user completes an assessment</p>
                          </div>
                          <Badge className="bg-green-100 text-green-700">Active</Badge>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                          <div>
                            <h4 className="font-medium">Consultation Booking</h4>
                            <p className="text-sm text-gray-600">Sent when user books a consultation</p>
                          </div>
                          <Badge className="bg-purple-100 text-purple-700">Coming Soon</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Email Stats */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Common SMTP Providers</CardTitle>
                      <CardDescription>Quick setup guides for popular email providers</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 border rounded-lg">
                          <h4 className="font-medium mb-2">Gmail</h4>
                          <ul className="text-sm text-gray-600 space-y-1">
                            <li>• Host: smtp.gmail.com</li>
                            <li>• Port: 587</li>
                            <li>• Use App Password (not regular password)</li>
                          </ul>
                        </div>
                        <div className="p-4 border rounded-lg">
                          <h4 className="font-medium mb-2">SendGrid</h4>
                          <ul className="text-sm text-gray-600 space-y-1">
                            <li>• Host: smtp.sendgrid.net</li>
                            <li>• Port: 587</li>
                            <li>• Username: apikey</li>
                          </ul>
                        </div>
                        <div className="p-4 border rounded-lg">
                          <h4 className="font-medium mb-2">Mailgun</h4>
                          <ul className="text-sm text-gray-600 space-y-1">
                            <li>• Host: smtp.mailgun.org</li>
                            <li>• Port: 587</li>
                            <li>• Use your Mailgun credentials</li>
                          </ul>
                        </div>
                        <div className="p-4 border rounded-lg">
                          <h4 className="font-medium mb-2">AWS SES</h4>
                          <ul className="text-sm text-gray-600 space-y-1">
                            <li>• Host: email-smtp.[region].amazonaws.com</li>
                            <li>• Port: 587</li>
                            <li>• Use SMTP credentials from SES</li>
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Gamification Tab */}
              <TabsContent value="gamification">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-2xl font-bold">Gamification Management</h3>
                      <p className="text-gray-600 mt-1">Manage badges, rewards, and student achievements</p>
                    </div>
                    <Button onClick={() => navigate('/admin/gamification')} className="gap-2">
                      <Trophy className="w-4 h-4" />
                      Open Full Panel
                    </Button>
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="border-l-4 border-l-yellow-500">
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Trophy className="w-5 h-5 text-yellow-500" />
                          Total Badges
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold">{badges.length}</div>
                        <p className="text-sm text-gray-600 mt-1">Available badges to earn</p>
                      </CardContent>
                    </Card>

                    <Card className="border-l-4 border-l-purple-500">
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Gift className="w-5 h-5 text-purple-500" />
                          Active Rewards
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold">{rewards.length}</div>
                        <p className="text-sm text-gray-600 mt-1">Rewards in store</p>
                      </CardContent>
                    </Card>

                    <Card className="border-l-4 border-l-blue-500">
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Users className="w-5 h-5 text-blue-500" />
                          Active Users
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold">{users.length}</div>
                        <p className="text-sm text-gray-600 mt-1">Students in system</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Quick Actions */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Quick Actions</CardTitle>
                      <CardDescription>Common gamification tasks</CardDescription>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Button 
                        variant="outline" 
                        className="h-auto flex flex-col items-start p-4"
                        onClick={() => navigate('/admin/gamification')}
                      >
                        <Trophy className="w-6 h-6 mb-2 text-yellow-500" />
                        <h4 className="font-semibold">Create Badge</h4>
                        <p className="text-sm text-gray-600">Add new achievement badge</p>
                      </Button>

                      <Button 
                        variant="outline" 
                        className="h-auto flex flex-col items-start p-4"
                        onClick={() => navigate('/admin/gamification')}
                      >
                        <Gift className="w-6 h-6 mb-2 text-purple-500" />
                        <h4 className="font-semibold">Create Reward</h4>
                        <p className="text-sm text-gray-600">Add reward to store</p>
                      </Button>

                      <Button 
                        variant="outline" 
                        className="h-auto flex flex-col items-start p-4"
                        onClick={() => navigate('/admin/gamification')}
                      >
                        <Star className="w-6 h-6 mb-2 text-orange-500" />
                        <h4 className="font-semibold">Award Badge</h4>
                        <p className="text-sm text-gray-600">Give badge to student</p>
                      </Button>

                      <Button 
                        variant="outline" 
                        className="h-auto flex flex-col items-start p-4"
                        onClick={() => navigate('/admin/gamification')}
                      >
                        <Coins className="w-6 h-6 mb-2 text-green-500" />
                        <h4 className="font-semibold">Award Points</h4>
                        <p className="text-sm text-gray-600">Give points to student</p>
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Recent Badges */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Recent Badges</CardTitle>
                      <CardDescription>Latest badges in the system</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {badges.slice(0, 8).map((badge) => (
                          <div key={badge.id} className="text-center p-3 border rounded-lg hover:shadow-md transition-shadow">
                            <div className="text-3xl mb-2">{badge.icon}</div>
                            <h4 className="font-semibold text-sm">{badge.name}</h4>
                            <Badge variant="outline" className="mt-1 text-xs">{badge.rarity}</Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* User Progress Tab */}
              <TabsContent value="progress">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold">User Progress & Skill Tests</h3>
                    <p className="text-gray-600 mt-1">View all users' progress, career readiness scores, and skill test results</p>
                  </div>

                  {/* Search/Filter */}
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex gap-3 items-center">
                        <div className="flex-1">
                          <Label>Search Users</Label>
                          <Input
                            placeholder="Search by name or email..."
                            value={userSearchQuery}
                            onChange={(e) => setUserSearchQuery(e.target.value)}
                            className="mt-1"
                          />
                        </div>
                        <Button
                          variant="outline"
                          onClick={() => setUserSearchQuery('')}
                          className="mt-6"
                        >
                          Clear
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Users Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Users Career Readiness ({users.filter(u => 
                        u.name.toLowerCase().includes(userSearchQuery.toLowerCase()) || 
                        u.email.toLowerCase().includes(userSearchQuery.toLowerCase())
                      ).length} users)</CardTitle>
                      <CardDescription>Click "View Results" to see detailed skill test results</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {users.length === 0 ? (
                        <div className="text-center py-8 text-gray-600">
                          <p>No users found</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left p-3">Name</th>
                                <th className="text-left p-3">Email</th>
                                <th className="text-left p-3">Type</th>
                                <th className="text-left p-3">Total Points</th>
                                <th className="text-left p-3">Login Streak</th>
                                <th className="text-left p-3">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {users
                                .filter(u => 
                                  u.name.toLowerCase().includes(userSearchQuery.toLowerCase()) || 
                                  u.email.toLowerCase().includes(userSearchQuery.toLowerCase())
                                )
                                .map((user) => (
                                <tr key={user.id} className="border-b hover:bg-gray-50">
                                  <td className="p-3 font-medium">{user.name}</td>
                                  <td className="p-3 text-sm text-gray-600">{user.email}</td>
                                  <td className="p-3">
                                    <Badge variant="outline">{user.user_type || 'student'}</Badge>
                                  </td>
                                  <td className="p-3">
                                    <Badge variant="secondary">{user.points || 0} pts</Badge>
                                  </td>
                                  <td className="p-3">
                                    <Badge>{user.login_streak || 0} days</Badge>
                                  </td>
                                  <td className="p-3">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={async () => {
                                        try {
                                          const response = await axios.get(`${API}/admin/skill-test-results`, {
                                            headers: { Authorization: `Bearer ${token}` }
                                          });
                                          const userResults = response.data.filter(r => r.user_id === user.id);
                                          
                                          setSelectedUserResults({
                                            user: user,
                                            results: userResults
                                          });
                                          setShowResultsModal(true);
                                        } catch (error) {
                                          console.error("Error fetching results:", error);
                                          toast.error("Failed to fetch user results");
                                        }
                                      }}
                                    >
                                      View Results
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Readiness Settings Tab */}
              <TabsContent value="readiness-settings">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold">Career Readiness Score Settings</h3>
                    <p className="text-gray-600 mt-1">Configure the weightage for career readiness score calculation</p>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle>Weightage Settings</CardTitle>
                      <CardDescription>Total must equal 100%</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label>Profile Completion Weight (%)</Label>
                        <Input
                          type="number"
                          value={readinessSettings.profile_completion_weight}
                          onChange={(e) => setReadinessSettings({...readinessSettings, profile_completion_weight: parseInt(e.target.value)})}
                          min="0"
                          max="100"
                        />
                      </div>

                      <div>
                        <Label>Assessment Completion Weight (%)</Label>
                        <Input
                          type="number"
                          value={readinessSettings.assessment_weight}
                          onChange={(e) => setReadinessSettings({...readinessSettings, assessment_weight: parseInt(e.target.value)})}
                          min="0"
                          max="100"
                        />
                      </div>

                      <div>
                        <Label>Skills Development Weight (%)</Label>
                        <Input
                          type="number"
                          value={readinessSettings.skills_weight}
                          onChange={(e) => setReadinessSettings({...readinessSettings, skills_weight: parseInt(e.target.value)})}
                          min="0"
                          max="100"
                        />
                      </div>

                      <div>
                        <Label>Consultation Taken Weight (%)</Label>
                        <Input
                          type="number"
                          value={readinessSettings.consultation_weight}
                          onChange={(e) => setReadinessSettings({...readinessSettings, consultation_weight: parseInt(e.target.value)})}
                          min="0"
                          max="100"
                        />
                      </div>

                      <div>
                        <Label>Active Engagement Weight (%)</Label>
                        <Input
                          type="number"
                          value={readinessSettings.active_engagement_weight}
                          onChange={(e) => setReadinessSettings({...readinessSettings, active_engagement_weight: parseInt(e.target.value)})}
                          min="0"
                          max="100"
                        />
                      </div>

                      <div className="pt-4 border-t">
                        <div className="flex justify-between items-center mb-4">
                          <span className="font-semibold">Total:</span>
                          <Badge variant={
                            (readinessSettings.profile_completion_weight +
                            readinessSettings.assessment_weight +
                            readinessSettings.skills_weight +
                            readinessSettings.consultation_weight +
                            readinessSettings.active_engagement_weight) === 100 ? "default" : "destructive"
                          }>
                            {readinessSettings.profile_completion_weight +
                            readinessSettings.assessment_weight +
                            readinessSettings.skills_weight +
                            readinessSettings.consultation_weight +
                            readinessSettings.active_engagement_weight}%
                          </Badge>
                        </div>
                        
                        <Button
                          onClick={async () => {
                            const total = readinessSettings.profile_completion_weight +
                                        readinessSettings.assessment_weight +
                                        readinessSettings.skills_weight +
                                        readinessSettings.consultation_weight +
                                        readinessSettings.active_engagement_weight;
                            
                            if (total !== 100) {
                              toast.error(`Total must equal 100%. Current total: ${total}%`);
                              return;
                            }

                            try {
                              await axios.post(
                                `${API}/admin/career-readiness-settings`,
                                readinessSettings,
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              toast.success("Readiness settings updated successfully!");
                            } catch (error) {
                              toast.error(error.response?.data?.detail || "Failed to update settings");
                            }
                          }}
                        >
                          Save Settings
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Import Data Tab */}
              {/* Broadcast Notifications Tab */}
              <TabsContent value="notifications">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-2xl font-bold flex items-center gap-2">
                        <Bell className="w-6 h-6" />
                        Broadcast Notifications
                      </h3>
                      <p className="text-gray-600 mt-1">Send in-app and email notifications to all users or specific groups</p>
                    </div>
                  </div>

                  <Card className="border-2 border-primary/20">
                    <CardHeader>
                      <CardTitle>Create Broadcast Message</CardTitle>
                      <CardDescription>Send notifications to users across the platform</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="broadcast-title">Title *</Label>
                        <Input
                          id="broadcast-title"
                          placeholder="Enter notification title"
                          value={broadcastForm.title}
                          onChange={(e) => setBroadcastForm({...broadcastForm, title: e.target.value})}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="broadcast-message">Message *</Label>
                        <Textarea
                          id="broadcast-message"
                          placeholder="Enter notification message"
                          value={broadcastForm.message}
                          onChange={(e) => setBroadcastForm({...broadcastForm, message: e.target.value})}
                          rows={4}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="broadcast-type">Type</Label>
                          <select
                            id="broadcast-type"
                            className="w-full p-2 border rounded-md"
                            value={broadcastForm.type}
                            onChange={(e) => setBroadcastForm({...broadcastForm, type: e.target.value})}
                          >
                            <option value="system_update">System Update</option>
                            <option value="announcement">Announcement</option>
                            <option value="maintenance">Maintenance</option>
                            <option value="new_feature">New Feature</option>
                            <option value="promotion">Promotion</option>
                          </select>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="broadcast-priority">Priority</Label>
                          <select
                            id="broadcast-priority"
                            className="w-full p-2 border rounded-md"
                            value={broadcastForm.priority}
                            onChange={(e) => setBroadcastForm({...broadcastForm, priority: e.target.value})}
                          >
                            <option value="normal">Normal</option>
                            <option value="high">High</option>
                            <option value="urgent">Urgent</option>
                          </select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="broadcast-link">Link (Optional)</Label>
                        <Input
                          id="broadcast-link"
                          placeholder="/dashboard (optional)"
                          value={broadcastForm.link}
                          onChange={(e) => setBroadcastForm({...broadcastForm, link: e.target.value})}
                        />
                        <p className="text-xs text-gray-500">Relative path or full URL to redirect users</p>
                      </div>

                      <div className="border-t pt-4 space-y-3">
                        <h4 className="font-semibold flex items-center gap-2">
                          <Filter className="w-4 h-4" />
                          Target Audience (Optional Filters)
                        </h4>

                        <div className="space-y-2">
                          <Label>User Roles</Label>
                          <div className="flex flex-wrap gap-2">
                            {['school_student', 'college_student', 'jobseeker', 'professional', 'parent'].map((role) => (
                              <label key={role} className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={broadcastForm.target_roles?.includes(role) || false}
                                  onChange={(e) => {
                                    const roles = broadcastForm.target_roles || [];
                                    if (e.target.checked) {
                                      setBroadcastForm({
                                        ...broadcastForm,
                                        target_roles: [...roles, role]
                                      });
                                    } else {
                                      setBroadcastForm({
                                        ...broadcastForm,
                                        target_roles: roles.filter(r => r !== role)
                                      });
                                    }
                                  }}
                                  className="rounded"
                                />
                                <span className="text-sm capitalize">{role.replace('_', ' ')}</span>
                              </label>
                            ))}
                          </div>
                          <p className="text-xs text-gray-500">Leave unchecked to send to all roles</p>
                        </div>

                        <div className="space-y-2">
                          <Label>Subscription Tiers</Label>
                          <div className="flex flex-wrap gap-2">
                            {['free', 'premium', 'pro'].map((tier) => (
                              <label key={tier} className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={broadcastForm.target_subscription_tiers?.includes(tier) || false}
                                  onChange={(e) => {
                                    const tiers = broadcastForm.target_subscription_tiers || [];
                                    if (e.target.checked) {
                                      setBroadcastForm({
                                        ...broadcastForm,
                                        target_subscription_tiers: [...tiers, tier]
                                      });
                                    } else {
                                      setBroadcastForm({
                                        ...broadcastForm,
                                        target_subscription_tiers: tiers.filter(t => t !== tier)
                                      });
                                    }
                                  }}
                                  className="rounded"
                                />
                                <span className="text-sm capitalize">{tier}</span>
                              </label>
                            ))}
                          </div>
                          <p className="text-xs text-gray-500">Leave unchecked to send to all subscription tiers</p>
                        </div>
                      </div>

                      <div className="border-t pt-4 space-y-3">
                        <h4 className="font-semibold flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          Delivery Options
                        </h4>

                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={broadcastForm.send_email}
                            onChange={(e) => setBroadcastForm({...broadcastForm, send_email: e.target.checked})}
                            className="rounded"
                          />
                          <span className="text-sm">Send email notifications (in addition to in-app notifications)</span>
                        </label>

                        {broadcastForm.send_email && (
                          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-800">
                            <p className="flex items-center gap-2">
                              <AlertCircle className="w-4 h-4" />
                              Email notifications will be sent using the configured SMTP settings.
                            </p>
                            {emailSettings && !emailSettings.enabled && (
                              <p className="text-red-600 mt-2">⚠️ Warning: Email is currently disabled in Email Settings.</p>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2 pt-4">
                        <Button
                          onClick={handleBroadcastNotification}
                          disabled={!broadcastForm.title || !broadcastForm.message || broadcastLoading}
                          className="flex-1"
                        >
                          {broadcastLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Send Broadcast
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setBroadcastForm({
                            title: '',
                            message: '',
                            type: 'system_update',
                            priority: 'normal',
                            link: '',
                            send_email: false,
                            target_roles: null,
                            target_subscription_tiers: null
                          })}
                        >
                          Reset
                        </Button>
                      </div>

                      {broadcastResult && (
                        <div className={`mt-4 p-4 rounded-md ${
                          broadcastResult.success ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'
                        }`}>
                          <p className="font-semibold flex items-center gap-2">
                            {broadcastResult.success ? (
                              <CheckCircle className="w-5 h-5" />
                            ) : (
                              <XCircle className="w-5 h-5" />
                            )}
                            {broadcastResult.message}
                          </p>
                          {broadcastResult.users_notified && (
                            <p className="mt-2 text-sm">Users notified: {broadcastResult.users_notified}</p>
                          )}
                          {broadcastResult.emails_sent !== undefined && (
                            <p className="text-sm">Emails sent: {broadcastResult.emails_sent} / Failed: {broadcastResult.emails_failed || 0}</p>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Broadcast History Section */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Broadcast History
                      </CardTitle>
                      <CardDescription>View all sent broadcast notifications</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {broadcastHistory.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <Bell className="w-12 h-12 mx-auto mb-2 opacity-30" />
                          <p>No broadcasts sent yet</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {broadcastHistory.map((broadcast) => (
                            <div key={broadcast.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <h4 className="font-semibold text-lg">{broadcast.title}</h4>
                                    <Badge variant="outline" className="capitalize">{broadcast.type.replace('_', ' ')}</Badge>
                                    <Badge className={
                                      broadcast.priority === 'urgent' ? 'bg-red-500' :
                                      broadcast.priority === 'high' ? 'bg-orange-500' :
                                      'bg-blue-500'
                                    }>
                                      {broadcast.priority}
                                    </Badge>
                                  </div>
                                  <p className="text-gray-600 mb-3">{broadcast.message}</p>
                                  
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                    <div className="flex items-center gap-1">
                                      <Users className="w-4 h-4 text-blue-500" />
                                      <span>{broadcast.users_notified} users</span>
                                    </div>
                                    {broadcast.send_email && (
                                      <div className="flex items-center gap-1">
                                        <Mail className="w-4 h-4 text-green-500" />
                                        <span>{broadcast.emails_sent} sent</span>
                                      </div>
                                    )}
                                    {broadcast.target_roles && (
                                      <div className="flex items-center gap-1">
                                        <Filter className="w-4 h-4 text-purple-500" />
                                        <span>{broadcast.target_roles.join(', ')}</span>
                                      </div>
                                    )}
                                    {broadcast.target_subscription_tiers && (
                                      <div className="flex items-center gap-1">
                                        <Star className="w-4 h-4 text-yellow-500" />
                                        <span>{broadcast.target_subscription_tiers.join(', ')}</span>
                                      </div>
                                    )}
                                  </div>
                                  
                                  <div className="mt-3 text-xs text-gray-500 flex items-center gap-4">
                                    <span>Sent by: {broadcast.sent_by_name}</span>
                                    <span>•</span>
                                    <span>{new Date(broadcast.created_at).toLocaleString()}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Import Data Tab */}
              <TabsContent value="import-data">
                <ImportDataTab />
              </TabsContent>

              {/* Mock Interviews Tab */}
              <TabsContent value="mock-interviews">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-semibold">Mock Interview Results</h3>
                    <Badge variant="secondary">{mockInterviewSessions.length} Total Sessions</Badge>
                  </div>

                  <Card>
                    <CardContent className="pt-6">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left p-3 font-semibold">Student</th>
                              <th className="text-left p-3 font-semibold">Career Field</th>
                              <th className="text-left p-3 font-semibold">Experience Level</th>
                              <th className="text-left p-3 font-semibold">Status</th>
                              <th className="text-left p-3 font-semibold">Overall Score</th>
                              <th className="text-left p-3 font-semibold">Date</th>
                              <th className="text-left p-3 font-semibold">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {mockInterviewSessions.map((session) => (
                              <tr key={session.id} className="border-b hover:bg-gray-50">
                                <td className="p-3">
                                  <div>
                                    <p className="font-medium">{session.user_name}</p>
                                    <p className="text-sm text-gray-600">{session.user_email}</p>
                                  </div>
                                </td>
                                <td className="p-3">{session.career_field}</td>
                                <td className="p-3">
                                  <Badge variant="outline">{session.experience_level}</Badge>
                                </td>
                                <td className="p-3">
                                  <Badge variant={session.completed ? "default" : "secondary"}>
                                    {session.completed ? "Completed" : "In Progress"}
                                  </Badge>
                                </td>
                                <td className="p-3">
                                  {session.overall_feedback ? (
                                    <Badge
                                      variant={
                                        session.overall_feedback.average_overall_rating >= 7
                                          ? "default"
                                          : "secondary"
                                      }
                                    >
                                      {session.overall_feedback.average_overall_rating}/10
                                    </Badge>
                                  ) : (
                                    <span className="text-gray-400">N/A</span>
                                  )}
                                </td>
                                <td className="p-3 text-sm text-gray-600">
                                  {new Date(session.created_at).toLocaleDateString()}
                                </td>
                                <td className="p-3">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => viewInterviewDetails(session.id)}
                                  >
                                    <Eye className="w-4 h-4 mr-1" />
                                    View Details
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {mockInterviewSessions.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          No mock interview sessions found
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Legal & Compliance Tab */}
              <TabsContent value="legal-compliance">
                <AdminLegalCompliance />
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>

        {/* User Profile Modal */}
        <Dialog open={selectedUser && !showWalletModal && !showPlanModal} onOpenChange={() => setSelectedUser(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>User Profile</DialogTitle>
            </DialogHeader>
            {selectedUser && (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-600">Name</Label>
                    <p className="font-medium">{selectedUser.name}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Email</Label>
                    <p className="font-medium">{selectedUser.email}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Role</Label>
                    <Badge>{selectedUser.role}</Badge>
                  </div>
                  <div>
                    <Label className="text-gray-600">Plan</Label>
                    <Badge variant="outline">{selectedUser.subscription_plan || 'Free'}</Badge>
                  </div>
                  <div>
                    <Label className="text-gray-600">Wallet Balance</Label>
                    <p className="font-medium text-green-600">₹{selectedUser.wallet_balance || 0}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Points</Label>
                    <p className="font-medium">{selectedUser.points || 0}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Grade</Label>
                    <p className="font-medium">{selectedUser.grade || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Status</Label>
                    {selectedUser.locked ? (
                      <Badge variant="destructive">Locked</Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-700">Active</Badge>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 pt-4">
                  <Button onClick={() => {setShowWalletModal(true);}} className="flex-1">
                    <DollarSign className="w-4 h-4 mr-2" />
                    Topup Wallet
                  </Button>
                  <Button onClick={() => {setShowPlanModal(true);}} variant="outline" className="flex-1">
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Upgrade Plan
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Wallet Topup Modal */}
        <Dialog open={showWalletModal} onOpenChange={setShowWalletModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Topup Wallet</DialogTitle>
              <DialogDescription>
                Add funds to {selectedUser?.name}'s wallet
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Current Balance</Label>
                <p className="text-2xl font-bold text-green-600">₹{selectedUser?.wallet_balance || 0}</p>
              </div>
              <div>
                <Label>Topup Amount (₹) *</Label>
                <Input 
                  type="number"
                  value={walletAmount}
                  onChange={(e) => setWalletAmount(e.target.value)}
                  placeholder="Enter amount"
                  min="1"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={() => setWalletAmount('100')} variant="outline" size="sm">₹100</Button>
                <Button onClick={() => setWalletAmount('500')} variant="outline" size="sm">₹500</Button>
                <Button onClick={() => setWalletAmount('1000')} variant="outline" size="sm">₹1000</Button>
                <Button onClick={() => setWalletAmount('5000')} variant="outline" size="sm">₹5000</Button>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleWalletTopup} className="flex-1">Topup Wallet</Button>
              <Button onClick={() => {setShowWalletModal(false); setWalletAmount('');}} variant="outline">Cancel</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Plan Upgrade Modal */}
        <Dialog open={showPlanModal} onOpenChange={setShowPlanModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Upgrade User Plan</DialogTitle>
              <DialogDescription>
                Change subscription plan for {selectedUser?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Current Plan</Label>
                <Badge variant="outline" className="mt-1">{selectedUser?.subscription_plan || 'Free'}</Badge>
              </div>
              <div>
                <Label>Select New Plan *</Label>
                <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {subscriptionPlans.map((plan) => (
                      <SelectItem key={plan.plan_id} value={plan.plan_id}>
                        {plan.plan_name} - ₹{plan.price_monthly}/month
                      </SelectItem>
                    ))}
                    {subscriptionPlans.length === 0 && (
                      <>
                        <SelectItem value="free">Free Plan</SelectItem>
                        <SelectItem value="basic">Basic Plan - ₹499/month</SelectItem>
                        <SelectItem value="premium">Premium Plan - ₹999/month</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Payment Details */}
              {paymentDetails && (
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="pt-4">
                    <h4 className="font-semibold mb-2 text-sm">Payment Information</h4>
                    {paymentDetails.type === 'bank' ? (
                      <div className="text-sm space-y-1">
                        <p><strong>Bank:</strong> {paymentDetails.bank_name}</p>
                        <p><strong>Account:</strong> {paymentDetails.account_number}</p>
                        <p><strong>IFSC:</strong> {paymentDetails.ifsc_code}</p>
                        <p><strong>Holder:</strong> {paymentDetails.account_holder_name}</p>
                      </div>
                    ) : (
                      <div className="text-sm">
                        <p><strong>UPI ID:</strong> {paymentDetails.upi_id}</p>
                        <p><strong>Name:</strong> {paymentDetails.merchant_name}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
            <div className="flex gap-2">
              <Button onClick={handlePlanUpgrade} className="flex-1">Upgrade Plan</Button>
              <Button onClick={() => setShowPlanModal(false)} variant="outline">Cancel</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modals for each content type */}
        
        {/* Scholarship Modal */}
        <Dialog open={modals.scholarship} onOpenChange={() => closeModal('scholarship')}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing.scholarship ? 'Edit' : 'Add'} Scholarship</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="col-span-2">
                <Label>Name *</Label>
                <Input value={forms.scholarship.name} onChange={(e) => updateForm('scholarship', 'name', e.target.value)} />
              </div>
              <div>
                <Label>Organization *</Label>
                <Input value={forms.scholarship.organization} onChange={(e) => updateForm('scholarship', 'organization', e.target.value)} />
              </div>
              <div>
                <Label>Amount *</Label>
                <Input value={forms.scholarship.amount} onChange={(e) => updateForm('scholarship', 'amount', e.target.value)} placeholder="e.g., $5,000" />
              </div>
              <div>
                <Label>Min Grade</Label>
                <Input type="number" value={forms.scholarship.min_grade} onChange={(e) => updateForm('scholarship', 'min_grade', e.target.value)} min={1} max={12} />
              </div>
              <div>
                <Label>Max Grade</Label>
                <Input type="number" value={forms.scholarship.max_grade} onChange={(e) => updateForm('scholarship', 'max_grade', e.target.value)} min={1} max={12} />
              </div>
              <div>
                <Label>Category *</Label>
                <Input value={forms.scholarship.category} onChange={(e) => updateForm('scholarship', 'category', e.target.value)} placeholder="e.g., Merit-based" />
              </div>
              <div>
                <Label>Deadline *</Label>
                <Input type="date" value={forms.scholarship.deadline} onChange={(e) => updateForm('scholarship', 'deadline', e.target.value)} />
              </div>
              <div className="col-span-2">
                <Label>Eligibility (comma-separated)</Label>
                <Textarea value={forms.scholarship.eligibility} onChange={(e) => updateForm('scholarship', 'eligibility', e.target.value)} placeholder="e.g., GPA 3.5+, US Citizen" />
              </div>
              <div className="col-span-2">
                <Label>Application Link *</Label>
                <Input value={forms.scholarship.application_link} onChange={(e) => updateForm('scholarship', 'application_link', e.target.value)} />
              </div>
              <div className="col-span-2">
                <Label>Description *</Label>
                <Textarea value={forms.scholarship.description} onChange={(e) => updateForm('scholarship', 'description', e.target.value)} rows={3} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => closeModal('scholarship')}>Cancel</Button>
              <Button onClick={() => handleSubmit('scholarship')}>
                {editing.scholarship ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Exam Modal */}
        <Dialog open={modals.exam} onOpenChange={() => closeModal('exam')}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing.exam ? 'Edit' : 'Add'} Exam</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div>
                <Label>Short Name *</Label>
                <Input value={forms.exam.name} onChange={(e) => updateForm('exam', 'name', e.target.value)} placeholder="e.g., JEE" />
              </div>
              <div>
                <Label>Full Name *</Label>
                <Input value={forms.exam.full_name} onChange={(e) => updateForm('exam', 'full_name', e.target.value)} placeholder="e.g., Joint Entrance Examination" />
              </div>
              <div>
                <Label>Exam Date *</Label>
                <Input type="date" value={forms.exam.exam_date} onChange={(e) => updateForm('exam', 'exam_date', e.target.value)} />
              </div>
              <div>
                <Label>Registration Deadline *</Label>
                <Input type="date" value={forms.exam.registration_deadline} onChange={(e) => updateForm('exam', 'registration_deadline', e.target.value)} />
              </div>
              <div className="col-span-2">
                <Label>Eligibility (comma-separated)</Label>
                <Textarea value={forms.exam.eligibility} onChange={(e) => updateForm('exam', 'eligibility', e.target.value)} placeholder="e.g., 12th pass, Age 18+" />
              </div>
              <div className="col-span-2">
                <Label>Subjects (comma-separated) *</Label>
                <Input value={forms.exam.subjects} onChange={(e) => updateForm('exam', 'subjects', e.target.value)} placeholder="e.g., Physics, Chemistry, Mathematics" />
              </div>
              <div className="col-span-2">
                <Label>Career Paths (comma-separated) *</Label>
                <Input value={forms.exam.career_paths} onChange={(e) => updateForm('exam', 'career_paths', e.target.value)} placeholder="e.g., Engineering, Architecture" />
              </div>
              <div className="col-span-2">
                <Label>Description *</Label>
                <Textarea value={forms.exam.description} onChange={(e) => updateForm('exam', 'description', e.target.value)} rows={3} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => closeModal('exam')}>Cancel</Button>
              <Button onClick={() => handleSubmit('exam')}>
                {editing.exam ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Resource Modal */}
        <Dialog open={modals.resource} onOpenChange={() => closeModal('resource')}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing.resource ? 'Edit' : 'Add'} Resource</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="col-span-2">
                <Label>Title *</Label>
                <Input value={forms.resource.title} onChange={(e) => updateForm('resource', 'title', e.target.value)} />
              </div>
              <div>
                <Label>Type *</Label>
                <Select value={forms.resource.type} onValueChange={(val) => updateForm('resource', 'type', val)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="article">Article</SelectItem>
                    <SelectItem value="ebook">E-book</SelectItem>
                    <SelectItem value="webinar">Webinar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category *</Label>
                <Input value={forms.resource.category} onChange={(e) => updateForm('resource', 'category', e.target.value)} placeholder="e.g., Career Guidance" />
              </div>
              <div className="col-span-2">
                <Label>URL *</Label>
                <Input value={forms.resource.url} onChange={(e) => updateForm('resource', 'url', e.target.value)} placeholder="https://..." />
              </div>
              <div>
                <Label>Thumbnail URL</Label>
                <Input value={forms.resource.thumbnail} onChange={(e) => updateForm('resource', 'thumbnail', e.target.value)} />
              </div>
              <div>
                <Label>Duration</Label>
                <Input value={forms.resource.duration} onChange={(e) => updateForm('resource', 'duration', e.target.value)} placeholder="e.g., 10 mins" />
              </div>
              <div className="col-span-2">
                <Label>Description *</Label>
                <Textarea value={forms.resource.description} onChange={(e) => updateForm('resource', 'description', e.target.value)} rows={3} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => closeModal('resource')}>Cancel</Button>
              <Button onClick={() => handleSubmit('resource')}>
                {editing.resource ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Event Modal */}
        <Dialog open={modals.event} onOpenChange={() => closeModal('event')}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing.event ? 'Edit' : 'Add'} Event</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="col-span-2">
                <Label>Title *</Label>
                <Input value={forms.event.title} onChange={(e) => updateForm('event', 'title', e.target.value)} />
              </div>
              <div>
                <Label>Type *</Label>
                <Select value={forms.event.type} onValueChange={(val) => updateForm('event', 'type', val)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="webinar">Webinar</SelectItem>
                    <SelectItem value="career_fair">Career Fair</SelectItem>
                    <SelectItem value="workshop">Workshop</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Speaker *</Label>
                <Input value={forms.event.speaker} onChange={(e) => updateForm('event', 'speaker', e.target.value)} />
              </div>
              <div>
                <Label>Date *</Label>
                <Input type="date" value={forms.event.date} onChange={(e) => updateForm('event', 'date', e.target.value)} />
              </div>
              <div>
                <Label>Time *</Label>
                <Input type="time" value={forms.event.time} onChange={(e) => updateForm('event', 'time', e.target.value)} />
              </div>
              <div>
                <Label>Max Attendees</Label>
                <Input type="number" value={forms.event.max_attendees} onChange={(e) => updateForm('event', 'max_attendees', e.target.value)} />
              </div>
              <div className="col-span-2">
                <Label>Registration Link</Label>
                <Input value={forms.event.registration_link} onChange={(e) => updateForm('event', 'registration_link', e.target.value)} />
              </div>
              <div className="col-span-2">
                <Label>Description *</Label>
                <Textarea value={forms.event.description} onChange={(e) => updateForm('event', 'description', e.target.value)} rows={3} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => closeModal('event')}>Cancel</Button>
              <Button onClick={() => handleSubmit('event')}>
                {editing.event ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Mentor Modal */}
        <Dialog open={modals.mentor} onOpenChange={() => closeModal('mentor')}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing.mentor ? 'Edit' : 'Add'} Mentor/Counselor</DialogTitle>
              <p className="text-sm text-gray-500">Fill in the mentor details. Fields marked with * are required.</p>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              {/* Basic Information */}
              <div className="col-span-2 border-b pb-3 mb-2">
                <h4 className="font-semibold text-sm">Basic Information</h4>
              </div>
              
              <div>
                <Label>Name *</Label>
                <Input value={forms.mentor.name} onChange={(e) => updateForm('mentor', 'name', e.target.value)} />
              </div>
              <div>
                <Label>Profession *</Label>
                <Input value={forms.mentor.profession} onChange={(e) => updateForm('mentor', 'profession', e.target.value)} placeholder="e.g., Career Counselor" />
              </div>
              
              <div>
                <Label>Current Company *</Label>
                <Input value={forms.mentor.company} onChange={(e) => updateForm('mentor', 'company', e.target.value)} placeholder="e.g., ABC Consulting" />
              </div>
              <div>
                <Label>Designation</Label>
                <Input value={forms.mentor.designation || ''} onChange={(e) => updateForm('mentor', 'designation', e.target.value)} placeholder="e.g., Senior Career Counselor" />
              </div>

              {/* Profile Image */}
              <div className="col-span-2 border-b pb-3 mb-2 mt-3">
                <h4 className="font-semibold text-sm">Profile Picture</h4>
              </div>

              <div className="col-span-2">
                <Label>Profile Image</Label>
                <div className="space-y-3">
                  <Input 
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          updateForm('mentor', 'image', reader.result);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="cursor-pointer"
                  />
                  {forms.mentor.image && (
                    <div className="relative w-32 h-32 border rounded-lg overflow-hidden">
                      <img 
                        src={forms.mentor.image} 
                        alt="Mentor preview" 
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => updateForm('mentor', 'image', '')}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">Upload a professional profile picture (JPG, PNG, max 5MB)</p>
              </div>

              {/* Education & Qualifications */}
              <div className="col-span-2 border-b pb-3 mb-2 mt-3">
                <h4 className="font-semibold text-sm">Education & Qualifications</h4>
              </div>

              <div className="col-span-2">
                <Label>Education Details *</Label>
                <Textarea 
                  value={forms.mentor.education || ''} 
                  onChange={(e) => updateForm('mentor', 'education', e.target.value)} 
                  rows={2}
                  placeholder="e.g., M.A. in Psychology, B.Ed in Career Counseling"
                />
              </div>

              <div className="col-span-2">
                <Label>Qualifications/Certifications (comma-separated)</Label>
                <Input 
                  value={forms.mentor.qualifications || ''} 
                  onChange={(e) => updateForm('mentor', 'qualifications', e.target.value)} 
                  placeholder="e.g., Certified Career Coach, NLP Practitioner"
                />
              </div>

              {/* Skills & Experience */}
              <div className="col-span-2 border-b pb-3 mb-2 mt-3">
                <h4 className="font-semibold text-sm">Skills & Experience</h4>
              </div>

              <div className="col-span-2">
                <Label>Skills (comma-separated)</Label>
                <Input 
                  value={forms.mentor.skills || ''} 
                  onChange={(e) => updateForm('mentor', 'skills', e.target.value)} 
                  placeholder="e.g., Career Counseling, Resume Building, Interview Preparation"
                />
              </div>

              <div>
                <Label>Years of Experience</Label>
                <Input 
                  type="number" 
                  value={forms.mentor.experience_years || 0} 
                  onChange={(e) => updateForm('mentor', 'experience_years', parseInt(e.target.value))} 
                  placeholder="e.g., 5"
                />
              </div>

              <div className="col-span-2">
                <Label>Work Experience Details</Label>
                <Textarea 
                  value={forms.mentor.work_experience || ''} 
                  onChange={(e) => updateForm('mentor', 'work_experience', e.target.value)} 
                  rows={3}
                  placeholder="Describe your professional experience in detail..."
                />
              </div>

              {/* Areas of Expertise */}
              <div className="col-span-2 border-b pb-3 mb-2 mt-3">
                <h4 className="font-semibold text-sm">Areas of Expertise & Bio</h4>
              </div>

              <div className="col-span-2">
                <Label>Expertise (comma-separated) *</Label>
                <Input 
                  value={forms.mentor.expertise} 
                  onChange={(e) => updateForm('mentor', 'expertise', e.target.value)} 
                  placeholder="e.g., Software Engineering, Career Planning, Student Counseling"
                />
              </div>

              <div className="col-span-2">
                <Label>Bio *</Label>
                <Textarea 
                  value={forms.mentor.bio} 
                  onChange={(e) => updateForm('mentor', 'bio', e.target.value)} 
                  rows={3}
                  placeholder="Write a brief bio about yourself..."
                />
              </div>

              {/* Private Contact Info (Admin Only) */}
              <div className="col-span-2 border-b pb-3 mb-2 mt-3">
                <h4 className="font-semibold text-sm text-red-600">🔒 Private Contact Info (Admin Only - Not Visible to Users)</h4>
              </div>

              <div>
                <Label>Email (Private)</Label>
                <Input 
                  type="email"
                  value={forms.mentor.email || ''} 
                  onChange={(e) => updateForm('mentor', 'email', e.target.value)} 
                  placeholder="mentor@example.com"
                />
                <p className="text-xs text-red-500 mt-1">⚠️ Not shown to users</p>
              </div>

              <div>
                <Label>Contact Number (Private)</Label>
                <Input 
                  type="tel"
                  value={forms.mentor.contact_number || ''} 
                  onChange={(e) => updateForm('mentor', 'contact_number', e.target.value)} 
                  placeholder="+91 9876543210"
                />
                <p className="text-xs text-red-500 mt-1">⚠️ Not shown to users</p>
              </div>

              {/* Consultation Settings */}
              <div className="col-span-2 border-b pb-3 mb-2 mt-3">
                <h4 className="font-semibold text-sm">Consultation Settings</h4>
              </div>

              <div>
                <Label>Rate Per Minute (₹) *</Label>
                <Input 
                  type="number" 
                  step="0.01"
                  value={forms.mentor.rate_per_minute || 2.0} 
                  onChange={(e) => updateForm('mentor', 'rate_per_minute', parseFloat(e.target.value))} 
                  placeholder="e.g., 2.50"
                />
                <p className="text-xs text-gray-500 mt-1">Amount charged per minute</p>
              </div>

              <div>
                <Label>Availability</Label>
                <Select 
                  value={forms.mentor.availability ? "true" : "false"} 
                  onValueChange={(val) => updateForm('mentor', 'availability', val === "true")}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Available</SelectItem>
                    <SelectItem value="false">Not Available</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => closeModal('mentor')}>Cancel</Button>
              <Button onClick={() => handleSubmit('mentor')}>
                {editing.mentor ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Quiz Modal */}
        <Dialog open={modals.quiz} onOpenChange={() => closeModal('quiz')}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing.quiz ? 'Edit' : 'Add'} Quiz</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Title *</Label>
                  <Input 
                    value={forms.quiz.title} 
                    onChange={(e) => updateForm('quiz', 'title', e.target.value)} 
                    placeholder="e.g., Technology Career Trivia"
                  />
                </div>
                <div>
                  <Label>Category *</Label>
                  <Input 
                    value={forms.quiz.category} 
                    onChange={(e) => updateForm('quiz', 'category', e.target.value)} 
                    placeholder="e.g., Technology, Engineering" 
                  />
                </div>
                <div>
                  <Label>Difficulty *</Label>
                  <Select value={forms.quiz.difficulty} onValueChange={(val) => updateForm('quiz', 'difficulty', val)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="easy">Easy</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="hard">Hard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Points *</Label>
                  <Input 
                    type="number" 
                    value={forms.quiz.points} 
                    onChange={(e) => updateForm('quiz', 'points', e.target.value)} 
                    placeholder="e.g., 20"
                  />
                </div>
                <div>
                  <Label>Time Limit (minutes)</Label>
                  <Input 
                    type="number" 
                    value={forms.quiz.time_limit} 
                    onChange={(e) => updateForm('quiz', 'time_limit', e.target.value)} 
                    placeholder="e.g., 30"
                  />
                </div>
              </div>

              {/* Questions Section */}
              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Quiz Questions</h3>
                  <Button 
                    type="button" 
                    size="sm"
                    onClick={() => {
                      const questions = forms.quiz.questions || [];
                      updateForm('quiz', 'questions', [...questions, {
                        question: '',
                        options: ['', '', '', ''],
                        correct: 0
                      }]);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Question
                  </Button>
                </div>

                {(!forms.quiz.questions || forms.quiz.questions.length === 0) && (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed">
                    <p className="text-gray-500 mb-2">No questions added yet</p>
                    <p className="text-sm text-gray-400">Click "Add Question" to create quiz questions</p>
                  </div>
                )}

                {forms.quiz.questions && forms.quiz.questions.map((question, qIdx) => (
                  <Card key={qIdx} className="mb-4 p-4">
                    <div className="space-y-4">
                      <div className="flex justify-between items-start">
                        <Label className="text-base font-semibold">Question {qIdx + 1}</Label>
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => {
                            const newQuestions = forms.quiz.questions.filter((_, i) => i !== qIdx);
                            updateForm('quiz', 'questions', newQuestions);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      <div>
                        <Label>Question Text *</Label>
                        <Input
                          value={question.question}
                          onChange={(e) => {
                            const newQuestions = [...forms.quiz.questions];
                            newQuestions[qIdx].question = e.target.value;
                            updateForm('quiz', 'questions', newQuestions);
                          }}
                          placeholder="Enter your question"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Options (select the correct answer)</Label>
                        {question.options.map((option, optIdx) => (
                          <div key={optIdx} className="flex items-center gap-2">
                            <input
                              type="radio"
                              name={`question-${qIdx}`}
                              checked={question.correct === optIdx}
                              onChange={() => {
                                const newQuestions = [...forms.quiz.questions];
                                newQuestions[qIdx].correct = optIdx;
                                updateForm('quiz', 'questions', newQuestions);
                              }}
                              className="w-4 h-4"
                            />
                            <Input
                              value={option}
                              onChange={(e) => {
                                const newQuestions = [...forms.quiz.questions];
                                newQuestions[qIdx].options[optIdx] = e.target.value;
                                updateForm('quiz', 'questions', newQuestions);
                              }}
                              placeholder={`Option ${optIdx + 1}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => closeModal('quiz')}>Cancel</Button>
              <Button onClick={() => handleSubmit('quiz')}>
                {editing.quiz ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* College Modal */}
        <Dialog open={modals.college} onOpenChange={() => closeModal('college')}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing.college ? 'Edit' : 'Add'} College</DialogTitle>
              <p className="text-sm text-gray-500">Fill in comprehensive college information</p>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              {/* Basic Information */}
              <div className="col-span-2">
                <h4 className="font-semibold text-sm mb-3 text-primary">Basic Information</h4>
              </div>
              <div className="col-span-2">
                <Label>Name *</Label>
                <Input value={forms.college.name} onChange={(e) => updateForm('college', 'name', e.target.value)} placeholder="e.g., Indian Institute of Technology Delhi" />
              </div>
              <div className="col-span-2">
                <Label>Description *</Label>
                <Textarea value={forms.college.description} onChange={(e) => updateForm('college', 'description', e.target.value)} rows={4} placeholder="Comprehensive description about the institution (150-200 words)..." />
              </div>
              <div>
                <Label>Location *</Label>
                <Input value={forms.college.location} onChange={(e) => updateForm('college', 'location', e.target.value)} placeholder="City, State" />
              </div>
              <div>
                <Label>State *</Label>
                <Input value={forms.college.state} onChange={(e) => updateForm('college', 'state', e.target.value)} placeholder="e.g., Delhi" />
              </div>
              <div>
                <Label>Type *</Label>
                <Input value={forms.college.type} onChange={(e) => updateForm('college', 'type', e.target.value)} placeholder="e.g., Engineering & Technology" />
              </div>
              <div>
                <Label>Established Year</Label>
                <Input type="number" value={forms.college.established} onChange={(e) => updateForm('college', 'established', e.target.value)} placeholder="e.g., 1961" />
              </div>
              <div>
                <Label>Image URL</Label>
                <Input value={forms.college.image} onChange={(e) => updateForm('college', 'image', e.target.value)} placeholder="https://..." />
              </div>
              <div>
                <Label>Website *</Label>
                <Input value={forms.college.website} onChange={(e) => updateForm('college', 'website', e.target.value)} placeholder="https://..." />
              </div>

              {/* Rankings */}
              <div className="col-span-2">
                <h4 className="font-semibold text-sm mb-3 text-primary mt-2">Rankings</h4>
              </div>
              <div>
                <Label>NIRF Ranking</Label>
                <Input type="number" value={forms.college.ranking_nirf} onChange={(e) => updateForm('college', 'ranking_nirf', e.target.value)} placeholder="e.g., 1" />
              </div>
              <div>
                <Label>India Today Ranking</Label>
                <Input type="number" value={forms.college.ranking_india_today} onChange={(e) => updateForm('college', 'ranking_india_today', e.target.value)} placeholder="e.g., 2" />
              </div>
              <div>
                <Label>QS World Ranking</Label>
                <Input type="number" value={forms.college.ranking_qs_world} onChange={(e) => updateForm('college', 'ranking_qs_world', e.target.value)} placeholder="e.g., 174" />
              </div>

              {/* Courses */}
              <div className="col-span-2">
                <h4 className="font-semibold text-sm mb-3 text-primary mt-2">Courses Offered</h4>
              </div>
              <div className="col-span-2">
                <Label>Courses (comma-separated) *</Label>
                <Textarea value={forms.college.courses_offered} onChange={(e) => updateForm('college', 'courses_offered', e.target.value)} rows={3} placeholder="e.g., B.Tech in Computer Science, M.Tech in AI & ML, MBA, PhD Programs" />
              </div>

              {/* Admission */}
              <div className="col-span-2">
                <h4 className="font-semibold text-sm mb-3 text-primary mt-2">Admission Details</h4>
              </div>
              <div>
                <Label>UG Admission Exam</Label>
                <Input value={forms.college.admission_ug} onChange={(e) => updateForm('college', 'admission_ug', e.target.value)} placeholder="e.g., JEE Advanced" />
              </div>
              <div>
                <Label>PG Admission Exam</Label>
                <Input value={forms.college.admission_pg} onChange={(e) => updateForm('college', 'admission_pg', e.target.value)} placeholder="e.g., GATE / CAT" />
              </div>
              <div className="col-span-2">
                <Label>Cutoff Marks</Label>
                <Input value={forms.college.cutoff_marks} onChange={(e) => updateForm('college', 'cutoff_marks', e.target.value)} placeholder="e.g., JEE Advanced Rank: 1-500 (General Category)" />
              </div>

              {/* Fees */}
              <div className="col-span-2">
                <h4 className="font-semibold text-sm mb-3 text-primary mt-2">Fee Structure (Annual)</h4>
              </div>
              <div>
                <Label>UG Fees</Label>
                <Input value={forms.college.fees_ug} onChange={(e) => updateForm('college', 'fees_ug', e.target.value)} placeholder="e.g., ₹2-3 Lakhs per year" />
              </div>
              <div>
                <Label>PG Fees</Label>
                <Input value={forms.college.fees_pg} onChange={(e) => updateForm('college', 'fees_pg', e.target.value)} placeholder="e.g., ₹2-4 Lakhs per year" />
              </div>

              {/* Placements */}
              <div className="col-span-2">
                <h4 className="font-semibold text-sm mb-3 text-primary mt-2">Placements</h4>
              </div>
              <div>
                <Label>Average Package</Label>
                <Input value={forms.college.placement_avg} onChange={(e) => updateForm('college', 'placement_avg', e.target.value)} placeholder="e.g., ₹18-22 LPA" />
              </div>
              <div>
                <Label>Highest Package</Label>
                <Input value={forms.college.placement_highest} onChange={(e) => updateForm('college', 'placement_highest', e.target.value)} placeholder="e.g., ₹1.8 Crore" />
              </div>
              <div>
                <Label>Placement Rate</Label>
                <Input value={forms.college.placement_rate} onChange={(e) => updateForm('college', 'placement_rate', e.target.value)} placeholder="e.g., 95%" />
              </div>
              <div>
                <Label>Top Recruiters (comma-separated)</Label>
                <Input value={forms.college.top_recruiters} onChange={(e) => updateForm('college', 'top_recruiters', e.target.value)} placeholder="e.g., Google, Microsoft, Amazon" />
              </div>

              {/* Scholarships */}
              <div className="col-span-2">
                <h4 className="font-semibold text-sm mb-3 text-primary mt-2">Scholarships</h4>
              </div>
              <div className="col-span-2">
                <Label>Available Scholarships (comma-separated)</Label>
                <Textarea value={forms.college.scholarships} onChange={(e) => updateForm('college', 'scholarships', e.target.value)} rows={3} placeholder="e.g., Merit-cum-Means Scholarship, Institute Free Studentship, SC/ST/OBC Scholarships" />
              </div>

              {/* Infrastructure */}
              <div className="col-span-2">
                <h4 className="font-semibold text-sm mb-3 text-primary mt-2">Infrastructure</h4>
              </div>
              <div className="col-span-2">
                <Label>Library</Label>
                <Input value={forms.college.infra_library} onChange={(e) => updateForm('college', 'infra_library', e.target.value)} placeholder="e.g., Central Library with 4 lakh books" />
              </div>
              <div className="col-span-2">
                <Label>Hostels</Label>
                <Input value={forms.college.infra_hostels} onChange={(e) => updateForm('college', 'infra_hostels', e.target.value)} placeholder="e.g., 15 hostels accommodating 7000+ students" />
              </div>
              <div className="col-span-2">
                <Label>Labs & Research</Label>
                <Input value={forms.college.infra_labs} onChange={(e) => updateForm('college', 'infra_labs', e.target.value)} placeholder="e.g., State-of-the-art research labs" />
              </div>
              <div className="col-span-2">
                <Label>Sports Facilities</Label>
                <Input value={forms.college.infra_sports} onChange={(e) => updateForm('college', 'infra_sports', e.target.value)} placeholder="e.g., Olympic-size swimming pool, cricket ground" />
              </div>
              <div className="col-span-2">
                <Label>Other Facilities</Label>
                <Input value={forms.college.infra_other} onChange={(e) => updateForm('college', 'infra_other', e.target.value)} placeholder="e.g., Innovation Center, Medical facilities" />
              </div>

              {/* Contact & Accreditation */}
              <div className="col-span-2">
                <h4 className="font-semibold text-sm mb-3 text-primary mt-2">Contact & Accreditation</h4>
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={forms.college.contact_phone} onChange={(e) => updateForm('college', 'contact_phone', e.target.value)} placeholder="e.g., +91-11-2659-1749" />
              </div>
              <div>
                <Label>Email</Label>
                <Input value={forms.college.contact_email} onChange={(e) => updateForm('college', 'contact_email', e.target.value)} placeholder="e.g., info@college.edu" />
              </div>
              <div className="col-span-2">
                <Label>Accreditation (comma-separated)</Label>
                <Input value={forms.college.accreditation} onChange={(e) => updateForm('college', 'accreditation', e.target.value)} placeholder="e.g., NAAC A++, NBA" />
              </div>

              {/* Status */}
              <div className="col-span-2">
                <h4 className="font-semibold text-sm mb-3 text-primary mt-2">Status</h4>
              </div>
              <div className="flex items-center space-x-2">
                <input 
                  type="checkbox" 
                  id="is_featured" 
                  checked={forms.college.is_featured || false}
                  onChange={(e) => updateForm('college', 'is_featured', e.target.checked)}
                  className="w-4 h-4"
                />
                <Label htmlFor="is_featured">Featured College</Label>
              </div>
              <div className="flex items-center space-x-2">
                <input 
                  type="checkbox" 
                  id="is_active" 
                  checked={forms.college.is_active !== false}
                  onChange={(e) => updateForm('college', 'is_active', e.target.checked)}
                  className="w-4 h-4"
                />
                <Label htmlFor="is_active">Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => closeModal('college')}>Cancel</Button>
              <Button onClick={() => handleSubmit('college')}>
                {editing.college ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Career Modal */}
        <Dialog open={modals.career} onOpenChange={() => closeModal('career')}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing.career ? 'Edit' : 'Add'} Career</DialogTitle>
              <p className="text-sm text-gray-500">Fill in comprehensive career information</p>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              {/* Basic Information */}
              <div className="col-span-2">
                <h4 className="font-semibold text-sm mb-3 text-primary">Basic Information</h4>
              </div>
              <div className="col-span-2">
                <Label>Title *</Label>
                <Input value={forms.career.title} onChange={(e) => updateForm('career', 'title', e.target.value)} placeholder="e.g., Software Engineer" />
              </div>
              <div>
                <Label>Category *</Label>
                <Select value={forms.career.category} onValueChange={(value) => updateForm('career', 'category', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Technology">Technology</SelectItem>
                    <SelectItem value="Healthcare">Healthcare</SelectItem>
                    <SelectItem value="Business">Business</SelectItem>
                    <SelectItem value="Creative">Creative</SelectItem>
                    <SelectItem value="Education">Education</SelectItem>
                    <SelectItem value="Engineering">Engineering</SelectItem>
                    <SelectItem value="Legal">Legal</SelectItem>
                    <SelectItem value="Government">Government</SelectItem>
                    <SelectItem value="Hospitality">Hospitality</SelectItem>
                    <SelectItem value="Science">Science</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Work Environment</Label>
                <Input value={forms.career.work_environment} onChange={(e) => updateForm('career', 'work_environment', e.target.value)} placeholder="e.g., Office/Remote/Hybrid" />
              </div>
              <div className="col-span-2">
                <Label>Description *</Label>
                <Textarea value={forms.career.description} onChange={(e) => updateForm('career', 'description', e.target.value)} rows={3} placeholder="Describe the career role and responsibilities..." />
              </div>

              {/* Salary Information */}
              <div className="col-span-2">
                <h4 className="font-semibold text-sm mb-3 text-primary mt-2">Salary Ranges</h4>
              </div>
              <div>
                <Label>Salary Range (India) *</Label>
                <Input value={forms.career.salary_range_india} onChange={(e) => updateForm('career', 'salary_range_india', e.target.value)} placeholder="e.g., ₹58 - 149 LPA" />
                <p className="text-xs text-gray-500 mt-1">Use format: ₹XX - YY LPA</p>
              </div>
              <div>
                <Label>Salary Range (Global) *</Label>
                <Input value={forms.career.salary_range_global} onChange={(e) => updateForm('career', 'salary_range_global', e.target.value)} placeholder="e.g., $70,000 - $180,000" />
                <p className="text-xs text-gray-500 mt-1">Use format: $XX,XXX - $YY,YYY</p>
              </div>

              {/* Career Metrics */}
              <div className="col-span-2">
                <h4 className="font-semibold text-sm mb-3 text-primary mt-2">Career Metrics</h4>
              </div>
              <div>
                <Label>Timeline to Enter</Label>
                <Input value={forms.career.timeline_to_enter} onChange={(e) => updateForm('career', 'timeline_to_enter', e.target.value)} placeholder="e.g., 4-6 years" />
              </div>
              <div>
                <Label>Job Outlook *</Label>
                <Input value={forms.career.job_outlook} onChange={(e) => updateForm('career', 'job_outlook', e.target.value)} placeholder="e.g., Excellent - 22% growth expected" />
              </div>
              <div>
                <Label>Work-Life Balance</Label>
                <Select value={forms.career.work_life_balance} onValueChange={(value) => updateForm('career', 'work_life_balance', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Very Good">Very Good</SelectItem>
                    <SelectItem value="Good">Good</SelectItem>
                    <SelectItem value="Moderate">Moderate</SelectItem>
                    <SelectItem value="Challenging">Challenging</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Growth Potential</Label>
                <Select value={forms.career.growth_potential} onValueChange={(value) => updateForm('career', 'growth_potential', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Very High">Very High</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Moderate">Moderate</SelectItem>
                    <SelectItem value="Limited">Limited</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Education & Skills */}
              <div className="col-span-2">
                <h4 className="font-semibold text-sm mb-3 text-primary mt-2">Education & Skills</h4>
              </div>
              <div className="col-span-2">
                <Label>Required Education (comma-separated) *</Label>
                <Textarea value={forms.career.required_education} onChange={(e) => updateForm('career', 'required_education', e.target.value)} placeholder="e.g., Bachelor's in Computer Science, Bachelor's in Software Engineering, Self-taught with portfolio" rows={2} />
              </div>
              <div className="col-span-2">
                <Label>Skills (comma-separated) *</Label>
                <Textarea value={forms.career.skills} onChange={(e) => updateForm('career', 'skills', e.target.value)} placeholder="e.g., Programming, Problem Solving, Data Structures, Algorithms" rows={2} />
              </div>
              <div className="col-span-2">
                <Label>Certifications (comma-separated)</Label>
                <Textarea value={forms.career.certifications} onChange={(e) => updateForm('career', 'certifications', e.target.value)} placeholder="e.g., AWS Certified Developer, Google Cloud Professional, Microsoft Azure" rows={2} />
              </div>

              {/* Additional Information */}
              <div className="col-span-2">
                <h4 className="font-semibold text-sm mb-3 text-primary mt-2">Additional Information</h4>
              </div>
              <div className="col-span-2">
                <Label>Top Companies (comma-separated)</Label>
                <Textarea value={forms.career.top_companies} onChange={(e) => updateForm('career', 'top_companies', e.target.value)} placeholder="e.g., Google, Microsoft, Amazon, Meta, Apple" rows={2} />
              </div>
              <div className="col-span-2">
                <Label>Day in Life</Label>
                <Textarea value={forms.career.day_in_life} onChange={(e) => updateForm('career', 'day_in_life', e.target.value)} placeholder="Describe a typical day in this career..." rows={3} />
              </div>
              <div className="col-span-2">
                <Label>Career Roadmap (JSON format)</Label>
                <Textarea value={forms.career.roadmap} onChange={(e) => updateForm('career', 'roadmap', e.target.value)} placeholder='[{"phase": "Year 1-4", "action": "Complete degree"}]' rows={4} />
                <p className="text-xs text-gray-500 mt-1">Format: Array of objects with "phase" and "action" fields</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => closeModal('career')}>Cancel</Button>
              <Button onClick={() => handleSubmit('career')}>
                {editing.career ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Opportunity Modal */}
        <Dialog open={modals.opportunity} onOpenChange={() => closeModal('opportunity')}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing.opportunity ? 'Edit' : 'Add'} Opportunity</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="col-span-2">
                <Label>Title *</Label>
                <Input value={forms.opportunity.title} onChange={(e) => updateForm('opportunity', 'title', e.target.value)} placeholder="e.g., Software Development Intern" />
              </div>
              <div>
                <Label>Company Name *</Label>
                <Input value={forms.opportunity.company_name} onChange={(e) => updateForm('opportunity', 'company_name', e.target.value)} placeholder="e.g., Google" />
              </div>
              <div>
                <Label>Type *</Label>
                <Select value={forms.opportunity.type} onValueChange={(value) => updateForm('opportunity', 'type', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internship">Internship</SelectItem>
                    <SelectItem value="job">Job</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Location *</Label>
                <Input value={forms.opportunity.location} onChange={(e) => updateForm('opportunity', 'location', e.target.value)} placeholder="e.g., Mumbai, India" />
              </div>
              <div>
                <Label>Work Mode *</Label>
                <Select value={forms.opportunity.work_mode} onValueChange={(value) => updateForm('opportunity', 'work_mode', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select work mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="remote">Remote</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                    <SelectItem value="onsite">Onsite</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Field *</Label>
                <Input value={forms.opportunity.field} onChange={(e) => updateForm('opportunity', 'field', e.target.value)} placeholder="e.g., Software Development" />
              </div>
              <div>
                <Label>Experience Level *</Label>
                <Select value={forms.opportunity.experience_level} onValueChange={(value) => updateForm('opportunity', 'experience_level', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="entry">Entry Level</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="senior">Senior</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {forms.opportunity.type === 'job' ? (
                <div>
                  <Label>Salary</Label>
                  <Input value={forms.opportunity.salary} onChange={(e) => updateForm('opportunity', 'salary', e.target.value)} placeholder="e.g., ₹8-12 LPA" />
                </div>
              ) : (
                <div>
                  <Label>Stipend</Label>
                  <Input value={forms.opportunity.stipend} onChange={(e) => updateForm('opportunity', 'stipend', e.target.value)} placeholder="e.g., ₹15,000/month" />
                </div>
              )}
              <div>
                <Label>Duration</Label>
                <Input value={forms.opportunity.duration} onChange={(e) => updateForm('opportunity', 'duration', e.target.value)} placeholder="e.g., 3 months, Full-time" />
              </div>
              <div>
                <Label>Start Date</Label>
                <Input type="date" value={forms.opportunity.start_date} onChange={(e) => updateForm('opportunity', 'start_date', e.target.value)} />
              </div>
              <div>
                <Label>Application Deadline *</Label>
                <Input type="date" value={forms.opportunity.application_deadline} onChange={(e) => updateForm('opportunity', 'application_deadline', e.target.value)} />
              </div>
              <div>
                <Label>Spots Available</Label>
                <Input type="number" value={forms.opportunity.spots_available || ''} onChange={(e) => updateForm('opportunity', 'spots_available', e.target.value ? parseInt(e.target.value) : null)} placeholder="e.g., 5" />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={forms.opportunity.status} onValueChange={(value) => updateForm('opportunity', 'status', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Description *</Label>
                <Textarea value={forms.opportunity.description} onChange={(e) => updateForm('opportunity', 'description', e.target.value)} rows={3} placeholder="Describe the opportunity..." />
              </div>
              <div className="col-span-2">
                <Label>Requirements *</Label>
                <Textarea value={forms.opportunity.requirements} onChange={(e) => updateForm('opportunity', 'requirements', e.target.value)} rows={2} placeholder="List the requirements..." />
              </div>
              <div className="col-span-2">
                <Label>Skills Required (comma-separated)</Label>
                <Input value={forms.opportunity.skills_required} onChange={(e) => updateForm('opportunity', 'skills_required', e.target.value)} placeholder="e.g., JavaScript, React, Node.js" />
              </div>
              <div className="col-span-2">
                <Label>Application Link *</Label>
                <Input value={forms.opportunity.application_link} onChange={(e) => updateForm('opportunity', 'application_link', e.target.value)} placeholder="https://..." />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => closeModal('opportunity')}>Cancel</Button>
              <Button onClick={() => handleSubmit('opportunity')}>
                {editing.opportunity ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Currency Modal */}
        <Dialog open={modals.currency} onOpenChange={() => closeModal('currency')}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editing.currency ? 'Edit' : 'Add'} Currency</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div>
                <Label>Currency Code * (e.g., USD, EUR)</Label>
                <Input value={forms.currency.code} onChange={(e) => updateForm('currency', 'code', e.target.value.toUpperCase())} placeholder="USD" maxLength={3} />
              </div>
              <div>
                <Label>Symbol * (e.g., $, €, ₹)</Label>
                <Input value={forms.currency.symbol} onChange={(e) => updateForm('currency', 'symbol', e.target.value)} placeholder="$" />
              </div>
              <div>
                <Label>Name *</Label>
                <Input value={forms.currency.name} onChange={(e) => updateForm('currency', 'name', e.target.value)} placeholder="US Dollar" />
              </div>
              <div>
                <Label>Decimal Places</Label>
                <Input type="number" value={forms.currency.decimal_places} onChange={(e) => updateForm('currency', 'decimal_places', parseInt(e.target.value))} min={0} max={4} />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={forms.currency.is_active}
                  onChange={(e) => updateForm('currency', 'is_active', e.target.checked)}
                  className="w-4 h-4"
                />
                <Label>Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => closeModal('currency')}>Cancel</Button>
              <Button onClick={() => handleSubmit('currency')}>
                {editing.currency ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Country Modal */}
        <Dialog open={modals.country} onOpenChange={() => closeModal('country')}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editing.country ? 'Edit' : 'Add'} Country</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div>
                <Label>Country Name *</Label>
                <Input value={forms.country.name} onChange={(e) => updateForm('country', 'name', e.target.value)} placeholder="United States" />
              </div>
              <div>
                <Label>Country Code * (ISO 3166-1 alpha-2)</Label>
                <Input value={forms.country.code} onChange={(e) => updateForm('country', 'code', e.target.value.toUpperCase())} placeholder="US" maxLength={2} />
              </div>
              <div>
                <Label>Currency Code *</Label>
                <Input value={forms.country.currency_code} onChange={(e) => updateForm('country', 'currency_code', e.target.value.toUpperCase())} placeholder="USD" maxLength={3} />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={forms.country.is_active}
                  onChange={(e) => updateForm('country', 'is_active', e.target.checked)}
                  className="w-4 h-4"
                />
                <Label>Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => closeModal('country')}>Cancel</Button>
              <Button onClick={() => handleSubmit('country')}>
                {editing.country ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Exchange Rate Modal */}
        <Dialog open={modals.exchangeRate} onOpenChange={() => closeModal('exchangeRate')}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add Exchange Rate</DialogTitle>
              <DialogDescription>Set a manual exchange rate between two currencies</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div>
                <Label>From Currency *</Label>
                <Input value={forms.exchangeRate.from_currency} onChange={(e) => updateForm('exchangeRate', 'from_currency', e.target.value.toUpperCase())} placeholder="USD" maxLength={3} />
              </div>
              <div>
                <Label>To Currency *</Label>
                <Input value={forms.exchangeRate.to_currency} onChange={(e) => updateForm('exchangeRate', 'to_currency', e.target.value.toUpperCase())} placeholder="INR" maxLength={3} />
              </div>
              <div>
                <Label>Exchange Rate *</Label>
                <Input type="number" step="0.0001" value={forms.exchangeRate.rate} onChange={(e) => updateForm('exchangeRate', 'rate', parseFloat(e.target.value))} placeholder="83.50" />
                <p className="text-xs text-gray-500 mt-1">
                  Example: 1 {forms.exchangeRate.from_currency || 'USD'} = {forms.exchangeRate.rate || '0'} {forms.exchangeRate.to_currency || 'INR'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={forms.exchangeRate.is_manual}
                  onChange={(e) => updateForm('exchangeRate', 'is_manual', e.target.checked)}
                  className="w-4 h-4"
                />
                <Label>Manual Rate (won't be overwritten by auto-fetch)</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => closeModal('exchangeRate')}>Cancel</Button>
              <Button onClick={() => handleSubmit('exchangeRate')}>Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Language Modal */}
        <Dialog open={modals.language} onOpenChange={() => closeModal('language')}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editing.language ? 'Edit' : 'Add'} Language</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div>
                <Label>Language Code * (e.g., en, hi, mr)</Label>
                <Input 
                  value={forms.language.code} 
                  onChange={(e) => updateForm('language', 'code', e.target.value.toLowerCase())} 
                  placeholder="en" 
                  maxLength={2}
                  disabled={editing.language}
                />
                <p className="text-xs text-gray-500 mt-1">2-letter ISO 639-1 code</p>
              </div>
              <div>
                <Label>English Name *</Label>
                <Input 
                  value={forms.language.name} 
                  onChange={(e) => updateForm('language', 'name', e.target.value)} 
                  placeholder="English" 
                />
              </div>
              <div>
                <Label>Native Name *</Label>
                <Input 
                  value={forms.language.native_name} 
                  onChange={(e) => updateForm('language', 'native_name', e.target.value)} 
                  placeholder="English" 
                />
                <p className="text-xs text-gray-500 mt-1">Name in the language itself (e.g., हिंदी for Hindi)</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={forms.language.is_active}
                    onChange={(e) => updateForm('language', 'is_active', e.target.checked)}
                    className="w-4 h-4"
                  />
                  <Label>Active</Label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={forms.language.is_default}
                    onChange={(e) => updateForm('language', 'is_default', e.target.checked)}
                    className="w-4 h-4"
                  />
                  <Label>Set as Default</Label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => closeModal('language')}>Cancel</Button>
              <Button onClick={() => handleSubmit('language')}>
                {editing.language ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* User Skill Test Results Modal */}
        <Dialog open={showResultsModal} onOpenChange={setShowResultsModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedUserResults?.user?.name}'s Skill Test Results
              </DialogTitle>
              <DialogDescription>
                Detailed skill test performance and scores
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {selectedUserResults?.results?.length === 0 ? (
                <div className="text-center py-8 text-gray-600">
                  <Target className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p className="text-lg font-medium">No skill tests taken yet</p>
                  <p className="text-sm">This user hasn't completed any skill assessments.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* User Summary */}
                  <Card className="bg-gradient-to-r from-blue-50 to-indigo-50">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-lg font-semibold text-blue-900">
                            {selectedUserResults?.user?.name}
                          </h4>
                          <p className="text-blue-700">{selectedUserResults?.user?.email}</p>
                          <div className="flex gap-2 mt-2">
                            <Badge variant="outline">{selectedUserResults?.user?.user_type || 'student'}</Badge>
                            <Badge variant="secondary">{selectedUserResults?.user?.points || 0} pts</Badge>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-blue-600">Total Tests Taken</p>
                          <p className="text-3xl font-bold text-blue-900">
                            {selectedUserResults?.results?.length || 0}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Test Results */}
                  <div className="grid gap-4">
                    {selectedUserResults?.results?.map((result, index) => (
                      <Card key={index} className="border-l-4 border-l-blue-500">
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h5 className="text-lg font-semibold text-gray-900 mb-2">
                                {result.skill_name}
                              </h5>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <p className="text-gray-600">Score</p>
                                  <p className="font-semibold text-lg">
                                    {Math.round(result.score)}%
                                  </p>
                                </div>
                                <div>
                                  <p className="text-gray-600">Correct Answers</p>
                                  <p className="font-semibold">
                                    {result.correct_answers}/{result.total_questions}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-gray-600">Date Taken</p>
                                  <p className="font-semibold">
                                    {new Date(result.created_at).toLocaleDateString()}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-gray-600">Time Taken</p>
                                  <p className="font-semibold">
                                    {new Date(result.created_at).toLocaleTimeString()}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className={`w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-lg ${
                                result.score >= 80 ? 'bg-green-500' :
                                result.score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}>
                                {Math.round(result.score)}%
                              </div>
                            </div>
                          </div>
                          
                          {/* Progress Bar */}
                          <div className="mt-4">
                            <div className="flex justify-between text-sm text-gray-600 mb-1">
                              <span>Performance</span>
                              <span>{Math.round(result.score)}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${
                                  result.score >= 80 ? 'bg-green-500' :
                                  result.score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${result.score}%` }}
                              ></div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Overall Statistics */}
                  {selectedUserResults?.results?.length > 1 && (
                    <Card className="bg-gray-50">
                      <CardContent className="p-6">
                        <h5 className="text-lg font-semibold mb-4">Overall Performance</h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                          <div>
                            <p className="text-2xl font-bold text-blue-600">
                              {Math.round(
                                selectedUserResults.results.reduce((sum, r) => sum + r.score, 0) / 
                                selectedUserResults.results.length
                              )}%
                            </p>
                            <p className="text-sm text-gray-600">Average Score</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-green-600">
                              {Math.max(...selectedUserResults.results.map(r => r.score)).toFixed(0)}%
                            </p>
                            <p className="text-sm text-gray-600">Best Score</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-orange-600">
                              {selectedUserResults.results.reduce((sum, r) => sum + r.correct_answers, 0)}
                            </p>
                            <p className="text-sm text-gray-600">Total Correct</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowResultsModal(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Mock Interview Details Modal */}
        <Dialog open={showInterviewModal} onOpenChange={setShowInterviewModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Mock Interview Details</DialogTitle>
              {selectedInterview && (
                <DialogDescription>
                  {selectedInterview.user_name} - {selectedInterview.career_field} ({selectedInterview.experience_level})
                </DialogDescription>
              )}
            </DialogHeader>
            
            {selectedInterview && (
              <div className="space-y-6">
                {/* Overall Summary */}
                {selectedInterview.overall_feedback && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Overall Performance</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center p-4 bg-blue-50 rounded-lg">
                          <p className="text-2xl font-bold text-blue-600">
                            {selectedInterview.overall_feedback.average_overall_rating}/10
                          </p>
                          <p className="text-sm text-gray-600">Overall</p>
                        </div>
                        <div className="text-center p-4 bg-green-50 rounded-lg">
                          <p className="text-2xl font-bold text-green-600">
                            {selectedInterview.overall_feedback.average_grammar}/10
                          </p>
                          <p className="text-sm text-gray-600">Grammar</p>
                        </div>
                        <div className="text-center p-4 bg-purple-50 rounded-lg">
                          <p className="text-2xl font-bold text-purple-600">
                            {selectedInterview.overall_feedback.average_quality}/10
                          </p>
                          <p className="text-sm text-gray-600">Quality</p>
                        </div>
                        <div className="text-center p-4 bg-orange-50 rounded-lg">
                          <p className="text-2xl font-bold text-orange-600">
                            {selectedInterview.overall_feedback.average_keyword_relevance}/10
                          </p>
                          <p className="text-sm text-gray-600">Keywords</p>
                        </div>
                      </div>
                      <div className="mt-4 text-center">
                        <Badge variant={selectedInterview.overall_feedback.average_overall_rating >= 7 ? "default" : "secondary"} className="text-lg px-4 py-2">
                          {selectedInterview.overall_feedback.performance_level}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Questions & Answers */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Questions & Answers</h3>
                  {selectedInterview.questions.map((q, idx) => (
                    <Card key={idx}>
                      <CardContent className="pt-6">
                        <div className="space-y-3">
                          <div className="flex items-start justify-between">
                            <h4 className="font-medium text-blue-600">Question {idx + 1}</h4>
                            {q.feedback && (
                              <Badge variant={q.feedback.overall_rating >= 7 ? "default" : "secondary"}>
                                Score: {q.feedback.overall_rating}/10
                              </Badge>
                            )}
                          </div>
                          <p className="text-gray-700">{q.question}</p>
                          
                          {q.answer && (
                            <>
                              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                                <p className="text-sm font-medium text-gray-600 mb-1">Answer:</p>
                                <p className="text-gray-800">{q.answer}</p>
                              </div>

                              {q.feedback && (
                                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                                  <p className="text-sm font-medium text-blue-900 mb-2">AI Feedback:</p>
                                  <div className="grid grid-cols-3 gap-2 mb-2">
                                    <div className="text-center">
                                      <p className="text-lg font-bold text-blue-600">{q.feedback.grammar_score}/10</p>
                                      <p className="text-xs text-gray-600">Grammar</p>
                                    </div>
                                    <div className="text-center">
                                      <p className="text-lg font-bold text-blue-600">{q.feedback.quality_score}/10</p>
                                      <p className="text-xs text-gray-600">Quality</p>
                                    </div>
                                    <div className="text-center">
                                      <p className="text-lg font-bold text-blue-600">{q.feedback.keyword_relevance}/10</p>
                                      <p className="text-xs text-gray-600">Keywords</p>
                                    </div>
                                  </div>
                                  <div className="space-y-2 text-sm">
                                    {q.feedback.strengths && q.feedback.strengths.length > 0 && (
                                      <div>
                                        <p className="font-medium text-green-700">✓ Strengths:</p>
                                        <ul className="list-disc list-inside text-gray-700 ml-2">
                                          {q.feedback.strengths.map((s, i) => <li key={i}>{s}</li>)}
                                        </ul>
                                      </div>
                                    )}
                                    {q.feedback.improvements && q.feedback.improvements.length > 0 && (
                                      <div>
                                        <p className="font-medium text-orange-700">→ Areas for Improvement:</p>
                                        <ul className="list-disc list-inside text-gray-700 ml-2">
                                          {q.feedback.improvements.map((i, idx) => <li key={idx}>{i}</li>)}
                                        </ul>
                                      </div>
                                    )}
                                    {q.feedback.tips && (
                                      <div>
                                        <p className="font-medium text-blue-700">💡 Tips:</p>
                                        <p className="text-gray-700 ml-2">{q.feedback.tips}</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                          
                          {!q.answer && (
                            <p className="text-gray-400 italic">Not answered yet</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowInterviewModal(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
      <Footer />
    </div>
  );
};

export default AdminPanel;
